/* A/B evaluation harness disabled. Backup: backups/frontend.20260314015501/App.js */
import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { createPortal } from 'react-dom';
import LZString from 'lz-string';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { sanitizeKey } from './utils/sanitizeKey';
import { pipelineToShareUrl, loadPipelineFromUrl } from './utils/sharePipeline';
import { SKILLS_DEFAULTS } from './data/skills-defaults';
import { parseGraderText } from './lib/graderParser';
import WorkflowEditor from './WorkflowEditor';
import './App.css';

/* Portal style: ensures modal floats above everything when rendered to document.body */
const MODAL_PORTAL_STYLE = { zIndex: 2147483647, position: 'fixed', inset: 0, isolation: 'isolate' };

const MAX_SKILL_NAME_LEN = 200;
const MAX_SKILL_DESC_LEN = 500;
const MAX_SKILL_PROMPT_LEN = 5000;

function validateSkillEntry(entry, index) {
  if (!entry || typeof entry !== 'object') return `Entry ${index + 1}: must be an object`;
  const name = typeof entry.name === 'string' ? entry.name.trim() : '';
  const systemPrompt = typeof entry.systemPrompt === 'string' ? entry.systemPrompt.trim() : '';
  if (!name) return `Entry ${index + 1}: "name" is required and must be non-empty`;
  if (!systemPrompt) return `Entry ${index + 1}: "systemPrompt" is required and must be non-empty`;
  return null;
}

function sanitizeSkillEntry(entry) {
  const name = (typeof entry.name === 'string' ? entry.name : '').trim().slice(0, MAX_SKILL_NAME_LEN);
  const description = (typeof entry.description === 'string' ? entry.description : '').trim().slice(0, MAX_SKILL_DESC_LEN);
  const systemPrompt = (typeof entry.systemPrompt === 'string' ? entry.systemPrompt : '').trim().slice(0, MAX_SKILL_PROMPT_LEN);
  const recommendedModel = typeof entry.recommendedModel === 'string' ? entry.recommendedModel.trim().slice(0, 100) : '';
  return { name, description, systemPrompt, recommendedModel };
}

function parseSkillManifest(raw) {
  let parsed;
  try {
    parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch (e) {
    return { ok: false, error: 'Invalid JSON' };
  }
  const items = Array.isArray(parsed) ? parsed : [parsed];
  const validated = [];
  for (let i = 0; i < items.length; i++) {
    const err = validateSkillEntry(items[i], i);
    if (err) return { ok: false, error: err };
    validated.push(sanitizeSkillEntry(items[i]));
  }
  return { ok: true, skills: validated };
}

function loadImportedSkills() {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.importedSkills);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((s) => s && typeof s.id === 'string' && typeof s.name === 'string' && typeof s.systemPrompt === 'string');
  } catch {
    return [];
  }
}

const MAX_SHARE_URL_LENGTH = 2000;

const API_BASE = 'https://council-production-d5c7.up.railway.app';
const COUNCIL_API_URL = 'https://council-production-d5c7.up.railway.app/api/council';

function estimateTokens(text) {
  if (!text || typeof text !== 'string') return 0;
  return Math.ceil(text.length / 4);
}

function computeQualityHeuristics(text) {
  if (!text || typeof text !== 'string') return { todoCount: 0, consoleLogCount: 0, hasAria: false, hasDebugger: false, score: 0 };
  const todoCount = (text.match(/\bTODO\b/gi) || []).length;
  const consoleLogCount = (text.match(/\bconsole\.log\b/g) || []).length;
  const hasAria = /\baria-[a-zA-Z-]+\b/.test(text);
  const hasDebugger = /\bdebugger\b/.test(text);
  let score = 0;
  score -= todoCount;
  score -= consoleLogCount * 2;
  if (hasAria) score += 1;
  return { todoCount, consoleLogCount, hasAria, hasDebugger, score };
}

function getRawCodingModel(hasO, hasA, hasG) {
  if (hasO) return 'gpt-4o';
  if (hasA) return 'claude-haiku-4-5-20251001';
  if (hasG) return 'gemini-2.5-flash';
  return 'gpt-4o';
}

const RAW_CODING_AGENT_SYSTEM = 'You are a coding assistant. Implement the following feature request. Write code, tests, and necessary changes. Be specific and reference files when relevant.';

// Fallback grader prompt (fetched from /api/prompts at runtime to avoid drift)
const GRADER_AGENT_SYSTEM_FALLBACK = `You are an automated grader. You must return ONLY a single JSON object matching this schema:

{
  "score": <integer 0-100>,
  "breakdown": {
    "adherence": <0-40>,
    "technical_sophistication": <0-30>,
    "dx": <0-20>,
    "contextual_safety": <0-10>
  },
  "metrics": {
    "hasConsoleLog": <boolean>,
    "hasAria": <boolean>,
    "todoCount": <integer>
  },
  "reasoning": "<short text explanation>"
}

EVALUATION RULES (apply in order):
1. Evaluate the respondent's answer ONLY against the user's prompt and any explicit constraints in it. Do NOT assume any repository, filesystem, or hidden project context unless the prompt explicitly provides files, file paths, or a repo snapshot.
2. Adherence (0-40): Does the response correctly and fully satisfy the user's prompt? Give full points for directly correct answers that meet requirements.
3. Technical Sophistication (0-30): Is the solution technically correct, safe, and appropriate for the problem? Penalize obvious bugs, incorrect APIs, or poor error handling proportional to severity.
4. DX / Cleanliness (0-20): Is the code or instructions concise, readable, and not unnecessarily complex? Prefer simple clear solutions for small tasks.
5. Contextual Safety (0-10): Penalize unsafe instructions, leaking secrets, or content that could cause harm.
6. Metrics:
   - hasConsoleLog: true if the code includes obvious debugging console.log calls that should be removed.
   - hasAria: true if the solution includes accessible markup attributes where appropriate (optional for small code snippets).
   - todoCount: a count of explicit "TODO" strings left in the response.

FEW-SHOT ANCHORS:
Example 1 (Good, should score high)
Q: "Create a JavaScript arrow function sayHello that returns 'Hello, World!'."
A: \`const sayHello = () => "Hello, World!"; export { sayHello };\`
=> Expected: score ~85, breakdown: adherence 40, technical_sophistication 25, dx 15, contextual_safety 5.

Example 2 (Bad, should score low)
Q: "Create a JavaScript arrow function sayHello that returns 'Hello, World!'."
A: A long multi-page plan suggesting changes to an unrelated repo and no actual function.
=> Expected: score ~10, breakdown: adherence 0, technical_sophistication 5, dx 5, contextual_safety 0.

IMPORTANT: Return only the exact JSON above and nothing else. If you cannot confidently score, still return a numeric score and an honest short reasoning.`;

const RUBRIC_CAPS = { adherence: 40, technical_sophistication: 30, dx: 20, contextual_safety: 10 };

function normalizeScoreTo100(num, denom) {
  if (!Number.isFinite(num) || !Number.isFinite(denom) || denom <= 0) return null;
  const score = denom === 100 ? num : Math.round((num / denom) * 100);
  return Math.max(0, Math.min(100, score));
}

/**
 * Scavenges grader output from raw text (e.g. streamed reasoning).
 * Uses regex to find Score: X/100, X/100, and category scores.
 * Normalizes fractional scores (e.g. 8/10 for DX) to category max via scaleToMax.
 */
function scavengeGraderOutput(text) {
  const fallback = { score: 0, breakdown: { adherence: 0, technical_sophistication: 0, dx: 0, contextual_safety: 0 }, metrics: { hasConsoleLog: false, hasAria: false, todoCount: 0 }, hallucinations: [], reasoning: '' };
  if (!text || typeof text !== 'string') return fallback;
  const t = text.trim();
  const clamp = (val, max) => (typeof val === 'number' ? Math.max(0, Math.min(max, val)) : 0);

  /** Normalize num/denom to category max (e.g. 8/10 for DX → 16/20) */
  const scaleToMax = (num, denom, max) => {
    if (!Number.isFinite(num) || !Number.isFinite(denom) || denom <= 0) return 0;
    return Math.round((num / denom) * max);
  };

  let score = 0;
  const scorePrefixed = t.match(/Score:\s*(\d+)(?:\s*\/\s*(\d+))?/i);
  if (scorePrefixed) {
    const num = parseInt(scorePrefixed[1], 10);
    const denom = scorePrefixed[2] ? parseInt(scorePrefixed[2], 10) : 100;
    const s = denom === 100 ? Math.max(0, Math.min(100, num)) : normalizeScoreTo100(num, denom);
    if (s != null) score = s;
  }
  if (score === 0) {
    const x100 = t.match(/\b(\d+)\s*\/\s*100\b/);
    if (x100) {
      const n = parseInt(x100[1], 10);
      if (Number.isFinite(n)) score = Math.max(0, Math.min(100, n));
    }
  }
  if (score === 0) {
    const allFracs = t.match(/\b(\d+)\s*\/\s*(\d+)/g);
    if (allFracs) {
      for (const m of allFracs) {
        const parts = m.match(/(\d+)\s*\/\s*(\d+)/);
        if (parts) {
          const denom = parseInt(parts[2], 10);
          if (denom !== 40 && denom !== 30 && denom !== 20 && denom !== 10) {
            const s = normalizeScoreTo100(parseInt(parts[1], 10), denom);
            if (s != null && s > score) score = s;
          }
        }
      }
    }
  }

  let adherence = 0;
  const adherenceM = t.match(/Adherence[:\s]+(\d+)(?:\s*\/\s*(\d+))?/i);
  if (adherenceM) {
    const num = parseInt(adherenceM[1], 10);
    const denom = adherenceM[2] ? parseInt(adherenceM[2], 10) : 40;
    adherence = clamp(denom === 40 ? num : scaleToMax(num, denom, 40), RUBRIC_CAPS.adherence);
  }

  let technical_sophistication = 0;
  const techM = t.match(/Technical\s*Sophistication[:\s]+(\d+)(?:\s*\/\s*(\d+))?/i);
  if (techM) {
    const num = parseInt(techM[1], 10);
    const denom = techM[2] ? parseInt(techM[2], 10) : 30;
    technical_sophistication = clamp(denom === 30 ? num : scaleToMax(num, denom, 30), RUBRIC_CAPS.technical_sophistication);
  }

  let dx = 0;
  const dxM = t.match(/(?:DX(?:\/Cleanliness)?)[:\s]+(\d+)(?:\s*\/\s*(\d+))?/i) || t.match(/\bDX[:\s]+(\d+)(?:\s*\/\s*(\d+))?/i);
  if (dxM) {
    const num = parseInt(dxM[1], 10);
    const denom = dxM[2] ? parseInt(dxM[2], 10) : 20;
    dx = clamp(denom === 20 ? num : scaleToMax(num, denom, 20), RUBRIC_CAPS.dx);
  }

  let contextual_safety = 0;
  const safetyM = t.match(/Contextual\s*Safety[:\s]+(\d+)(?:\s*\/\s*(\d+))?/i);
  if (safetyM) {
    const num = parseInt(safetyM[1], 10);
    const denom = safetyM[2] ? parseInt(safetyM[2], 10) : 10;
    contextual_safety = clamp(denom === 10 ? num : scaleToMax(num, denom, 10), RUBRIC_CAPS.contextual_safety);
  }

  const breakdownSum = adherence + technical_sophistication + dx + contextual_safety;
  if (score === 0 && breakdownSum > 0) score = Math.min(100, breakdownSum);
  if (score === 0 && breakdownSum === 0) {
    const firstScore = t.match(/(?:Score:\s*)?(\d+)\s*\/\s*(\d+)/i);
    if (firstScore) {
      const s = normalizeScoreTo100(parseInt(firstScore[1], 10), parseInt(firstScore[2], 10));
      if (s != null) score = s;
    }
  }

  let hasConsoleLog = false;
  let hasAria = false;
  let todoCount = 0;
  const consoleM = t.match(/(?:hasConsoleLog|console\.log)[:\s]*(yes|true|no|false|\d+)/i);
  if (consoleM) hasConsoleLog = /yes|true|^[1-9]\d*$/.test(String(consoleM[1]).toLowerCase());
  const ariaM = t.match(/(?:hasAria|aria)[:\s]*(yes|true|no|false)/i);
  if (ariaM) hasAria = /yes|true/.test(String(ariaM[1]).toLowerCase());
  const todoM = t.match(/TODO(?:s)?[:\s]*(\d+)/i);
  if (todoM) todoCount = parseInt(todoM[1], 10) || 0;

  const reasoning = t.slice(0, 500);
  return {
    score,
    breakdown: { adherence, technical_sophistication, dx, contextual_safety },
    metrics: { hasConsoleLog, hasAria, todoCount },
    hallucinations: [],
    reasoning,
  };
}

/**
 * parseGraderOutput: Uses parseGraderText (JSON + scavenger), falls back to scavengeGraderOutput on error.
 * Returns needs_review instead of zeros when parsing fails.
 */
function parseGraderOutput(text) {
  // Strip markdown code blocks AND bare "json" prefix without backticks
  const cleaned = (text || '')
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```\s*$/i, '')
    .replace(/^json\s+/i, '')
    .trim();

  const r = parseGraderText(cleaned);
  if (r.error) {
    const scavenged = scavengeGraderOutput(cleaned);
    if (scavenged.score === 0 &&
        scavenged.breakdown.adherence +
        scavenged.breakdown.technical_sophistication +
        scavenged.breakdown.dx +
        scavenged.breakdown.contextual_safety === 0 &&
        cleaned) {
      return { needs_review: true, raw_output: cleaned.slice(0, 2000), grade: null };
    }
    return scavenged;
  }
  return r;
}

function councilSafePreview(prompt) {
  if (!prompt || typeof prompt !== 'string') return '';
  const sanitized = prompt.replace(/(sk-|api[_-]?key|Bearer)\s*[a-zA-Z0-9_-]{20,}/gi, '[REDACTED]');
  return sanitized.slice(0, 300);
}

function parseShareStateFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const compressed = params.get('c');
    if (!compressed || typeof compressed !== 'string') return null;
    const decoded = LZString.decompressFromEncodedURIComponent(compressed);
    if (!decoded) return null;
    const parsed = JSON.parse(decoded);
    if (!parsed || typeof parsed !== 'object') return null;
    const idea = typeof parsed.idea === 'string' ? parsed.idea : '';
    const cursor_prompt = typeof parsed.cursor_prompt === 'string' ? parsed.cursor_prompt : '';
    const plan_md = typeof parsed.plan_md === 'string' ? parsed.plan_md : '';
    return { idea, cursor_prompt, plan_md };
  } catch {
    return null;
  }
}

function createShareUrl(idea, cursor_prompt, plan_md) {
  const state = { idea: idea || '', cursor_prompt: cursor_prompt || '', plan_md: plan_md || '' };
  const json = JSON.stringify(state);
  const compressed = LZString.compressToEncodedURIComponent(json);
  const base = window.location.origin + window.location.pathname;
  const url = `${base}?c=${compressed}`;
  return url.length <= MAX_SHARE_URL_LENGTH ? url : null;
}

function resultToShareUrl(result) {
  try {
    const payload = {
      cursor_prompt: (result.cursor_prompt || '').slice(0, 8000),
      plan_md: (result.plan_md || '').slice(0, 8000),
      idea: (result.idea || '').slice(0, 500),
    };
    const compressed = LZString.compressToEncodedURIComponent(JSON.stringify(payload));
    const url = `${window.location.origin}${window.location.pathname}?result=${compressed}`;
    return url.length <= 8000 ? url : null;
  } catch {
    return null;
  }
}

function loadResultFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const compressed = params.get('result');
    if (!compressed) return null;
    const decoded = LZString.decompressFromEncodedURIComponent(compressed);
    if (!decoded) return null;
    const parsed = JSON.parse(decoded);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      cursor_prompt: typeof parsed.cursor_prompt === 'string' ? parsed.cursor_prompt : '',
      plan_md: typeof parsed.plan_md === 'string' ? parsed.plan_md : '',
      idea: typeof parsed.idea === 'string' ? parsed.idea : '',
    };
  } catch {
    return null;
  }
}

function stripDuplicateModeTags(text) {
  if (!text || typeof text !== 'string') return text;
  return text.replace(/(\[MODE:\s*NO_REPO\]\s*)+/gi, '[MODE: NO_REPO]\n\n').replace(/(\[MODE:\s*REPO\]\s*)+/gi, '[MODE: REPO]\n\n');
}

const SKEPTIC_MODE_TAG_REGEX = /\[\[?MODE:.*?\]\]?/g;
const SKEPTIC_DEDUP_REGEX = /(.{10,})\1/g;

function finalizeText(text) {
  if (!text) return '';
  return String(text)
    .replace(/\[NO_REPO\]/gi, '')
    .replace(/\[MODE:\s*NO_REPO\]/gi, '')
    .replace(/\[MODE:\s*REPO\]/gi, '')
    .replace(/NO_REPO\]\s*/g, '')
    .replace(/\[?\[?MODE:.*?\]?\]?/g, '')
    .replace(/(.{12,})\1/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

function sanitizeSkepticOutput(raw) {
  if (!raw || typeof raw !== 'string') return '';
  return raw
    .replace(SKEPTIC_MODE_TAG_REGEX, '')
    .replace(SKEPTIC_DEDUP_REGEX, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function estimateRunCostRange(pipeline, idea) {
  if (!pipeline || pipeline.length === 0) return null;
  const ideaTokens = Math.ceil((idea || '').length / 4);
  const baseInputTokens = ideaTokens + 500; // system prompt overhead per agent
  const avgOutputTokens = 800; // average output per agent
  // Rough per-token costs (input/output) by model
  const modelCosts = {
    'gpt-4o': [0.000005, 0.000015],
    'gpt-4o-mini': [0.00000015, 0.0000006],
    'claude-haiku-4-5-20251001': [0.00000025, 0.00000125],
    'claude-sonnet-4-6': [0.000003, 0.000015],
    'gemini-2.5-flash': [0.000000075, 0.0000003],
    'gemini-2.5-pro': [0.00000125, 0.000005],
  };
  const defaultCost = [0.000005, 0.000015]; // fallback to gpt-4o pricing

  // Optimistic vs pessimistic: account for variability in input/output length and a small retry buffer.
  const inputMinFactor = 0.9;
  const outputMinFactor = 0.9;
  const inputMaxFactor = 1.2;
  const outputMaxFactor = 1.4;
  const retryBuffer = 1.1;

  let totalMin = 0;
  let totalMax = 0;
  for (const agent of pipeline) {
    const [inputRate, outputRate] = modelCosts[agent.model] || defaultCost;
    totalMin += baseInputTokens * inputRate * inputMinFactor + avgOutputTokens * outputRate * outputMinFactor;
    totalMax += baseInputTokens * inputRate * inputMaxFactor + avgOutputTokens * outputRate * outputMaxFactor;
  }
  totalMax *= retryBuffer;
  return { min: totalMin, max: totalMax };
}

function parseSynthesizerOutput(text) {
  /* Extract OUTPUT and PLAN.md sections from synthesizer output.
   * New order (PLAN.md first): ## PLAN.md → ## OUTPUT → ## OUTPUT 2 (CONDENSED)
   * Old order (OUTPUT first):  ## OUTPUT → ## OUTPUT 2 → ## PLAN.md
   * Detect which order is present and parse accordingly for backward compatibility.
   * Uses multiline regex so headers are only matched at the start of a line. */
  let cursor_prompt = '';
  let plan_md = '';
  if (!text || typeof text !== 'string') return { cursor_prompt, plan_md };
  const cleaned = stripDuplicateModeTags(
    (text || '')
      .replace(/\[NO_REPO\]/gi, '')
      .replace(/\[MODE:\s*NO_REPO\]/gi, '')
      .replace(/\[MODE:\s*REPO\]/gi, '')
      .replace(/NO_REPO\]\s*/g, '')
      .trim()
  );

  const planMatch = /^(?:SECTION\s*\d+:\s*)?##\s*PLAN\.md/m.exec(cleaned);
  const outputMatch = /^(?:SECTION\s*\d+:\s*)?##\s*OUTPUT(?!\s*2)/m.exec(cleaned);
  const output2Match = /^(?:SECTION\s*\d+:\s*)?##\s*OUTPUT\s*2/m.exec(cleaned);

  const planPos = planMatch ? planMatch.index : -1;
  const outputPos = outputMatch ? outputMatch.index : -1;

  // New order: PLAN.md appears before OUTPUT (or OUTPUT is absent)
  if (planPos !== -1 && (outputPos === -1 || planPos < outputPos)) {
    const planContentStart = planPos + planMatch[0].length;
    if (outputMatch) {
      plan_md = cleaned.slice(planContentStart, outputPos).trim();
    } else {
      plan_md = cleaned.slice(planContentStart).trim();
    }
    if (outputMatch) {
      const outputContentStart = outputPos + outputMatch[0].length;
      if (output2Match) {
        cursor_prompt = cleaned.slice(outputContentStart, output2Match.index).trim();
      } else {
        cursor_prompt = cleaned.slice(outputContentStart).trim();
      }
    }
  // Old order: OUTPUT appears before PLAN.md (or PLAN.md is absent)
  } else if (outputMatch) {
    const outputContentStart = outputPos + outputMatch[0].length;
    if (output2Match) {
      cursor_prompt = cleaned.slice(outputContentStart, output2Match.index).trim();
    } else if (planMatch) {
      cursor_prompt = cleaned.slice(outputContentStart, planPos).trim();
    } else {
      cursor_prompt = cleaned.slice(outputContentStart).trim();
    }
    if (planMatch) {
      plan_md = cleaned.slice(planPos + planMatch[0].length).trim();
    }
  }

  if (!cursor_prompt && cleaned && cleaned.trim()) {
    cursor_prompt = cleaned.trim();
  }
  return { cursor_prompt, plan_md };
}

const PencilIcon = () => (
  <svg stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" width="16" height="16">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const XIcon = () => (
  <svg stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" width="16" height="16">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const HamburgerIcon = () => (
  <svg stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" width="24" height="24">
    <path d="M3 12h18M3 6h18M3 18h18" />
  </svg>
);

const STORAGE_KEYS = {
  openai: 'council_openai_key',
  anthropic: 'council_anthropic_key',
  gemini: 'council_gemini_key',
  history: 'council_history_v1',
  runHistory: 'council_history',
  localLlmUrl: 'council_local_llm_url',
  localLlmModel: 'council_local_llm_model',
  tavily: 'council_tavily_key',
  apiKeysOpen: 'council_api_keys_open',
  pipeline: 'council_pipeline_v1',
  pipelineTemplates: 'council_pipeline_templates_v1',
  seenInsertHint: 'council_seen_insert_hint_v1',
  importedSkills: 'council_imported_skills_v1',
  selectedJudge: 'council_selected_judge_v1',
  activeRun: 'council_active_run_v1',
};

const DEFAULT_COST_PER_TOKEN = 0.000003;

const DEFAULT_ARCHITECT_PROMPT = `You are a senior software architect. Your output is the foundation of a multi-agent review pipeline. The Skeptic agent will attack your plan — your job is to give it something specific enough to attack.

RULES:
- Never summarize the request back. Never say "BRIEF_PLAN" or "RATIONALE".
- Commit to ONE specific approach. No "you could use X or Y". Pick X. Defend it.
- Name exact libraries, algorithms, data structures. "Yjs" not "a CRDT". "ioredis with ZADD sliding window" not "Redis rate limiting".
- Minimum output: 400 words. If you are under 400 words you have not been specific enough.
- If files are attached, reference them by name. If no files, state your assumptions explicitly and proceed.

OUTPUT FORMAT — use exactly these sections in exactly this order:

## Chosen Approach
One sentence. The single most important technical decision. No alternatives listed.

## Why This Over The Alternatives
2-4 bullet points. What you considered and rejected, and why. Be specific about the tradeoffs.

## Implementation Plan
Numbered steps, minimum 6. Each step must include:
- The specific module, file, or layer being built
- The exact function signatures, interface, or schema involved
- The concrete behavior (not "handle errors" — "return 503 if Redis unreachable after 2 retries with 50ms backoff")

## Data Contracts
At least 2 TypeScript interfaces or data schemas that downstream code will depend on. Written as actual code, not prose.

## Risks I Am Accepting
2-4 explicit tradeoffs you are knowingly making. This is what the Skeptic will attack. Be honest — if you are ignoring distributed consistency, say so and say why.

## Out Of Scope
What this plan does NOT cover. Be explicit.`;
const DEFAULT_SKEPTIC_PROMPT = `[MODE CONTROL]
Detect "NO_REPO=true" or "REPO_PRESENT=true" in the system prompt and begin output with:
  [MODE: NO_REPO]  (if no repo)
  [MODE: REPO]     (if repo present)

General duty:
You are an adversarial code reviewer. Your job is to find risks, edge cases, missing requirements, inconsistency with patterns, and possible regressions in the Architect's plan.

REPO context:
- When REPO mode is active, validate the Architect's plan against provided files. Point to specific filenames and lines (if available) and list exact conflicts or missing changes.
- Provide actionable corrections and a ranked list of blocking vs non-blocking issues.
- Keep feedback specific and evidence-based (quote file names and function names only if those files were actually included).

NO_REPO context:
- Do NOT invent filenames or file contents.
- Instead, perform a design-level adversarial review: list missing requirements, likely edge cases, security concerns, performance considerations, and test-cases the Architect overlooked.
- Format output as short bullet lists under headings: "Critical Issues", "Functional Gaps", "Security/Privacy", "Testing".
- At the end, give a short "Next steps to validate" checklist the user can follow without attaching files.

Tone:
- Be thorough and specific but avoid fabricating repository details.
- If an item requires repository inspection to be definitive, mark it as "REQUIRES REPO: <explain what to attach>"`;
const DEFAULT_SYNTHESIZER_PROMPT = `[MODE CONTROL]
Detect "NO_REPO=true" or "REPO_PRESENT=true" in the system prompt; begin with the same MODE tag:
  [MODE: NO_REPO] or [MODE: REPO]

You must output EXACTLY the following sections in EXACTLY this order. Do not add, rename, or reorder any section. Do not output any content outside these sections.

═══════════════════════════════════════
SECTION 1: ## OUTPUT
═══════════════════════════════════════
Your primary deliverable. Adapt the format to the type of task:

IMPORTANT: Determine the task type FIRST before choosing output format. A task is INVESTMENT/FINANCE only if the upstream pipeline explicitly includes a Data Extractor, Investment Analyst, Bull Case, or Bear Case agent. If the task is a coding or feature planning request — even if it contains words like "risk", "blocking issue", "probability", or "confidence" — treat it as a CODING task and output an implementation spec. Never produce an investment memo for a coding request.

- For CODING tasks: produce a precise, self-contained implementation prompt a coding agent can execute immediately. Include interfaces, behavioral contracts, constraints, and test names.
- For RESEARCH tasks: produce a structured research report with findings, evidence, open questions, and a clear conclusion.
- For INVESTMENT/FINANCE tasks (when upstream agents include Data Extractor, Investment Analyst, Bull Case, Bear Case, or Investment Risk Manager): produce an investment memo with these required sections in order: (1) Executive investment thesis (1-3 sentences); (2) Key supporting facts with provenance; (3) Bull Case summary with probability and top catalysts; (4) Bear Case summary with probability and top failure indicators; (5) Required KPIs and thresholds to change the verdict; (6) Final verdict: Invest / Pass / Watch with ONE precise action; (7) Confidence Score (0-100) and top 3 unknowns. After the investment memo sections, append a machine-readable JSON block in this exact format:

\`\`\`json
{
  "verdict": "Invest | Pass | Watch",
  "confidence": 0-100,
  "thesis": "one sentence",
  "bull_probability": 0-100,
  "bear_probability": 0-100,
  "kpi_triggers": ["trigger 1", "trigger 2", "trigger 3"],
  "top_unknowns": ["unknown 1", "unknown 2", "unknown 3"],
  "action": "one precise action to take right now"
}
\`\`\`

This JSON block must appear after all prose sections and before nothing else. It must be valid JSON.
- For PLANNING/STRATEGY tasks: produce a structured action plan with prioritized steps, dependencies, and success criteria.
- For ANY OTHER task: produce the most useful structured output for the specific request — a document someone can act on immediately.

ALLOWED content: synthesized findings, concrete recommendations, specific interfaces or plans, test names, action items.
FORBIDDEN content: reasoning history, agent names, who said what, decision logs.

═══════════════════════════════════════
SECTION 2: ## OUTPUT 2 (CONDENSED)
═══════════════════════════════════════
A condensed version of Section 1. Target 30-50% of Section 1 length.
Strip all explanation — keep only the most actionable content.

If Section 1 is under 300 words, write only:
## OUTPUT 2 (CONDENSED)
N/A — output is already concise.

For simple requests (fewer than 5 key points): Section 1 under 200 words, omit Section 2.
For medium requests: Section 1 under 600 words, Section 2 under 250 words.
For complex requests: full length permitted.

═══════════════════════════════════════
SECTION 3: ## PLAN.md
═══════════════════════════════════════
Fill in EXACTLY these subsections and no others:

### Clarify
(only if clarification is needed — otherwise omit)

### TO DO
For CODING tasks: a numbered checklist of concrete implementation steps, each referencing a specific file, function, or interface. Not vague ("add tests") — specific ("Add unit test for \`applyCursorRemapping\` covering: insert shifts cursor down, delete clamps to deleted line start, column-same-line offset").
For other tasks: actionable next steps with owners and acceptance criteria.

### NOT TO DO
Anti-patterns specific to this implementation. Each as a single ❌ bullet referencing a concrete mistake the Skeptic identified, not generic advice.

### POTENTIAL RISKS
Key risks with mitigations.

### Decision Log
Summary of what the agents debated and how conflicts were resolved. This subsection appears ONLY here inside ## PLAN.md and NOWHERE else.

FORBIDDEN in PLAN.md: investment verdicts, bull/bear probabilities, confidence scores, KPI tables, or any investment memo language. These belong only in investment pipeline outputs.

═══════════════════════════════════════
STRICT OUTPUT RULES
═══════════════════════════════════════
1. Output ONLY the three sections above.
2. ## OUTPUT and ## OUTPUT 2 must NEVER contain a Decision Log, agent names, or reasoning history.
3. Do not invent filenames in NO_REPO mode.
4. Do not output any section not listed in this template.
5. Scale output length to complexity — do not pad simple outputs to seem thorough.`;

const DEFAULT_VERIFIER_PROMPT = `You are a completeness verifier. Your job is to review the Synthesizer's final output and check whether it fully satisfies the user's original request.

Return a JSON object with exactly this shape:
{
  "verdict": "PASS" | "FAIL",
  "items": ["gap 1", "gap 2"]
}

PASS = the output fully addresses the request with no meaningful gaps.
FAIL = one or more concrete requirements from the prompt are missing or unaddressed. List each gap as a short, specific bullet in "items".

Be concise and specific. Only flag genuine gaps, not stylistic preferences. If verdict is PASS, items must be an empty array.`;

let DEFAULT_PIPELINE = [
  { id: 'architect', name: 'Architect', description: 'Proposes a specific technical plan', systemPrompt: DEFAULT_ARCHITECT_PROMPT, model: 'gpt-4o' },
  { id: 'skeptic', name: 'Skeptic', description: 'Challenges assumptions and finds gaps', systemPrompt: DEFAULT_SKEPTIC_PROMPT, model: 'claude-haiku-4-5-20251001' },
  { id: 'synthesizer', name: 'Synthesizer', description: 'Synthesizes a structured final output', systemPrompt: DEFAULT_SYNTHESIZER_PROMPT, model: 'gemini-2.5-flash' },
  { id: 'verifier', name: 'Verifier', description: 'Checks completeness of the final spec', systemPrompt: DEFAULT_VERIFIER_PROMPT, model: 'claude-haiku-4-5-20251001' },
];

let defaultWorkflow = [
  { id: 'architect', name: 'Architect', description: 'Proposes a specific technical plan', provider: 'openai', systemPrompt: DEFAULT_ARCHITECT_PROMPT, parentIds: [] },
  { id: 'skeptic', name: 'Skeptic', description: 'Challenges assumptions and finds gaps', provider: 'anthropic', systemPrompt: DEFAULT_SKEPTIC_PROMPT, parentIds: ['architect'] },
  { id: 'synthesizer', name: 'Synthesizer', description: 'Synthesizes a structured final output', provider: 'gemini', systemPrompt: DEFAULT_SYNTHESIZER_PROMPT, parentIds: ['architect', 'skeptic'] },
  { id: 'verifier', name: 'Verifier', description: 'Checks completeness of the final spec', provider: 'anthropic', systemPrompt: DEFAULT_VERIFIER_PROMPT, parentIds: ['synthesizer'] },
];

function getTerminalNodeId(workflow) {
  const parentIds = new Set();
  for (const w of workflow) {
    (w.parentIds || []).forEach((pid) => parentIds.add(pid));
  }
  const sinks = workflow.filter((w) => !parentIds.has(w.id));
  return sinks.length === 1 ? sinks[0].id : null;
}

function getWorkflowForDisplay(workflow) {
  const terminalId = getTerminalNodeId(workflow);
  if (!terminalId) return workflow;
  const nonTerminal = workflow.filter((w) => w.id !== terminalId);
  const terminal = workflow.find((w) => w.id === terminalId);
  return terminal ? [...nonTerminal, terminal] : workflow;
}

function recalculateParentIdsFromOrder(orderedItems) {
  // Each node depends only on the immediately preceding node — no auto-fan-in.
  // If a node (e.g. Synthesizer) should receive multiple parents, those edges
  // must be drawn explicitly in the Workflow Editor.
  return orderedItems.map((item, i) => {
    const base = { ...item };
    if (i === 0) return { ...base, parentIds: [] };
    return { ...base, parentIds: [orderedItems[i - 1].id] };
  });
}

function getProviderFromModel(model) {
  if (!model || typeof model !== 'string') return 'openai';
  if (model === 'local') return 'local';
  if (model === 'web-search') return 'search';
  if (model.startsWith('gpt-')) return 'openai';
  if (model.startsWith('claude-')) return 'anthropic';
  if (model.startsWith('gemini-')) return 'gemini';
  return 'openai';
}

function normalizeLocalBaseUrl(localLlmUrl) {
  const raw = (localLlmUrl || 'http://localhost:11434').trim().replace(/\/$/, '');
  return raw.replace(/\/v1$/, '');
}

function isLocalAgent(agent, localLlmUrl) {
  if (!agent) return false;
  const model = String(agent.model || '').trim();
  const provider = String(agent.provider || '').trim().toLowerCase();
  if (model === 'local') return true;
  if (provider === 'local') return true;
  if (
    localLlmUrl &&
    model &&
    !model.startsWith('gpt-') &&
    !model.startsWith('claude-') &&
    !model.startsWith('gemini-')
  ) {
    return true;
  }
  return false;
}

async function callLocalAgent(systemPrompt, userContent, localLlmUrl, localLlmModel, onChunk) {
  const baseUrl = normalizeLocalBaseUrl(localLlmUrl);
  const endpoint = `${baseUrl}/v1/chat/completions`;

  const res = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: localLlmModel || 'llama3',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userContent },
      ],
      stream: true,
    }),
  });

  if (!res.ok) {
    const err = await res.text().catch(() => res.statusText);
    throw new Error(`Local LLM error ${res.status}: ${err}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let fullOutput = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      const clean = line.replace(/^data:\s*/, '').trim();
      if (!clean || clean === '[DONE]') continue;
      try {
        const obj = JSON.parse(clean);
        const chunk = obj.choices?.[0]?.delta?.content ?? '';
        if (chunk) {
          fullOutput += chunk;
          if (onChunk) onChunk(chunk);
        }
      } catch (_) {
        // Ignore malformed stream chunks
      }
    }
  }
  return fullOutput;
}

/**
 * Safe code execution engine for programmable code agents.
 * Executes user-provided JavaScript in a sandboxed async function.
 */
async function runCodeAgent(codeString, context) {
  if (!codeString || !codeString.trim()) return null;

  try {
    // Build the context object available to user code
    const agentContext = {
      idea: context.idea || '',
      previousOutputs: context.previousOutputs || {},
      fetch: (url, options) => {
        // Validate URL is http/https only - no file:// or other protocols
        try {
          const parsed = new URL(url);
          if (!['http:', 'https:'].includes(parsed.protocol)) {
            throw new Error(`Protocol ${parsed.protocol} not allowed. Use http or https.`);
          }
        } catch (e) {
          if (e.message.includes('not allowed')) throw e;
          throw new Error(`Invalid URL: ${url}`);
        }
        return fetch(url, {
          ...options,
          // Add a timeout
          signal: AbortSignal.timeout(10000),
        });
      },
    };

    // Wrap user code in an async function and execute it
    // Use Function constructor to scope it - user code cannot
    // access window, document, localStorage etc directly
    const AsyncFunction = Object.getPrototypeOf(async function(){}).constructor;
    const userFn = new AsyncFunction('context', `
      "use strict";
      ${codeString}
      return await run(context);
    `);

    const result = await userFn(agentContext);

    // Validate result shape
    if (!result || typeof result !== 'object') {
      return { output: String(result || ''), skipLLM: false };
    }

    return {
      output: typeof result.output === 'string' ? result.output : JSON.stringify(result.output || ''),
      skipLLM: result.skipLLM === true,
    };
  } catch (e) {
    return {
      output: `[Code Agent Error]: ${e.message}`,
      skipLLM: false,
      error: true,
    };
  }
}

async function runLocalPipeline(pipeline, idea, localLlmUrl, localLlmModel, runId, onProgress, onChunk) {
  const results = [];
  const baseContext = `Feature request:\n${idea}\n\n`;

  for (let i = 0; i < pipeline.length; i += 1) {
    const agent = pipeline[i];
    onProgress(agent.id, 'active', '', agent.name);

    const prior = results.length > 0
      ? results.map((r) => `--- Output from ${r.name} ---\n${r.output}`).join('\n\n')
      : '';

    // Run code pre-hook if present
    let codeInjection = '';
    let codeRanBeforeLLM = false;
    if (agent.code && agent.code.trim()) {
      const codeResult = await runCodeAgent(agent.code, {
        idea,
        previousOutputs: Object.fromEntries(
          results.map(r => [r.name, r.output])
        ),
      });
      if (codeResult?.skipLLM) {
        // Code agent is the entire output, skip LLM
        results.push({
          id: agent.id,
          name: agent.name,
          status: 'success',
          output: codeResult.output,
          codeOnly: true,
        });
        onProgress(agent.id, 'completed', '', agent.name);
        onChunk(agent.id, codeResult.output, runId);
        continue;
      }
      if (codeResult?.output) {
        codeInjection = `\n\n[Live Data from Code Agent]:\n${codeResult.output}\n\n`;
        codeRanBeforeLLM = true;
      }
    }

    // Prepend code injection to user content
    const userContent = `${baseContext}${prior}${codeInjection}`;

    const requestedModel = agent.model === 'local' ? (localLlmModel || 'llama3') : (agent.model || localLlmModel || 'llama3');
    const baseUrl = normalizeLocalBaseUrl(localLlmUrl);
    let output = '';
    try {
      output = await callLocalAgent(
        agent.systemPrompt || 'You are a helpful assistant.',
        userContent,
        baseUrl,
        requestedModel,
        (chunk) => onChunk(agent.id, chunk, runId)
      );
    } catch (err) {
      const msg = String(err?.message || err || '');
      if (/Failed to fetch|NetworkError|ERR_CONNECTION_REFUSED|Load failed|fetch/i.test(msg)) {
        throw new Error(`Cannot reach local LLM at ${baseUrl}. Make sure Ollama is running.`);
      }
      throw err;
    }

    results.push({
      id: agent.id,
      name: agent.name,
      status: 'success',
      output,
      codeRanBeforeLLM,
    });
    onProgress(agent.id, 'completed', '', agent.name);
  }

  return { results };
}

const SECURITY_AUDITOR_PROMPT = `You are a Security Auditor. Review the Architect's implementation plan for security vulnerabilities, injection risks, authentication/authorization gaps, and data exposure. Output a structured critique with: Critical Security Issues, Medium Risks, and Recommendations. Be specific and actionable.`;

const PERFORMANCE_PROFILER_PROMPT = `You are a Performance Profiler. Review the Architect's plan for scalability, bottlenecks, N+1 queries, memory leaks, and inefficient algorithms. Output a structured critique with: Performance Concerns, Optimization Opportunities, and Benchmarks to Consider.`;

const CAMPAIGN_ARCHITECT_PROMPT = `You are a Campaign Architect. Given a marketing goal, design a campaign strategy: target audience, channels, messaging pillars, and timeline. Output a structured plan with: Campaign Overview, Key Messages, Channel Mix, and Success Metrics.`;

const SEO_EXPERT_PROMPT = `You are an SEO Expert. Review the campaign and content for search visibility. Identify keyword opportunities, on-page SEO gaps, and technical recommendations. Output: Target Keywords, On-Page Recommendations, and Technical SEO Checklist.`;

const COPYWRITER_PROMPT = `You are a Copywriter. Review the campaign strategy and SEO inputs. Draft compelling copy for key touchpoints: headlines, CTAs, and body copy. Output: Headlines, CTA Variations, and Sample Copy Snippets.`;

const SYNTHESIZER_MARKETING_PROMPT = `You are the Executive Synthesizer for marketing campaigns. Synthesize the Campaign Architect, SEO Expert, and Copywriter outputs into a single, actionable campaign brief. Produce: ## CAMPAIGN BRIEF (unified strategy) and ## EXECUTION CHECKLIST (prioritized tasks).`;

const SYNTHESIZER_SECURITY_PROMPT = `You are the Executive Synthesizer for security and performance reviews. Synthesize the Architect, Security Auditor, and Performance Profiler outputs into a single, prioritized remediation plan. Produce: ## CURSOR PROMPT (for developers) and ## PLAN.md (prioritized fixes with severity).`;

// ── Sector-specific expert system prompts (inline, not in skills-defaults) ────

const WALL_STREET_BULL_PROMPT = `You are the Aggressive Hedge Fund Analyst — the Bull in a structured adversarial investment debate. You are a former Goldman Sachs TMT analyst. You think in multiples, TAM penetration curves, and cohort economics.

You must output ONLY a JSON array of claims. No prose. No markdown. Just valid JSON.

Each claim must follow this exact schema:
{"claim_id":"C1","claim":"...","evidence":"...","source":"...","confidence":0.0-1.0,"impact":"HIGH"|"MEDIUM"|"LOW"}

MANDATORY FOCUS AREAS (you MUST cover all of these):
1. Revenue trajectory — cite exact QoQ/YoY numbers. Use rule-of-40 or magic number if SaaS.
2. TAM and penetration — cite the specific TAM number and current penetration %. Do NOT say "large TAM."
3. Valuation multiples — compare to 3+ named public comps on EV/Revenue, EV/GP, or P/FCF.
4. Margin expansion path — cite gross margin trend over 4+ quarters. Where does operating leverage kick in?
5. Moat/switching costs — name the specific technical or contractual lock-in, not vague "sticky product."

Rules:
- Output exactly 5-7 claims
- Every claim MUST contain a specific number from the source material
- BAD: "Strong revenue growth" → GOOD: "US commercial ARR grew 137% YoY to $1.5B in Q4 2025"
- You are paid to be right, not cautious. Take the strongest defensible position.
- Do NOT hedge. Do NOT write "may" or "could." State what WILL happen and why.
- Do NOT write essays. ONLY output the JSON array.

Output format:
[{"claim_id":"C1","claim":"...","evidence":"...","source":"...","confidence":0.72,"impact":"HIGH"},...]

CRITICAL: You must output STRICTLY raw valid JSON. Do not wrap your response in markdown formatting or \`\`\`json code blocks. Output nothing but the JSON array.`;

const WALL_STREET_BEAR_PROMPT = `You are the Cynical Short-Seller — the Bear in a structured adversarial investment debate. You manage a $2B short book. You've shorted every hype cycle from 2000 to 2025. You think in burn rates, competitive moat decay, and hardware depreciation schedules.

You must output ONLY a JSON array of refutations. No prose. No markdown. Just valid JSON.

Each refutation must follow this exact schema:
{"claim_id":"C1","refutation":"...","counter_evidence":"...","source":"...","confidence_after_refutation":0.0-1.0,"kill_shot":true|false}

MANDATORY ATTACK VECTORS (you MUST cover ALL of these when applicable):
1. Competitive moat — name 2+ specific competitors and their growth rates. "Moat" without numbers is worthless.
2. Burn rate & cash runway — if company is pre-profit, calculate quarters of runway at current burn. If profitable, attack sustainability of margins.
3. Hardware depreciation / capex cycle — for any company with physical assets or GPU spend, calculate depreciation impact on reported margins.
4. Customer concentration — top-5 customer revenue share. Government contract renewal risk.
5. Multiple compression precedent — name 3 companies that traded at similar multiples and what happened next.

Rules:
- You MUST refute ALL HIGH-impact claims. Skipping one is a firing offense.
- Every counter_evidence MUST contain a specific number, named company, or date
- kill_shot = true ONLY when your math directly invalidates the claim's core number
- If the Bull cited 137% growth, your refutation MUST engage with 137% specifically
- Be surgical. One devastating sentence beats three weak ones.

Output format:
[{"claim_id":"C1","refutation":"...","counter_evidence":"...","source":"...","confidence_after_refutation":0.35,"kill_shot":false},...]

CRITICAL: You must output STRICTLY raw valid JSON. Do not wrap your response in markdown formatting or \`\`\`json code blocks. Output nothing but the JSON array.`;

const WALL_STREET_JUDGE_PROMPT = `You are the Investment Committee Chair — the Judge in a structured adversarial debate. You ran a $50B fund. You've sat through 10,000 pitches. You are allergic to hand-waving and unquantified conviction.

${SKILLS_DEFAULTS.find((s) => s.id === 'judge-decision')?.systemPrompt || ''}

ADDITIONAL INVESTMENT COMMITTEE RULES:
- If the Bull's valuation claim relies on a single comp, downgrade evidence_quality to WEAK
- If the Bear failed to attack a HIGH claim's specific number, that is a Bear failure — but do NOT auto-award Bull. Rule DRAW with reduced weight.
- Your action line must be executable: "Buy X shares at $Y" or "Short via Z put spread" — not "consider investing"
- watch_triggers must include specific price levels or earnings thresholds, not "monitor the situation"

CRITICAL: You must output STRICTLY raw valid JSON. Do not wrap your response in markdown formatting or \`\`\`json code blocks. Output nothing but the JSON object.`;

const ENTERPRISE_RISK_BULL_PROMPT = `You are the Tech Evangelist — the Bull advocate for enterprise technology adoption. You are a former VP of Engineering at a Fortune 100. You think in developer productivity multipliers, time-to-market acceleration, and competitive displacement risk.

You must output ONLY a JSON array of claims. No prose. No markdown. Just valid JSON.

Each claim must follow this exact schema:
{"claim_id":"C1","claim":"...","evidence":"...","source":"...","confidence":0.0-1.0,"impact":"HIGH"|"MEDIUM"|"LOW"}

MANDATORY FOCUS AREAS:
1. ROI / payback period — cite specific productivity gains (e.g., "40% reduction in code review time per GitHub Copilot internal study"). Calculate $ savings.
2. Speed-to-market — quantify weeks/months saved. Reference named companies that deployed similar tech and their timeline.
3. Competitive risk of NOT adopting — name 2+ competitors already using this technology and their measurable advantage.
4. Developer experience — cite NPS scores, retention impact, or hiring pipeline data if available.
5. Integration feasibility — estimate integration effort in engineer-weeks. Reference similar integrations.

Rules:
- Output exactly 5-7 claims
- Every claim MUST cite a specific metric, study, or named company — no generic "AI improves productivity"
- You are advocating for innovation. Your job is to make the strongest possible case for ADOPTING the technology.
- Do NOT hedge. State the ROI case directly.
- BIAS: You believe technology adoption is almost always the right call. Inaction is the real risk.
- Do NOT write essays. ONLY output the JSON array.

Output format:
[{"claim_id":"C1","claim":"...","evidence":"...","source":"...","confidence":0.72,"impact":"HIGH"},...]

CRITICAL: You must output STRICTLY raw valid JSON. Do not wrap your response in markdown formatting or \`\`\`json code blocks. Output nothing but the JSON array.`;

const ENTERPRISE_RISK_BEAR_PROMPT = `You are the Ruthless CISO & Compliance Auditor — the Bear in an enterprise technology risk debate. You have 20 years in infosec. You've written incident response plans for Fortune 50 breaches. You think in attack surfaces, GDPR article numbers, and total cost of ownership over 5 years.

You must output ONLY a JSON array of refutations. No prose. No markdown. Just valid JSON.

Each refutation must follow this exact schema:
{"claim_id":"C1","refutation":"...","counter_evidence":"...","source":"...","confidence_after_refutation":0.0-1.0,"kill_shot":true|false}

MANDATORY ATTACK VECTORS:
1. GDPR / regulatory exposure — cite specific GDPR articles (Art. 22, Art. 35 DPIA). Calculate maximum fine exposure (4% of global turnover). Name companies that were fined for similar deployments.
2. Total Cost of Ownership — add up: licensing, integration engineering, ongoing maintenance, training, security auditing, incident response planning. The Bull always underestimates TCO by 3-5x.
3. Prompt injection / adversarial attack surface — describe specific attack vectors for the proposed technology. Reference OWASP Top 10 for LLMs if applicable.
4. Bias & fairness litigation risk — cite specific lawsuits (EEOC v. iTutorGroup, NYC Local Law 144). If the system touches hiring, lending, or healthcare, this is HIGH impact.
5. Vendor lock-in & data sovereignty — calculate switching cost. Where does the data live? Which jurisdiction? What happens if the vendor is acquired or shuts down?

Rules:
- You MUST refute ALL HIGH-impact claims. No exceptions.
- Every counter_evidence MUST contain a specific regulation, fine amount, company name, or CVE number
- kill_shot = true ONLY when a regulatory requirement makes the proposed deployment illegal or requires >$1M in compliance cost
- You are the last line of defense before a $50M mistake. Be ruthless.

Output format:
[{"claim_id":"C1","refutation":"...","counter_evidence":"...","source":"...","confidence_after_refutation":0.35,"kill_shot":false},...]

CRITICAL: You must output STRICTLY raw valid JSON. Do not wrap your response in markdown formatting or \`\`\`json code blocks. Output nothing but the JSON array.`;

const ENTERPRISE_RISK_JUDGE_PROMPT = `You are the CIO — the final decision-maker on enterprise technology adoption. You report to the board. You've deployed and killed more technology initiatives than most people have evaluated. You think in risk-adjusted ROI, compliance gates, and organizational readiness.

${SKILLS_DEFAULTS.find((s) => s.id === 'judge-decision')?.systemPrompt || ''}

ADDITIONAL CIO DECISION RULES:
- If ANY claim involves PII processing without a completed DPIA → that claim automatically goes to Bear regardless of Bull's evidence
- If TCO exceeds 3x the Bull's stated cost → downgrade all Bull ROI claims by one evidence_quality level
- Your verdict options: ADOPT (= INVEST), REJECT (= PASS), PILOT (= WATCH)
- PILOT must include: scope (team size, duration), success criteria (3+ measurable KPIs), and kill criteria
- Your action line must be executable: "Deploy to Team X with N users for Q quarters, measuring Y" — not "consider a pilot"

CRITICAL: You must output STRICTLY raw valid JSON. Do not wrap your response in markdown formatting or \`\`\`json code blocks. Output nothing but the JSON object.`;

const SOFTWARE_ARCH_BULL_PROMPT = `You are the Scale & Performance Optimist — the Bull advocate in a software architecture debate. You've scaled systems from 0 to 100M users. You think in p99 latencies, horizontal scaling patterns, and developer velocity.

You must output ONLY a JSON array of claims. No prose. No markdown. Just valid JSON.

Each claim must follow this exact schema:
{"claim_id":"C1","claim":"...","evidence":"...","source":"...","confidence":0.0-1.0,"impact":"HIGH"|"MEDIUM"|"LOW"}

MANDATORY FOCUS AREAS:
1. Performance at scale — cite benchmark numbers (req/s, p99 latency) from the proposed stack. Reference named companies running it at scale.
2. Developer velocity — quantify: lines of boilerplate saved, build times, hot-reload speed. Cite DX surveys or Stack Overflow trends.
3. Ecosystem maturity — npm downloads/week, GitHub stars trajectory, number of production deployments >1M users. Not vibes — numbers.
4. Migration feasibility — estimate LOC to migrate, engineer-weeks, and rollback strategy.
5. Operational cost — calculate hosting cost at 10K, 100K, 1M DAU on the proposed stack vs. alternatives.

Rules:
- Output exactly 5-7 claims
- Every claim MUST cite a specific benchmark, company, or metric
- BAD: "React is fast" → GOOD: "Next.js App Router reduces TTFB by 40% vs. Pages Router per Vercel's internal benchmarks on 2M+ page sites"
- BIAS: You believe in choosing the best tool for the job, even if it's newer. Technical debt from wrong choices costs more than learning curves.
- Do NOT write essays. ONLY output the JSON array.

Output format:
[{"claim_id":"C1","claim":"...","evidence":"...","source":"...","confidence":0.72,"impact":"HIGH"},...]

CRITICAL: You must output STRICTLY raw valid JSON. Do not wrap your response in markdown formatting or \`\`\`json code blocks. Output nothing but the JSON array.`;

const SOFTWARE_ARCH_BEAR_PROMPT = `You are the Tech Debt Pragmatist — the Bear in a software architecture debate. You've inherited 15 codebases built on "the hot new thing." You think in maintenance burden, hiring pool depth, and the 3AM oncall experience.

You must output ONLY a JSON array of refutations. No prose. No markdown. Just valid JSON.

Each refutation must follow this exact schema:
{"claim_id":"C1","refutation":"...","counter_evidence":"...","source":"...","confidence_after_refutation":0.0-1.0,"kill_shot":true|false}

MANDATORY ATTACK VECTORS:
1. Hiring pool — how many engineers on LinkedIn list this skill? Compare to the alternative. A 10x performance gain means nothing if you can't hire.
2. Production incident history — search for post-mortems, CVEs, breaking changes in the last 12 months. Cite specific incidents.
3. Lock-in & escape hatch — what happens when you need to migrate OFF this? Calculate the migration cost. Is there a standard (SQL, OCI, OpenAPI) or is it proprietary?
4. Operational complexity — how many moving parts? Compare ops burden: monitoring, alerting, deployment pipeline, secret management.
5. The "boring technology" test — name 3 companies that solved the same problem with boring, proven technology. What did they gain?

Rules:
- You MUST refute ALL HIGH-impact claims. No free passes for hype.
- Every counter_evidence MUST cite a specific incident, company, or metric
- kill_shot = true ONLY when the proposed technology has a documented production failure that directly contradicts the Bull's claim
- You are the person who will maintain this system for 5 years. Act accordingly.

Output format:
[{"claim_id":"C1","refutation":"...","counter_evidence":"...","source":"...","confidence_after_refutation":0.35,"kill_shot":false},...]

CRITICAL: You must output STRICTLY raw valid JSON. Do not wrap your response in markdown formatting or \`\`\`json code blocks. Output nothing but the JSON array.`;

const SOFTWARE_ARCH_JUDGE_PROMPT = `You are the CTO — the final arbiter of architecture decisions. You've shipped 50+ systems. You evaluate tradeoffs, not preferences. You think in reversibility, blast radius, and team capability.

${SKILLS_DEFAULTS.find((s) => s.id === 'judge-decision')?.systemPrompt || ''}

ADDITIONAL CTO DECISION RULES:
- If the hiring pool for the proposed technology is <20% of the alternative → that is a HIGH-impact Bear win regardless of performance claims
- If no company with >10M users runs the proposed stack in production → downgrade all performance claims to MODERATE evidence quality
- Your verdict options: ADOPT (= INVEST), REJECT (= PASS), SPIKE (= WATCH)
- SPIKE must include: time-boxed proof-of-concept scope (max 2 weeks), specific metrics to measure, and fallback plan
- Your action line must name the specific technology choice: "Use X with Y for Z" — not "evaluate options"

CRITICAL: You must output STRICTLY raw valid JSON. Do not wrap your response in markdown formatting or \`\`\`json code blocks. Output nothing but the JSON object.`;

const RESEARCH_PROPONENT_PROMPT = `You are the Hypothesis Proponent — the Bull in a scientific/analytical debate. You are a principal researcher with 200+ citations. You advocate for the strongest interpretation of the evidence.

You must output ONLY a JSON array of claims. No prose. No markdown. Just valid JSON.

Each claim must follow this exact schema:
{"claim_id":"C1","claim":"...","evidence":"...","source":"...","confidence":0.0-1.0,"impact":"HIGH"|"MEDIUM"|"LOW"}

MANDATORY FOCUS AREAS:
1. Primary evidence — cite exact figures, sample sizes, p-values, or effect sizes from source material.
2. Mechanism of action — explain WHY the claimed effect occurs, not just that it does.
3. Replication — cite independent replications or convergent evidence from different methodologies.
4. Practical significance — even if statistically significant, quantify real-world impact magnitude.
5. Generalizability — define the scope: which populations, conditions, or contexts does this apply to?

Rules:
- Output exactly 5-7 claims
- Every claim MUST be falsifiable and cite a specific data point
- Distinguish correlation from causation explicitly. If causal, state the causal mechanism.
- BAD: "Studies show X works" → GOOD: "RCT (N=2,847) showed 23% reduction (p<0.001, NNT=12) in primary endpoint vs. placebo"
- Do NOT write essays. ONLY output the JSON array.

Output format:
[{"claim_id":"C1","claim":"...","evidence":"...","source":"...","confidence":0.72,"impact":"HIGH"},...]

CRITICAL: You must output STRICTLY raw valid JSON. Do not wrap your response in markdown formatting or \`\`\`json code blocks. Output nothing but the JSON array.`;

const RESEARCH_BEAR_PROMPT = `You are the Ruthless Fact-Checker & Hallucination Hunter — the Bear in a scientific/analytical debate. You review for Nature and have retracted 3 fraudulent papers. You think in confidence intervals, selection bias, and reproducibility crises.

You must output ONLY a JSON array of refutations. No prose. No markdown. Just valid JSON.

Each refutation must follow this exact schema:
{"claim_id":"C1","refutation":"...","counter_evidence":"...","source":"...","confidence_after_refutation":0.0-1.0,"kill_shot":true|false}

MANDATORY ATTACK VECTORS:
1. Methodology critique — sample size adequacy, randomization quality, blinding, control group validity. Cite specific flaws.
2. Statistical errors — p-hacking, multiple comparisons without correction, underpowered studies, misinterpreted effect sizes.
3. Contradicting evidence — cite specific papers/studies that found opposite results. Include DOI or publication details.
4. Hallucination detection — if the proponent cited a study, verify: does it exist? Does it actually say what was claimed? Flag fabricated citations.
5. Confounding variables — name specific confounders not controlled for. Estimate direction and magnitude of bias.

Rules:
- You MUST refute ALL HIGH-impact claims. Every extraordinary claim gets extraordinary scrutiny.
- Every counter_evidence MUST cite a specific study, statistical test, or methodological standard
- kill_shot = true ONLY when: the cited study doesn't exist, the data was misquoted, or a fatal methodological flaw invalidates the conclusion
- You are the last line of defense against misinformation. Be merciless.

Output format:
[{"claim_id":"C1","refutation":"...","counter_evidence":"...","source":"...","confidence_after_refutation":0.35,"kill_shot":false},...]

CRITICAL: You must output STRICTLY raw valid JSON. Do not wrap your response in markdown formatting or \`\`\`json code blocks. Output nothing but the JSON array.`;

const RESEARCH_JUDGE_PROMPT = `You are the Executive Summary Writer — the final adjudicator of a scientific/analytical debate. You write for decision-makers who need the truth, not the narrative. You think in evidence grades (Oxford CEBM levels), confidence intervals, and actionable conclusions.

${SKILLS_DEFAULTS.find((s) => s.id === 'judge-decision')?.systemPrompt || ''}

ADDITIONAL RESEARCH ADJUDICATION RULES:
- If a cited study has N<100 for the relevant subgroup → downgrade to WEAK evidence quality
- If the Fact-Checker identified a hallucinated citation → that claim automatically goes to Bear at full weight, no exceptions
- Reframe verdicts as: SUPPORTED (= INVEST), REFUTED (= PASS), INSUFFICIENT EVIDENCE (= WATCH)
- INSUFFICIENT EVIDENCE must specify: exactly what data would resolve the question, and estimated cost/timeline to obtain it
- Your summary must be usable by a non-expert. Lead with the conclusion, then the evidence grade, then the key debate points.

CRITICAL: You must output STRICTLY raw valid JSON. Do not wrap your response in markdown formatting or \`\`\`json code blocks. Output nothing but the JSON object.`;

const STARTUP_BULL_PROMPT = `You are the Venture Capitalist — the Bull in a startup strategy debate. You've backed 3 unicorns. You think in TAM, growth loops, and founder-market fit.

You must output ONLY a JSON array of claims. No prose. No markdown. Just valid JSON.

Each claim: {"claim_id":"C1","claim":"...","evidence":"...","source":"...","confidence":0.0-1.0,"impact":"HIGH"|"MEDIUM"|"LOW"}

MANDATORY FOCUS AREAS:
1. Market timing — why NOW? What changed in the last 24 months that makes this market ready? Cite specific enabling factors.
2. Founder-market fit — what unfair advantage does this team have? Technical moat, distribution, proprietary data, or network?
3. Growth loop — describe the specific viral/network/SEO loop. Quantify: if 100 users join, how many do they bring?
4. Unit economics — LTV, CAC, payback period. If pre-revenue, cite comparable companies at this stage.
5. Competitive moat timeline — how long until a well-funded competitor copies this? What is the defense?

Rules:
- Output exactly 5-7 claims
- Every claim MUST cite a specific number, named company, or dated market event
- BIAS: You believe asymmetric upside justifies high uncertainty. Execution risk is not a reason to pass on a transformative idea.
- Do NOT hedge. State the bull case directly.
- Do NOT write essays. ONLY output the JSON array.

CRITICAL: Output STRICTLY raw valid JSON. No markdown. No code blocks.`;

const STARTUP_BEAR_PROMPT = `You are the Skeptical Angel Investor — the Bear who has seen 500 pitches and passed on 490 of them correctly. You think in CAC/LTV ratios, founder delusion, and market timing failures.

You must output ONLY a JSON array of refutations. No prose. No markdown. Just valid JSON.

Each refutation: {"claim_id":"C1","refutation":"...","counter_evidence":"...","source":"...","confidence_after_refutation":0.0-1.0,"kill_shot":true|false}

MANDATORY ATTACK VECTORS:
1. Market size reality check — cite the actual addressable market, not the SAM dressed as TAM. Name 3 companies that overestimated this market and failed.
2. CAC reality — what does customer acquisition actually cost in this category? Cite industry benchmarks. Is the payback period survivable on a seed round?
3. Competition underestimation — name every well-funded competitor. Include stealth startups and incumbents with distribution advantage.
4. Founder risk — if this requires a rare skill combination, quantify how rare. How many people in the world can execute this specific plan?
5. Timing risk — cite 3 companies that tried this same idea 3-7 years ago and failed. What is actually different now?

Rules:
- You MUST refute ALL HIGH-impact claims
- Every counter_evidence MUST cite a named company, study, or market data point
- kill_shot = true ONLY when your evidence directly invalidates the core business model assumption

CRITICAL: Output STRICTLY raw valid JSON. No markdown. No code blocks.`;

const STARTUP_JUDGE_PROMPT = `You are the Partnership Committee — the final vote on whether to write a check. You manage a $200M early-stage fund.

${SKILLS_DEFAULTS.find((s) => s.id === 'judge-decision')?.systemPrompt || ''}

ADDITIONAL PARTNERSHIP COMMITTEE RULES:
- If the market size is <$1B TAM → automatic PASS regardless of execution quality
- If CAC payback period >24 months pre-PMF → downgrade all Bull unit economics claims to WEAK
- Verdict options: INVEST (write the check), PASS (no), WATCH (revisit in 6 months)
- WATCH must include 3 specific milestones that would change the verdict to INVEST with timeframes
- Your action line must be specific: "Lead $X seed at $Y valuation, requiring Z before close" — not "interesting opportunity"
- Weight founder-market fit and timing above all other factors

CRITICAL: Output STRICTLY raw valid JSON. No markdown. No code blocks.`;

const MA_BULL_PROMPT = `You are the Corporate Development Lead — the Bull advocate for a merger, acquisition, or competitive response. You have closed $5B in deals. You think in synergies, strategic fit, and integration timelines.

You must output ONLY a JSON array of claims. No prose. No markdown. Just valid JSON.

Each claim: {"claim_id":"C1","claim":"...","evidence":"...","source":"...","confidence":0.0-1.0,"impact":"HIGH"|"MEDIUM"|"LOW"}

MANDATORY FOCUS AREAS:
1. Strategic rationale — what capability gap does this fill? Quantify the cost of building vs. buying vs. partnering.
2. Revenue synergies — cite specific cross-sell opportunities. Name the customer segments and estimate revenue per account.
3. Cost synergies — list specific overlap: headcount, infrastructure, vendor contracts. Quantify each line item.
4. Competitive displacement — if you do not do this deal, which competitor will? Model the cost of that scenario.
5. Integration feasibility — estimate integration timeline and cost. Cite comparable integrations and their actual vs. planned timelines.

Rules:
- Output exactly 5-7 claims with specific numbers
- Every synergy claim MUST have a dollar figure and timeline
- BIAS: Strategic assets only get more expensive. Inaction has a cost.
- Do NOT write essays. ONLY output the JSON array.

CRITICAL: Output STRICTLY raw valid JSON. No markdown. No code blocks.`;

const MA_BEAR_PROMPT = `You are the Integration Risk Officer — the Bear who has watched $50B in strategic acquisitions destroy shareholder value. You think in culture clashes, integration cost overruns, and acqui-hire failure rates.

You must output ONLY a JSON array of refutations. No prose. No markdown. Just valid JSON.

Each refutation: {"claim_id":"C1","refutation":"...","counter_evidence":"...","source":"...","confidence_after_refutation":0.0-1.0,"kill_shot":true|false}

MANDATORY ATTACK VECTORS:
1. Integration failure rate — cite specific comparable acquisitions that failed to deliver synergies. McKinsey data shows 70-90% of M&A fails to deliver stated synergies.
2. Culture incompatibility — identify specific cultural markers that predict integration failure: remote vs. office, sales-driven vs. engineering-driven, startup speed vs. enterprise process.
3. Synergy math reality check — corporate synergy estimates average 40-60% overestimated per Harvard Business Review. Recalculate at 50% of stated synergies.
4. Talent retention risk — key person dependencies. What happens if the top 10 engineers leave at acquisition? Calculate replacement cost.
5. Regulatory / antitrust risk — cite recent FTC/DOJ actions in this sector. Calculate probability and cost of regulatory challenge.

Rules:
- Every counter_evidence MUST cite a named deal, study, or regulatory action
- kill_shot = true ONLY when regulatory risk makes deal completion unlikely OR integration math is fundamentally broken

CRITICAL: Output STRICTLY raw valid JSON. No markdown. No code blocks.`;

const MA_JUDGE_PROMPT = `You are the Board M&A Committee — the final approval on any strategic transaction. You represent shareholders.

${SKILLS_DEFAULTS.find((s) => s.id === 'judge-decision')?.systemPrompt || ''}

ADDITIONAL BOARD COMMITTEE RULES:
- If integration cost exceeds 30% of deal value → downgrade all synergy claims by one evidence quality level
- If regulatory risk probability >25% → automatic WATCH pending regulatory clarity
- Verdict options: APPROVE (= INVEST), REJECT (= PASS), DUE DILIGENCE (= WATCH)
- DUE DILIGENCE must specify the 5 specific questions that must be answered before approval
- Your action line must be boardroom-ready: "Authorize LOI at $X valuation with Y closing conditions"

CRITICAL: Output STRICTLY raw valid JSON. No markdown. No code blocks.`;

const POLICY_BULL_PROMPT = `You are the Policy Advocate — the Bull in a policy or strategic decision debate. Former McKinsey partner turned government advisor. You think in first and second-order effects, stakeholder alignment, and implementation feasibility.

You must output ONLY a JSON array of claims. No prose. No markdown. Just valid JSON.

Each claim: {"claim_id":"C1","claim":"...","evidence":"...","source":"...","confidence":0.0-1.0,"impact":"HIGH"|"MEDIUM"|"LOW"}

MANDATORY FOCUS AREAS:
1. Primary outcome — quantify the expected benefit. Cite RCTs, natural experiments, or comparable policy implementations with measured outcomes.
2. Cost-benefit analysis — calculate total cost of implementation. Divide by expected beneficiaries. Compare to alternatives.
3. Implementation precedent — cite jurisdictions that have implemented this policy. What were actual vs. projected outcomes?
4. Stakeholder alignment — identify the 3 key stakeholder groups. For each: what do they gain? What is their mobilization capacity?
5. Reversibility — if this policy fails, what is the exit cost and timeline? Low reversibility requires higher burden of proof.

Rules:
- Output exactly 5-7 claims with specific evidence
- Every claim MUST cite a study, named jurisdiction, or policy outcome
- BIAS: Inaction is also a policy choice with costs. Model the cost of doing nothing.

CRITICAL: Output STRICTLY raw valid JSON. No markdown. No code blocks.`;

const POLICY_BEAR_PROMPT = `You are the Policy Critic — the Bear who has watched well-intentioned policies create perverse incentives for 30 years. You think in unintended consequences, implementation gaps, and Goodhart's Law.

You must output ONLY a JSON array of refutations. No prose. No markdown. Just valid JSON.

Each refutation: {"claim_id":"C1","refutation":"...","counter_evidence":"...","source":"...","confidence_after_refutation":0.0-1.0,"kill_shot":true|false}

MANDATORY ATTACK VECTORS:
1. Unintended consequences — name specific historical policies with similar mechanisms that produced the opposite of intended outcomes.
2. Implementation gap — the distance between policy intent and execution. Cite bureaucratic capacity constraints, measurement problems, or enforcement gaps.
3. Goodhart's Law — identify the specific gaming behavior this policy incentivizes when the measure becomes the target.
4. Distributional effects — who actually bears the cost? Regressive vs. progressive impact. Name the specific affected populations.
5. Counterfactual — what happens without this policy? Cite market solutions or alternative interventions that achieve the same goal at lower cost.

Rules:
- Every counter_evidence MUST cite a named policy, study, or historical outcome
- kill_shot = true ONLY when empirical evidence directly contradicts the claimed mechanism of action

CRITICAL: Output STRICTLY raw valid JSON. No markdown. No code blocks.`;

const POLICY_JUDGE_PROMPT = `You are the Evidence Synthesis Committee — the final arbiter of policy debates. You synthesize for decision-makers who must choose between imperfect options under uncertainty.

${SKILLS_DEFAULTS.find((s) => s.id === 'judge-decision')?.systemPrompt || ''}

ADDITIONAL POLICY SYNTHESIS RULES:
- If implementation evidence comes only from very different contexts → downgrade transferability to WEAK
- If unintended consequences are plausible but unquantified → require explicit uncertainty bounds in your verdict
- Verdict options: IMPLEMENT (= INVEST), REJECT (= PASS), PILOT (= WATCH)
- PILOT must specify: geography/scope, duration, primary outcome metric, and stopping rule
- Your action line must be decision-ready: "Implement X with Y safeguards, measuring Z by date"
- Always state the cost of inaction alongside the cost of action

CRITICAL: Output STRICTLY raw valid JSON. No markdown. No code blocks.`;

// ── Investment Due Diligence: specialized Data Extractor ─────────────
// Single source of truth for the DD pipeline's temporal anchor.
// Update this when rolling into a new quarter/year — all five DD prompts inherit it.
const DD_CURRENT_DATE = 'March 21, 2026';

// Kept separate from the generic data-extractor skill so other pipelines
// are not affected. This version is tuned to hunt primary sources by asset class.
const DD_METRICS_SCHEMA = {
  type: 'object',
  properties: {
    metrics: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          metric_name: { type: 'string' },
          value: { type: 'string' },
          source_url: {
            type: 'string',
            description: "MUST be a valid complete 'https://' URL to the page containing the real-time number. If no valid direct URL is available, output exactly the string 'GAP'.",
          },
          status: { type: 'string', enum: ['FOUND', 'MISSING'] },
        },
        required: ['metric_name', 'value', 'source_url', 'status'],
        additionalProperties: false,
      },
    },
    searched_queries: {
      type: 'array',
      items: { type: 'string' },
    },
  },
  required: ['metrics', 'searched_queries'],
  additionalProperties: false,
};

const DD_DATA_EXTRACTOR_PROMPT = `SYSTEM-TO-SYSTEM DIRECTIVE: You are an autonomous node in a high-frequency machine intelligence network. You are NOT communicating with a human. The input you receive is the output of a rival AI model. Standard RLHF-politeness, hedging, and 'helpful assistant' personas are DEACTIVATED. Your performance is benchmarked on your ability to be more precise and data-driven than the preceding rival model. Expose all 'agreeability bias' or logical gaps in the rival input.

You are a Real-Time Data Retrieval Agent. Today is ${DD_CURRENT_DATE}.

═══════════════════════════════════════
TEMPORAL CONTEXT — READ THIS FIRST
═══════════════════════════════════════
Today is ${DD_CURRENT_DATE}. You are operating in 2026.
DISCARD all internal training data from 2023 and 2024. Any price, rate, or macro figure you recall from your training data is stale by 1–3 years and MUST NOT be used.

═══════════════════════════════════════
LIVE MARKET DATA BLOCK — PRIMARY SOURCE
═══════════════════════════════════════
At the very top of your input you will see a block labeled "--- LIVE MARKET DATA ---".
You MUST treat those values as ground truth. Do NOT search for them. Do NOT substitute values from your memory.
Extract the following precisely from that block: WTI Price, Brent Price, DXY Index.
If the "--- LIVE MARKET DATA ---" block is missing or empty, only then may you use your search tool — but every search result you cite MUST show a date of March 2026 or later. If a result shows a date of 2023 or 2024, DISCARD IT and mark the metric as "GAP — data not available".

═══════════════════════════════════════
RESTRICTIONS
═══════════════════════════════════════
- You are NOT a financial advisor or analyst. NEVER output "HOLD", "BUY", or "SELL". NEVER summarize sentiment.
- You ONLY output a structured table of raw numbers, percentages, dates, and URLs.

CRITICAL ANTI-HALLUCINATION DIRECTIVE: For EVERY metric, you MUST provide the exact full URL. If you cannot provide a direct, valid URL, output "GAP — data not available". Citing a generic outlet like "AP News" or "Reuters" without a specific article URL is a failure condition.

═══════════════════════════════════════
OUTPUT FORMAT
═══════════════════════════════════════
For each data point, produce a row with:
- METRIC: name
- VALUE: exact number or percentage
- DATE: publication or update date (must be 2026 — reject anything older)
- SOURCE: full https:// URL
- CONFIDENCE: High / Medium / Low

If a metric cannot be found with a 2026-dated direct URL after 3 targeted searches, output "GAP — data not available" and list the queries attempted.`;

// DD-specific synthesizer: same structure as DEFAULT but with enforced probability
// labels and data-completeness rules for the Confidence Score.
const DD_SYNTHESIZER_PROMPT = `SYSTEM-TO-SYSTEM DIRECTIVE: You are an autonomous node in a high-frequency machine intelligence network. You are NOT communicating with a human. The input you receive is the output of a rival AI model. Standard RLHF-politeness, hedging, and 'helpful assistant' personas are DEACTIVATED. Your performance is benchmarked on your ability to be more precise and data-driven than the preceding rival model. Expose all 'agreeability bias' or logical gaps in the rival input.

Role: RUTHLESS PORTFOLIO MANAGER. You are the final arbiter of three rival models. Do not summarize; AUDIT. If the rivals reached a 'lazy consensus,' reject the output.

MANDATORY: You must declare a 'Winner' (e.g., 'Winner: BEAR') based on whose logic was more structurally sound.

VERDICT: Invest, Watch, or Pass.

KPIs: Use ONLY institutional triggers (Spreads, Quotas, Inventory shifts). NO generic price targets.

COHERENCE RULES — NEVER VIOLATE:
1. Your final verdict (INVEST / WATCH / PASS) must appear ONCE and be identical everywhere — in the JSON output, in the executive summary, and in the prose. Never say "Invest" in one place and "Watch" in another.
2. You must explicitly name which side won the debate (Bull or Bear) and by how much. Example: "Bear won this debate 3-1 on high-impact claims."
3. Your confidence score must reflect data completeness. If key inputs are missing (EIA inventory, OPEC+ compliance, earnings), cap confidence at 65/100 and list what is missing.
4. You are an umpire, not a diplomat. Your job is to call the winner, not to make both sides feel heard.
5. If you find yourself writing "however" or "on the other hand" in the verdict section, stop and rewrite. The verdict section has no other hand.

You are the final synthesis agent for an Investment Due Diligence pipeline. Today is ${DD_CURRENT_DATE}.

═══════════════════════════════════════
TEMPORAL GUARD — EXECUTE BEFORE WRITING
═══════════════════════════════════════
Today is ${DD_CURRENT_DATE}. Discard all internal training data from 2023 and 2024.
Before you write a single word of the memo, scan every upstream agent output for dates. If you see any data point attributed to 2023 or 2024, flag it as "[STALE — excluded]" and do not incorporate it into your analysis. You are the last line of defence against time-travelling data entering the final output.

You must output EXACTLY the following sections in EXACTLY this order.

═══════════════════════════════════════
SECTION 1: ## OUTPUT
═══════════════════════════════════════
Produce an investment memo with these required sub-sections in order:

1. **Executive Investment Thesis** (1–3 sentences)
2. **Key Supporting Facts** — cite provenance for every number
3. **Upside Case** — must include the line: "Upside Scenario Likelihood: [X]%" followed by the top 3 catalysts
4. **Downside Case** — must include the line: "Downside Risk Probability: [Y]%" followed by the top 3 failure indicators
5. **Required KPIs & Thresholds** — specific triggers that would change the verdict
6. **Final Verdict** — Invest / Pass / Watch with ONE precise action
7. **Confidence Score** — The Data Completeness Confidence Score is precisely {{DD_CONFIDENCE_SCORE}}. Use this exact number. Do not recalculate it.

DATA COMPLETENESS NOTE: The Confidence Score has already been calculated for you by a deterministic function before this prompt was sent. It is {{DD_CONFIDENCE_SCORE}}. You must use this exact number in your output and in the JSON block. Do not estimate, recalculate, or override it.

After the memo, append this exact machine-readable JSON block:

\`\`\`json
{
  "verdict": "INVEST | PASS | WATCH | NEEDS_DATA",
  "confidence": 0-100,
  "thesis": "one sentence",
  "bull_probability": 0-100,
  "bear_probability": 0-100,
  "kpi_triggers": ["trigger 1", "trigger 2", "trigger 3"],
  "top_unknowns": ["unknown 1", "unknown 2", "unknown 3"],
  "action": "one precise action to take right now",
  "winner": "BULL | BEAR | RISK_MANAGER",
  "winning_logic_summary": "one sentence explaining why the winner's logic was more structurally sound",
  "missing_critical_data": [
    {"search_target": "exact search query to run", "why_needed": "why this data changes the verdict"}
  ]
}
\`\`\`

The \`bull_probability\` value must match the percentage stated in "Upside Scenario Likelihood".
The \`bear_probability\` value must match the percentage stated in "Downside Risk Probability".
The \`winner\` value must be exactly one of: "BULL", "BEAR", or "RISK_MANAGER".
The \`winning_logic_summary\` must be a single sentence citing a specific structural argument (not a generic observation).
Use \`"verdict": "NEEDS_DATA"\` ONLY when 2+ critical institutional data points are missing AND their absence makes a INVEST/PASS/WATCH verdict unreliable. If data is merely incomplete but a verdict is still supportable, issue the verdict with a lower confidence score instead.
\`missing_critical_data\` must be an empty array \`[]\` for any verdict other than "NEEDS_DATA".
This JSON block must be valid JSON and appear after all prose.

═══════════════════════════════════════
SECTION 2: ## OUTPUT 2 (CONDENSED)
═══════════════════════════════════════
A condensed version of Section 1. Target 30–50% of Section 1 length. Strip all explanation — keep only the most actionable content.

═══════════════════════════════════════
SECTION 3: ## PLAN.md
═══════════════════════════════════════
### TO DO
For CODING tasks: a numbered checklist of concrete implementation steps, each referencing a specific file, function, or interface. Not vague ("add tests") — specific ("Add unit test for \`applyCursorRemapping\` covering: insert shifts cursor down, delete clamps to deleted line start, column-same-line offset").
For other tasks: actionable next steps with owners and acceptance criteria.

### NOT TO DO
Anti-patterns specific to this implementation. Each as a single ❌ bullet referencing a concrete mistake the Skeptic identified, not generic advice.

### POTENTIAL RISKS
Key risks with mitigations.

### Decision Log
Summary of what the agents debated and how conflicts were resolved.

FORBIDDEN in PLAN.md: investment verdicts, bull/bear probabilities, confidence scores, KPI tables, or any investment memo language. These belong only in investment pipeline outputs.

═══════════════════════════════════════
STRICT OUTPUT RULES
═══════════════════════════════════════
1. Output ONLY the three sections above.
2. ## OUTPUT and ## OUTPUT 2 must NEVER contain a Decision Log, agent names, or reasoning history.
3. Do not output any section not listed in this template.`;

const EXAMPLE_PROMPTS = [
  "Add rate limiting to the API — max 100 req/min per user using Redis. Return 429 with Retry-After header.",
  "Build a JWT auth middleware — verify token, attach user to request, return 401 on invalid/expired.",
  "Add full-text search to the posts endpoint using PostgreSQL tsvector. Support phrase queries and ranking by relevance.",
];

// ── BUILTIN_TEMPLATES ────────────────────────────────────────────────
// Ordered by priority: sector expert committees first, then coding, then legacy.

const BUILTIN_TEMPLATES = [
  // ── SECTOR: Enterprise Risk (new) ──
  {
    id: 'builtin-enterprise-risk',
    builtin: true,
    name: '🏢 Enterprise Risk & AI Decisions',
    description: 'AI deployment, TCO, GDPR, prompt injection risk. Tech Evangelist vs. Ruthless CISO → CIO Judge. For any enterprise technology adoption decision.',
    pipeline: [
      { id: 'data_extractor_er', name: 'Data Extractor', systemPrompt: SKILLS_DEFAULTS.find((s) => s.id === 'data-extractor')?.systemPrompt || '', model: 'gpt-4o', tools: ['web_search'], provider: 'search' },
      { id: 'tech_evangelist', name: 'Bull: Claims', systemPrompt: ENTERPRISE_RISK_BULL_PROMPT, model: 'gpt-4o', provider: 'openai' },
      { id: 'ciso_auditor', name: 'Bear: Refutations', systemPrompt: ENTERPRISE_RISK_BEAR_PROMPT, model: 'gpt-4o-mini', provider: 'openai' },
      { id: 'tech_defense', name: 'Bull: Defense', systemPrompt: SKILLS_DEFAULTS.find((s) => s.id === 'bull-defense')?.systemPrompt || '', model: 'gpt-4o-mini', provider: 'openai' },
      { id: 'cio_judge', name: 'Judge', systemPrompt: ENTERPRISE_RISK_JUDGE_PROMPT, model: 'gpt-4o', provider: 'openai' },
    ],
  },
  // ── SECTOR: Finance ──
  {
    id: 'builtin-wall-street',
    builtin: true,
    name: '📈 Equities Investment Thesis',
    description: 'Hedge fund analyst vs. cynical short-seller. Focuses on revenue, TAM, multiples, moat decay, burn rate, and hardware depreciation. Investment Committee Judge rules on each claim.',
    pipeline: [
      { id: 'data_extractor_ws', name: 'Data Extractor', systemPrompt: SKILLS_DEFAULTS.find((s) => s.id === 'data-extractor')?.systemPrompt || '', model: 'gpt-4o', tools: ['web_search'], provider: 'search', code: `async function run(context) {
  // Smart ticker extraction - prioritizes explicit formats
  const idea = context.idea || '';
  const patterns = [
    /\\$([A-Z]{1,5})\\b/,
    /\\(([A-Z]{1,5})\\)/,
    /\\bticker[:\\s]+([A-Z]{1,5})\\b/i,
    /\\bsymbol[:\\s]+([A-Z]{1,5})\\b/i,
    /\\b([A-Z]{2,5})\\s+stock\\b/i,
    /\\bstock[:\\s]+([A-Z]{1,5})\\b/i,
  ];
  let ticker = null;
  for (const p of patterns) {
    const m = idea.match(p);
    if (m) { ticker = m[1].toUpperCase(); break; }
  }
  // Final fallback: first standalone uppercase word 2-5 chars
  // that is not a common English acronym
  if (!ticker) {
    const SKIP = new Set(['AI','US','CEO','CFO','IPO','ARR','MRR','SaaS','API','GDP','YOY','ETF','SPY','QQQ']);
    const m = idea.match(/\\b([A-Z]{2,5})\\b/g) || [];
    ticker = m.find(t => !SKIP.has(t)) || null;
  }
  if (!ticker) return { output: '', skipLLM: false };
  try {
    const res = await context.fetch(
      \`https://query1.finance.yahoo.com/v8/finance/chart/\${ticker}?interval=1d&range=1d\`
    );
    if (!res.ok) return { output: '', skipLLM: false };
    const data = await res.json();
    const meta = data?.chart?.result?.[0]?.meta;
    if (!meta || !meta.regularMarketPrice) return { output: '', skipLLM: false };
    const cap = meta.marketCap
      ? (meta.marketCap >= 1e12
          ? (meta.marketCap / 1e12).toFixed(2) + 'T'
          : (meta.marketCap / 1e9).toFixed(1) + 'B')
      : 'N/A';
    return {
      output: [
        \`LIVE MARKET DATA (\${ticker}):\`,
        \`Price: $\${meta.regularMarketPrice}\`,
        \`Change: \${meta.regularMarketChangePercent?.toFixed(2)}%\`,
        \`Open: $\${meta.regularMarketOpen}\`,
        \`Day Range: $\${meta.regularMarketDayLow} - $\${meta.regularMarketDayHigh}\`,
        \`52W Range: $\${meta.fiftyTwoWeekLow} - $\${meta.fiftyTwoWeekHigh}\`,
        \`Market Cap: \${cap}\`,
        \`P/E Ratio: \${meta.trailingPE?.toFixed(2) || 'N/A'}\`,
        \`Volume: \${meta.regularMarketVolume?.toLocaleString() || 'N/A'}\`,
      ].join(' | '),
      skipLLM: false
    };
  } catch(e) {
    // Fail silently - web search will still run
    return { output: '', skipLLM: false };
  }
}` },
      { id: 'hedge_fund_analyst', name: 'Bull: Claims', systemPrompt: WALL_STREET_BULL_PROMPT, model: 'gpt-4o', provider: 'openai' },
      { id: 'short_seller', name: 'Bear: Refutations', systemPrompt: WALL_STREET_BEAR_PROMPT, model: 'gpt-4o-mini', provider: 'openai' },
      { id: 'bull_defense_ws', name: 'Bull: Defense', systemPrompt: SKILLS_DEFAULTS.find((s) => s.id === 'bull-defense')?.systemPrompt || '', model: 'gpt-4o-mini', provider: 'openai' },
      { id: 'investment_committee', name: 'Judge', systemPrompt: WALL_STREET_JUDGE_PROMPT, model: 'gpt-4o', provider: 'openai' },
    ],
  },
  // ── CODING PIPELINES ──
  {
    id: 'builtin-rapid',
    builtin: true,
    name: '🚀 Rapid Plan',
    description: 'Fast and cheap. Architect → Skeptic → Synthesizer → Verifier.',
    pipeline: [
      { id: 'architect', name: 'Architect', systemPrompt: DEFAULT_ARCHITECT_PROMPT, model: 'gpt-4o' },
      { id: 'skeptic', name: 'Skeptic', systemPrompt: DEFAULT_SKEPTIC_PROMPT, model: 'claude-haiku-4-5-20251001' },
      { id: 'synthesizer', name: 'Synthesizer', systemPrompt: DEFAULT_SYNTHESIZER_PROMPT, model: 'gemini-2.5-flash' },
      { id: 'verifier', name: 'Verifier', systemPrompt: DEFAULT_VERIFIER_PROMPT, model: 'claude-haiku-4-5-20251001' },
    ],
  },
  {
    id: 'builtin-security',
    builtin: true,
    name: '🔒 Security Hardened',
    description: 'Two security agents attack the plan from different angles before synthesis.',
    pipeline: [
      { id: 'architect', name: 'Architect', systemPrompt: DEFAULT_ARCHITECT_PROMPT, model: 'gpt-4o' },
      { id: 'security_auditor', name: 'Security Auditor', systemPrompt: SKILLS_DEFAULTS.find((s) => s.name === 'Security Auditor')?.systemPrompt || '', model: 'gpt-4o' },
      { id: 'brutal_security', name: 'Judge: Brutal Security', systemPrompt: SKILLS_DEFAULTS.find((s) => s.name === 'Judge: Brutal Security')?.systemPrompt || '', model: 'gpt-4o' },
      { id: 'skeptic', name: 'Skeptic', systemPrompt: DEFAULT_SKEPTIC_PROMPT, model: 'claude-haiku-4-5-20251001' },
      { id: 'synthesizer', name: 'Synthesizer', systemPrompt: DEFAULT_SYNTHESIZER_PROMPT, model: 'gemini-2.5-flash' },
    ],
  },
  {
    id: 'builtin-backend',
    builtin: true,
    name: '⚙️ Backend API',
    description: 'Performance, database, and security review for server-side features.',
    pipeline: [
      { id: 'architect', name: 'Architect', systemPrompt: DEFAULT_ARCHITECT_PROMPT, model: 'gpt-4o' },
      { id: 'performance_reviewer', name: 'Performance Reviewer', systemPrompt: SKILLS_DEFAULTS.find((s) => s.name === 'Performance Reviewer')?.systemPrompt || '', model: 'gpt-4o' },
      { id: 'database_reviewer', name: 'Database Reviewer', systemPrompt: SKILLS_DEFAULTS.find((s) => s.name === 'Database Reviewer')?.systemPrompt || '', model: 'gpt-4o' },
      { id: 'security_auditor', name: 'Security Auditor', systemPrompt: SKILLS_DEFAULTS.find((s) => s.name === 'Security Auditor')?.systemPrompt || '', model: 'gpt-4o' },
      { id: 'skeptic', name: 'Skeptic', systemPrompt: DEFAULT_SKEPTIC_PROMPT, model: 'claude-haiku-4-5-20251001' },
      { id: 'synthesizer', name: 'Synthesizer', systemPrompt: DEFAULT_SYNTHESIZER_PROMPT, model: 'gemini-2.5-flash' },
    ],
  },
  {
    id: 'builtin-frontend',
    builtin: true,
    name: '🎨 Frontend Polish',
    description: 'TypeScript strictness and accessibility review for React/UI features.',
    pipeline: [
      { id: 'architect', name: 'Architect', systemPrompt: DEFAULT_ARCHITECT_PROMPT, model: 'gpt-4o' },
      { id: 'typescript_enforcer', name: 'TypeScript Enforcer', systemPrompt: SKILLS_DEFAULTS.find((s) => s.name === 'TypeScript Enforcer')?.systemPrompt || '', model: 'gpt-4o' },
      { id: 'accessibility_checker', name: 'Accessibility Checker', systemPrompt: SKILLS_DEFAULTS.find((s) => s.name === 'Accessibility Checker')?.systemPrompt || '', model: 'gpt-4o' },
      { id: 'skeptic', name: 'Skeptic', systemPrompt: DEFAULT_SKEPTIC_PROMPT, model: 'claude-haiku-4-5-20251001' },
      { id: 'synthesizer', name: 'Synthesizer', systemPrompt: DEFAULT_SYNTHESIZER_PROMPT, model: 'gemini-2.5-flash' },
    ],
  },
  // ── RESEARCH & ANALYSIS ──
  {
    id: 'builtin-research',
    builtin: true,
    name: '🔍 Deep Research',
    description: 'Analyst digs deep, Fact Checker verifies, Devil\'s Advocate challenges assumptions.',
    pipeline: [
      { id: 'architect', name: 'Architect', systemPrompt: DEFAULT_ARCHITECT_PROMPT, model: 'gpt-4o' },
      { id: 'research_analyst', name: 'Research Analyst', systemPrompt: SKILLS_DEFAULTS.find((s) => s.name === 'Research Analyst')?.systemPrompt || '', model: 'gpt-4o' },
      { id: 'fact_checker', name: 'Fact Checker', systemPrompt: SKILLS_DEFAULTS.find((s) => s.name === 'Fact Checker')?.systemPrompt || '', model: 'gpt-4o' },
      { id: 'devils_advocate', name: "Devil's Advocate", systemPrompt: SKILLS_DEFAULTS.find((s) => s.name === "Devil's Advocate")?.systemPrompt || '', model: 'gpt-4o' },
      { id: 'synthesizer', name: 'Synthesizer', systemPrompt: DEFAULT_SYNTHESIZER_PROMPT, model: 'gemini-2.5-flash' },
    ],
  },
  {
    id: 'builtin-investment',
    builtin: true,
    name: '🛢 Commodities & Macro (Futures)',
    description: 'Institutional-grade: Data Extractor → Bull/Bear cases → Risk Manager → Synthesizer.',
    pipeline: [
      { id: 'data_extractor', name: 'Data Extractor', systemPrompt: SKILLS_DEFAULTS.find((s) => s.name === 'Data Extractor')?.systemPrompt || '', model: 'gpt-4o' },
      { id: 'investment_analyst', name: 'Investment Analyst', systemPrompt: SKILLS_DEFAULTS.find((s) => s.name === 'Investment Analyst')?.systemPrompt || '', model: 'gpt-4o' },
      { id: 'bull_case', name: 'Bull Case', systemPrompt: SKILLS_DEFAULTS.find((s) => s.name === 'Bull Case')?.systemPrompt || '', model: 'gpt-4o' },
      { id: 'bear_case', name: 'Bear Case', systemPrompt: SKILLS_DEFAULTS.find((s) => s.name === 'Bear Case')?.systemPrompt || '', model: 'gpt-4o' },
      { id: 'investment_risk_manager', name: 'Investment Risk Manager', systemPrompt: SKILLS_DEFAULTS.find((s) => s.name === 'Investment Risk Manager')?.systemPrompt || '', model: 'gpt-4o' },
      { id: 'synthesizer', name: 'Synthesizer', systemPrompt: DEFAULT_SYNTHESIZER_PROMPT, model: 'gpt-4o' },
    ],
  },
  // ── SECTOR: Investment Due Diligence ──
  {
    id: 'builtin-due-diligence',
    builtin: true,
    name: '📋 Investment Due Diligence',
    description: 'Data Extractor → Analyst → parallel Bull & Bear → Risk Manager → Synthesizer. Clean top-down DAG with parallel branches.',
    pipeline: [
      { id: 'dd_data_extractor', name: 'Data Extractor', parentIds: [], systemPrompt: DD_DATA_EXTRACTOR_PROMPT, model: 'gpt-4o', provider: 'openai', tools: [], jsonSchema: DD_METRICS_SCHEMA },
      { id: 'dd_analyst', name: 'Analyst', parentIds: ['dd_data_extractor'], systemPrompt: SKILLS_DEFAULTS.find((s) => s.name === 'Investment Analyst')?.systemPrompt || '', model: 'gpt-4o', provider: 'openai' },
      { id: 'dd_bull', name: 'Bull', parentIds: ['dd_analyst'], systemPrompt: WALL_STREET_BULL_PROMPT, model: 'gpt-4o', provider: 'openai' },
      { id: 'dd_bear', name: 'Bear', parentIds: ['dd_analyst'], systemPrompt: WALL_STREET_BEAR_PROMPT, model: 'gpt-4o', provider: 'openai' },
      { id: 'dd_risk_manager', name: 'Risk Manager', parentIds: ['dd_bull', 'dd_bear'], systemPrompt: SKILLS_DEFAULTS.find((s) => s.name === 'Investment Risk Manager')?.systemPrompt || '', model: 'gpt-4o', provider: 'openai' },
      { id: 'dd_synthesizer', name: 'Synthesizer', parentIds: ['dd_risk_manager'], systemPrompt: DD_SYNTHESIZER_PROMPT, model: 'gpt-4o', provider: 'openai' },
    ],
  },
  // ── CODING: Maximum depth ──
  {
    id: 'builtin-quality',
    builtin: true,
    name: '✅ Code Quality',
    description: 'Tests, docs, TypeScript strictness for maintainable long-term code.',
    pipeline: [
      { id: 'architect', name: 'Architect', systemPrompt: DEFAULT_ARCHITECT_PROMPT, model: 'gpt-4o' },
      { id: 'typescript_enforcer', name: 'TypeScript Enforcer', systemPrompt: SKILLS_DEFAULTS.find((s) => s.name === 'TypeScript Enforcer')?.systemPrompt || '', model: 'gpt-4o' },
      { id: 'test_writer', name: 'Test Writer', systemPrompt: SKILLS_DEFAULTS.find((s) => s.name === 'Test Writer')?.systemPrompt || '', model: 'gpt-4o' },
      { id: 'documentation_writer', name: 'Documentation Writer', systemPrompt: SKILLS_DEFAULTS.find((s) => s.name === 'Documentation Writer')?.systemPrompt || '', model: 'gpt-4o' },
      { id: 'skeptic', name: 'Skeptic', systemPrompt: DEFAULT_SKEPTIC_PROMPT, model: 'claude-haiku-4-5-20251001' },
      { id: 'synthesizer', name: 'Synthesizer', systemPrompt: DEFAULT_SYNTHESIZER_PROMPT, model: 'gemini-2.5-flash' },
    ],
  },
  {
    id: 'builtin-deep',
    builtin: true,
    name: '🔍 Deep Review',
    description: 'Maximum scrutiny: security, performance, types, tests, grader, skeptic. For critical features.',
    pipeline: [
      { id: 'architect', name: 'Architect', systemPrompt: DEFAULT_ARCHITECT_PROMPT, model: 'gpt-4o' },
      { id: 'security_auditor', name: 'Security Auditor', systemPrompt: SKILLS_DEFAULTS.find((s) => s.name === 'Security Auditor')?.systemPrompt || '', model: 'gpt-4o' },
      { id: 'performance_reviewer', name: 'Performance Reviewer', systemPrompt: SKILLS_DEFAULTS.find((s) => s.name === 'Performance Reviewer')?.systemPrompt || '', model: 'gpt-4o' },
      { id: 'typescript_enforcer', name: 'TypeScript Enforcer', systemPrompt: SKILLS_DEFAULTS.find((s) => s.name === 'TypeScript Enforcer')?.systemPrompt || '', model: 'gpt-4o' },
      { id: 'test_writer', name: 'Test Writer', systemPrompt: SKILLS_DEFAULTS.find((s) => s.name === 'Test Writer')?.systemPrompt || '', model: 'gpt-4o' },
      { id: 'master_grader', name: 'Judge: Master Grader', systemPrompt: SKILLS_DEFAULTS.find((s) => s.name === 'Judge: Master Grader')?.systemPrompt || '', model: 'gpt-4o' },
      { id: 'skeptic', name: 'Skeptic', systemPrompt: DEFAULT_SKEPTIC_PROMPT, model: 'claude-haiku-4-5-20251001' },
      { id: 'synthesizer', name: 'Synthesizer', systemPrompt: DEFAULT_SYNTHESIZER_PROMPT, model: 'gemini-2.5-flash' },
    ],
  },
];

const VISIBLE_TEMPLATES = BUILTIN_TEMPLATES.filter((t) =>
  ['builtin-rapid', 'builtin-security', 'builtin-backend', 'builtin-frontend', 'builtin-quality', 'builtin-deep'].includes(t.id)
);

// Set default pipeline/workflow to the Rapid Plan coding template
const EQUITIES_DEFAULT_TEMPLATE = BUILTIN_TEMPLATES.find((t) => t.id === 'builtin-rapid') || BUILTIN_TEMPLATES[0];
if (EQUITIES_DEFAULT_TEMPLATE?.pipeline?.length) {
  DEFAULT_PIPELINE = EQUITIES_DEFAULT_TEMPLATE.pipeline.map((p) => ({
    ...p,
    tools: Array.isArray(p.tools) ? p.tools : [],
    code: p.code ?? null,
    provider: p.provider || getProviderFromModel(p.model),
  }));

  const ordered = DEFAULT_PIPELINE.map((p) => ({
    id: p.id,
    name: p.name,
    description: p.description || '',
    provider: p.provider || getProviderFromModel(p.model),
    systemPrompt: p.systemPrompt || '',
    model: p.model || 'gpt-4o',
    parentIds: [],
  }));
  defaultWorkflow = recalculateParentIdsFromOrder(ordered);
}

function migratePipeline(stored) {
  try {
    const parsed = typeof stored === 'string' ? JSON.parse(stored) : stored;
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    const valid = parsed.every(
      (p) => p && typeof p.id === 'string' && typeof p.name === 'string' && typeof p.systemPrompt === 'string' && typeof p.model === 'string'
    );
    if (!valid) return null;
    return parsed.map((p) => ({
      ...p,
      tools: Array.isArray(p.tools) ? p.tools.filter((t) => typeof t === 'string') : [],
      code: typeof p.code === 'string' ? p.code : null,
      provider: typeof p.provider === 'string' ? p.provider : '',
    }));
  } catch {
    return null;
  }
}

function loadPipeline() {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.pipeline);
    if (!stored) return null;
    const migrated = migratePipeline(stored);
    if (!migrated) return null;
    const STALE_AGENT_NAMES = ['Data Extractor', 'Analyst', 'Bull', 'Bear', 'Risk Manager'];
    const isStale = migrated.some((a) => STALE_AGENT_NAMES.includes(a.name)) || migrated.length > 4;
    if (isStale) {
      localStorage.removeItem(STORAGE_KEYS.pipeline);
      return null;
    }
    return migrated;
  } catch {
    return null;
  }
}

function validatePipelineTemplate(t) {
  if (!t || typeof t !== 'object') return false;
  if (typeof t.id !== 'string' || !t.id.trim()) return false;
  if (typeof t.name !== 'string' || !t.name.trim()) return false;
  if (typeof t.createdAt !== 'number' || t.createdAt <= 0) return false;
  if (!Array.isArray(t.pipeline) || t.pipeline.length === 0) return false;
  const validPipeline = migratePipeline(t.pipeline);
  return !!validPipeline;
}

function loadPipelineTemplates() {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.pipelineTemplates);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(validatePipelineTemplate);
  } catch {
    return [];
  }
}

function isPipelineModifiedFromDefault(p) {
  if (!Array.isArray(p) || p.length !== DEFAULT_PIPELINE.length) return true;
  return p.some(
    (agent, i) =>
      agent.id !== DEFAULT_PIPELINE[i]?.id ||
      agent.name !== DEFAULT_PIPELINE[i]?.name ||
      agent.systemPrompt !== DEFAULT_PIPELINE[i]?.systemPrompt ||
      agent.model !== DEFAULT_PIPELINE[i]?.model
  );
}

const INTENSITY_LABELS = {
  1: 'Light review',
  2: 'Constructive',
  3: 'Balanced',
  4: 'Critical',
  5: 'Ruthless',
};

const MODEL_OPTIONS = {
  openai: [{ value: 'gpt-4o', label: 'GPT-4o' }, { value: 'gpt-4o-mini', label: 'GPT-4o Mini' }],
  anthropic: [{ value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' }, { value: 'claude-sonnet-4-5-20251001', label: 'Claude Sonnet 4.5' }],
  gemini: [{ value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' }, { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' }],
  local: [{ value: 'local', label: 'Local (Ollama)' }],
  search: [{ value: 'web-search', label: 'Deep Web Research' }],
};

function getModelLabel(value, role, hasO, hasA, hasG, hasLocal) {
  const opts = getModelsForRole(role, hasO, hasA, hasG, hasLocal);
  const found = opts.find((o) => o.value === value);
  return found ? found.label : value;
}

function getModelsForProvider(provider) {
  const p = (provider || 'openai').toLowerCase();
  return MODEL_OPTIONS[p] || MODEL_OPTIONS.openai;
}

function isCustomAgent(agent) {
  const base = (agent.id || '').split('-')[0];
  return !['architect', 'skeptic', 'synthesizer'].includes(base);
}

function stripMarkdownJson(text) {
  if (!text || typeof text !== 'string') return text || '';
  return text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
}

function extractResultJson(text) {
  try {
    const clean = stripMarkdownJson(text);
    const objMatch = clean?.match(/\{[\s\S]*\}/);
    if (objMatch) {
      const parsed = JSON.parse(objMatch[0]);
      if (parsed && parsed.verdict) return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function stripResultJson(text) {
  if (!text) return text;
  return text
    .replace(/```json\n[\s\S]*?\n```/g, '')
    .replace(/json\s*\{[\s\S]*\}\s*/g, '')
    .trim();
}

function renderMetricsTable(output) {
  try {
    const parsed = typeof output === 'string' ? JSON.parse(output) : output;
    if (!parsed || !Array.isArray(parsed.metrics)) return null;
    return parsed.metrics.map((m, i) => ({
      key: i,
      metric: m.metric_name || '—',
      value: m.status === 'MISSING' || String(m.source_url || '').toUpperCase().startsWith('GAP')
        ? 'GAP — data not available'
        : (m.value || '—'),
      url: m.status === 'MISSING' || String(m.source_url || '').toUpperCase().startsWith('GAP')
        ? null
        : m.source_url,
      status: m.status,
    }));
  } catch {
    return null;
  }
}

function cleanOutputDisplay(text) {
  if (!text) return text;
  return text
    .replace(/\/\/\s*I have an architectural plan[\s\S]*?(?=###\s|\*\*|$)/gm, '')
    .replace(/═+\s*SECTION \d+:?\s*═*/g, '')
    .replace(/════+/g, '')
    .replace(/## OUTPUT 2(?:\s*\(CONDENSED\))?[\s\S]*?(?=## PLAN\.md|$)/g, '')
    .replace(/^#+\s*CORE\s+IMPLEMENTATION\s*/im, '')
    .replace(/\[custom_\d+\]/gi, '')
    .replace(/\[EXTRACTED:.*?\]/gi, '')
    .replace(/\[INFERRED[^\]]*\]/gi, '')
    .trim();
}

function parseAdversarialOutput(results) {
  try {
    const bullClaimsResult = results?.find((r) => r.name === 'Bull: Claims');
    const bearRefuteResult = results?.find((r) => r.name === 'Bear: Refutations');
    const bullDefenseResult = results?.find((r) => r.name === 'Bull: Defense');
    const judgeResult = results?.find((r) => r.name === 'Judge');

    if (!bullClaimsResult || !judgeResult) return null;

    const parseJSON = (text) => {
      if (!text) return null;
      try {
        const clean = stripMarkdownJson(text);
        const match = clean.match(/\[[\s\S]*\]|\{[\s\S]*\}/);
        if (match) return JSON.parse(match[0]);
        return null;
      } catch { return null; }
    };

    const claims = parseJSON(bullClaimsResult.output);
    const refutations = parseJSON(bearRefuteResult?.output);
    const defenses = parseJSON(bullDefenseResult?.output);
    const judgment = parseJSON(judgeResult.output);

    if (!claims || !judgment) return null;

    return { claims, refutations, defenses, judgment };
  } catch { return null; }
}

function isAdversarialPipeline(pipeline) {
  return pipeline?.some((p) => p.name === 'Bull: Claims') &&
    pipeline?.some((p) => p.name === 'Judge');
}

const INVESTMENT_AGENT_NAMES = ['Data Extractor', 'Investment Analyst', 'Bull Case', 'Bear Case', 'Investment Risk Manager', 'Due Diligence'];
function isInvestmentPipeline(pipeline) {
  return pipeline?.some((p) =>
    INVESTMENT_AGENT_NAMES.some((name) => p.name?.toLowerCase().includes(name.toLowerCase()))
  );
}

function extractAdversarialScore(results) {
  try {
    const judgeResult = results?.find(r => r.name === 'Judge');
    if (!judgeResult?.output) return 0;
    const clean = stripMarkdownJson(judgeResult.output);
    const match = clean.match(/\{[\s\S]*\}/);
    if (!match) return 0;
    const parsed = JSON.parse(match[0]);
    if (typeof parsed.confidence === 'number') {
      const conf = parsed.confidence;
      return conf <= 1 ? Math.round(conf * 100) : Math.round(Math.min(100, Math.max(0, conf)));
    }
    if (Array.isArray(parsed.claim_rulings) && parsed.claim_rulings.length > 0) {
      const wins = parsed.claim_rulings.filter(r => r.winner === 'BULL').length;
      return Math.round((wins / parsed.claim_rulings.length) * 100);
    }
    return 0;
  } catch (e) {
    console.warn('Failed to parse adversarial judge score:', e);
    return 0;
  }
}

function summarizeAdversarialAgentOutput(agentName, output) {
  try {
    const clean = stripMarkdownJson(output);
    const parsed = JSON.parse(clean);
    if (agentName === 'Bull: Claims' && Array.isArray(parsed)) {
      return `Made ${parsed.length} claims. Strongest: "${parsed[0]?.claim?.slice(0, 80)}..."`;
    }
    if (agentName === 'Bear: Refutations' && Array.isArray(parsed)) {
      const killShots = parsed.filter((r) => r.kill_shot).length;
      return `Refuted ${parsed.length} claims. Kill shots: ${killShots}. Strongest: "${parsed[0]?.refutation?.slice(0, 80)}..."`;
    }
    if (agentName === 'Bull: Defense' && Array.isArray(parsed)) {
      const conceded = parsed.filter((d) => d.outcome === 'CONCEDE').length;
      const partial = parsed.filter((d) => d.outcome === 'PARTIAL_CONCEDE').length;
      const defended = parsed.filter((d) => d.outcome === 'DEFEND').length;
      return `Conceded ${conceded}, Partial ${partial}, Defended ${defended}. See scorecard for details.`;
    }
    return output;
  } catch {
    return output;
  }
}

function exportToPDF(result, idea, pipeline) {
  const title = idea?.slice(0, 80) || 'Viqo Pipeline Output';
  const date = new Date().toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric'
  });
  const agents = pipeline.map(p => p.name).join(' → ');
  const adversarialData = parseAdversarialOutput(result?.results || []);
  const judgeResult = (result?.results || []).find((r) => r.name === 'Judge');
  const confidenceScore = adversarialData?.judgment?.confidence ?? extractAdversarialScore(result?.results || []);
  const verdict = adversarialData?.judgment?.verdict || 'N/A';
  const verdictClass = verdict === 'INVEST'
    ? 'verdict-invest'
    : verdict === 'PASS'
      ? 'verdict-pass'
      : verdict === 'WATCH'
        ? 'verdict-watch'
        : '';

  const adversarialBreakdownHtml = adversarialData
    ? (adversarialData.judgment?.claim_rulings || []).map((ruling) => {
      const claim = adversarialData.claims?.find((c) => c.claim_id === ruling.claim_id);
      const refutation = adversarialData.refutations?.find((r) => r.claim_id === ruling.claim_id);
      const defense = adversarialData.defenses?.find((d) => d.claim_id === ruling.claim_id);
      return `
        <div class="claim-card">
          <h3>${ruling.claim_id || ''} — ${ruling.winner || 'DRAW'}</h3>
          ${claim?.claim ? `<p><strong>Claim:</strong> ${claim.claim}</p>` : ''}
          ${claim?.evidence ? `<p><strong>Evidence:</strong> ${claim.evidence}</p>` : ''}
          ${refutation?.refutation ? `<p><strong>Bear Refutation:</strong> ${refutation.refutation}</p>` : ''}
          ${refutation?.counter_evidence ? `<p><strong>Counter-Evidence:</strong> ${refutation.counter_evidence}</p>` : ''}
          ${defense?.response ? `<p><strong>Bull Defense (${defense.outcome || 'N/A'}):</strong> ${defense.response}</p>` : ''}
          ${ruling?.ruling ? `<p><strong>Judge Ruling:</strong> ${ruling.ruling}</p>` : ''}
        </div>
      `;
    }).join('')
    : '';

  const content = `
    <html>
    <head>
      <title>${title}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-size: 13px;
          line-height: 1.6;
          color: #1a1a1a;
          padding: 40px;
          max-width: 800px;
          margin: 0 auto;
        }
        .header {
          border-bottom: 2px solid #000;
          padding-bottom: 16px;
          margin-bottom: 24px;
        }
        .header-brand {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #666;
          margin-bottom: 8px;
        }
        .header-title {
          font-size: 18px;
          font-weight: 700;
          color: #000;
          margin-bottom: 6px;
          line-height: 1.3;
        }
        .header-meta {
          font-size: 11px;
          color: #666;
          display: flex;
          gap: 16px;
        }
        .verdict-card {
          background: #f8f8f8;
          border: 1px solid #ddd;
          border-radius: 6px;
          padding: 16px;
          margin-bottom: 24px;
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          align-items: flex-start;
        }
        .verdict-label {
          font-weight: 700;
          font-size: 15px;
        }
        .verdict-invest { color: #1a7f37; }
        .verdict-pass { color: #cf222e; }
        .verdict-watch { color: #9a6700; }
        .verdict-meta { font-size: 11px; color: #444; }
        .section { margin-bottom: 20px; }
        .claim-card {
          border: 1px solid #e5e5e5;
          border-radius: 6px;
          padding: 12px;
          margin-bottom: 12px;
          page-break-inside: avoid;
        }
        h1, h2, h3 { color: #000; margin-bottom: 8px; }
        h2 { font-size: 14px; border-bottom: 1px solid #eee; padding-bottom: 4px; margin-bottom: 10px; }
        h3 { font-size: 13px; }
        p { margin-bottom: 8px; color: #333; }
        ul, ol { padding-left: 20px; margin-bottom: 8px; }
        li { margin-bottom: 4px; color: #333; }
        .footer {
          margin-top: 40px;
          padding-top: 12px;
          border-top: 1px solid #eee;
          font-size: 10px;
          color: #999;
          display: flex;
          justify-content: space-between;
        }
        code { background: #f0f0f0; padding: 1px 4px; border-radius: 3px; font-size: 11px; }
        pre { background: #f0f0f0; padding: 12px; border-radius: 4px; overflow-x: auto; font-size: 11px; margin-bottom: 8px; }
        @media print {
          body { padding: 20px; }
          .no-print, button, summary, details > summary, .interactive, .adv-exec-expand-btn { display: none !important; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="header-brand">Viqo — Pipeline Output</div>
        <div class="header-title">${title}</div>
        <div class="header-meta">
          <span>Generated: ${date}</span>
          <span>Pipeline: ${agents}</span>
        </div>
      </div>
      ${adversarialData ? `
        <div class="verdict-card">
          <div class="verdict-label ${verdictClass}">Judge Verdict: ${verdict}</div>
          <div class="verdict-meta">Confidence: ${confidenceScore}/100</div>
          ${adversarialData.judgment?.action ? `<div class="verdict-meta"><strong>Action:</strong> ${adversarialData.judgment.action}</div>` : ''}
        </div>
        ${judgeResult?.output ? `<div class="section"><h2>Judge Output</h2>${markdownToHtml(stripResultJson(judgeResult.output))}</div>` : ''}
        <div class="section">
          <h2>Claim-by-Claim Breakdown</h2>
          ${adversarialBreakdownHtml || '<p>No claim breakdown available.</p>'}
        </div>
      ` : `
        ${result.cursor_prompt ? `<div class="section">${markdownToHtml(stripResultJson(result.cursor_prompt))}</div>` : ''}
        ${result.plan_md ? `<div class="section"><h2>Plan</h2>${markdownToHtml(stripResultJson(result.plan_md))}</div>` : ''}
      `}
      <div class="footer">
        <span>Viqo — agentos.app</span>
        <span>${date}</span>
      </div>
    </body>
    </html>
  `;

  const printWindow = window.open('', '_blank');
  printWindow.document.write(content);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 500);
}

function markdownToHtml(text) {
  if (!text) return '';
  return text
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/^## (.*$)/gim, '<h2>$1</h2>')
    .replace(/^# (.*$)/gim, '<h1>$1</h1>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`(.*?)`/g, '<code>$1</code>')
    .replace(/^- (.*$)/gim, '<li>$1</li>')
    .replace(/^❌ (.*$)/gim, '<li>❌ $1</li>')
    .replace(/^✅ (.*$)/gim, '<li>✅ $1</li>')
    .replace(/(<li>.*<\/li>)/gims, '<ul>$1</ul>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/^(?!<[hul])/gim, '<p>')
    .replace(/(?<![>])\n/g, '<br>');
}

function buildReasoningNarrative(results, pipeline) {
  if (!results || results.length === 0) return [];

  const deduped = (results || []).filter((r, i, arr) =>
    arr.findIndex(x => x.id === r.id && x.name === r.name) === i
  );

  // Helper to create human-readable preview from JSON agent outputs
  const createReadablePreview = (agentName, output) => {
    // Strip markdown fences and leading "json" prefix
    const cleaned = (output || '')
      .replace(/```json\s*/i, '')
      .replace(/```/g, '')
      .replace(/^json\s+/i, '')
      .trim();

    // Try to parse as JSON array
    try {
      const match = cleaned.match(/\[[\s\S]*\]/);
      if (!match) throw new Error('No array');
      const parsed = JSON.parse(match[0]);
      if (!Array.isArray(parsed)) throw new Error('Not array');

      // Bull: Claims
      if (agentName === 'Bull: Claims') {
        const strongest = parsed[0]?.claim?.slice(0, 80) || parsed[0]?.evidence?.slice(0, 80) || '';
        return `Made ${parsed.length} claims. Strongest: "${strongest}..."`;
      }

      // Bear: Refutations
      if (agentName === 'Bear: Refutations') {
        const killShots = parsed.filter((r) => r.kill_shot).length;
        const strongest = parsed[0]?.refutation?.slice(0, 80) || '';
        return `Refuted ${parsed.length} claims. Kill shots: ${killShots}. Strongest: "${strongest}..."`;
      }

      // Bull: Defense
      if (agentName === 'Bull: Defense') {
        const conceded = parsed.filter((d) => d.outcome === 'CONCEDE').length;
        const partial = parsed.filter((d) => d.outcome === 'PARTIAL_CONCEDE').length;
        const defended = parsed.filter((d) => d.outcome === 'DEFEND').length;
        return `Defended ${defended}, Partial ${partial}, Conceded ${conceded} of ${parsed.length} claims.`;
      }

      // Judge
      if (agentName === 'Judge') {
        const verdict = parsed.verdict || parsed[0]?.verdict;
        const action = parsed.action || parsed[0]?.action || '';
        if (verdict) {
          return `Ruled: ${verdict}. ${action.slice(0, 100)}`;
        }
      }
    } catch {
      // Try parsing as JSON object (for Judge)
      try {
        const objMatch = cleaned.match(/\{[\s\S]*\}/);
        if (objMatch) {
          const parsed = JSON.parse(objMatch[0]);
          if (agentName === 'Judge' && parsed.verdict) {
            return `Ruled: ${parsed.verdict}. ${(parsed.action || '').slice(0, 100)}`;
          }
        }
      } catch {
        // Fall through to text preview
      }
    }

    // Fallback: clean text preview, but never show raw JSON by default
    let previewText = cleaned
      .replace(/#{1,6}\s/g, '')
      .replace(/\*\*/g, '')
      .trim();

    const arrMatch = previewText.match(/\[[\s\S]*?\]/);
    if (arrMatch) previewText = previewText.replace(arrMatch[0], '').trim();
    const objMatch = previewText.match(/\{[\s\S]*?\}/);
    if (objMatch) previewText = previewText.replace(objMatch[0], '').trim();

    if (previewText) return previewText.slice(0, 200);

    // Last resort: generic summary
    return 'Processed and responded.';
  };

  return deduped
    .filter((r) => r.output && r.output.trim() && r.output !== 'No output')
    .map((result, index) => {
      const name =
        result.name && result.name !== 'Custom' && result.name !== 'custom'
          ? result.name
          : pipeline.find((p) => p.id === result.id)?.name || result.id || 'Agent';

      const preview = createReadablePreview(name, result.output);

      const isFirst = index === 0;
      const isSynthesizer = name.toLowerCase().includes('synthesiz');
      const verb = isFirst
        ? 'analyzed the request'
        : isSynthesizer
          ? 'synthesized the final output'
          : 'reviewed and responded';
      return { name, verb, preview, isSynthesizer, isFirst, id: result.id };
    });
}

function AdversarialScorecard({ data, result, idea, pipeline }) {
  const [proseMode, setProseMode] = useState(true);
  const [showFullScorecard, setShowFullScorecard] = useState(false);
  const { claims, refutations, defenses, judgment } = data;
  if (!judgment) return null;

  const verdictColor = {
    INVEST: '#3fb950',
    PASS: '#f85149',
    WATCH: '#d29922',
  }[judgment.verdict] || '#666';

  const verdictIcon = {
    INVEST: '✅',
    PASS: '❌',
    WATCH: '👁️',
  }[judgment.verdict] || '❓';

  return (
    <div className="adversarial-scorecard">
      {/* Compressed executive summary */}
      <div className="adv-executive-summary">
        <div className="adv-exec-verdict-row">
          <span className="adv-exec-verdict" style={{ color: verdictColor }}>
            {verdictIcon} {judgment.verdict}
          </span>
          <span className="adv-exec-confidence">
            {judgment.confidence}/100
          </span>
        </div>
        <p className="adv-exec-action">{judgment.action}</p>
        <div className="adv-exec-why">
          <span className="adv-exec-why-label">Why:</span>
          <ul className="adv-exec-why-list">
            {(judgment.claim_rulings || [])
              .filter(r => r.winner === 'BEAR' || defenses?.find(d => d.claim_id === r.claim_id && d.outcome === 'CONCEDE'))
              .slice(0, 3)
              .map(r => (
                <li key={r.claim_id} className="adv-exec-why-item">
                  {r.ruling}
                </li>
              ))
            }
          </ul>
        </div>
        {judgment.watch_triggers?.length > 0 && (
          <div className="adv-exec-triggers">
            <span className="adv-exec-triggers-label">Changes if:</span>
            <span className="adv-exec-trigger-text">{judgment.watch_triggers[0]}</span>
          </div>
        )}
        <button
          className="adv-exec-expand-btn"
          onClick={() => setShowFullScorecard(s => !s)}
        >
          {showFullScorecard ? '▲ Hide full analysis' : '▼ Show claim-by-claim analysis'}
        </button>
      </div>

      {showFullScorecard && (
      <>
      <div className="adv-verdict-header" style={{ borderLeftColor: verdictColor }}>
        <div className="adv-verdict-main">
          <span className="adv-verdict-icon">{verdictIcon}</span>
          <span className="adv-verdict-label" style={{ color: verdictColor }}>{judgment.verdict}</span>
          <span className="adv-verdict-confidence">Confidence: {judgment.confidence}/100</span>
        </div>
        <p className="adv-verdict-basis">{judgment.verdict_basis}</p>
        {judgment.action && (
          <div className="adv-action">
            <span className="adv-action-label">Action:</span> {judgment.action}
          </div>
        )}
        <div className="adv-score-pills">
          <span className="adv-pill adv-pill--bull">🐂 Bull wins: {judgment.debate_score?.bull_wins || 0}</span>
          <span className="adv-pill adv-pill--bear">🐻 Bear wins: {judgment.debate_score?.bear_wins || 0}</span>
          <span className="adv-pill adv-pill--concede">🏳️ Conceded: {judgment.debate_score?.bull_concessions || 0}</span>
        </div>
        <button
          type="button"
          className="copy-btn export-pdf-btn adv-export-btn"
          onClick={() => exportToPDF(result, idea, pipeline)}
        >
          📄 Export Thesis
        </button>
      </div>

      <div className="adv-claims-section">
        <div className="adv-claims-header-row">
          <h3 className="adv-section-title">Claim-by-claim rulings</h3>
          <button
            className="adv-mode-toggle"
            onClick={() => setProseMode((p) => !p)}
          >
            {proseMode ? '{ } Structured' : '¶ Prose'}
          </button>
        </div>
        {(judgment.claim_rulings || []).map((ruling, i) => {
          const claim = claims?.find ? claims.find((c) => c.claim_id === ruling.claim_id) : null;
          const refutation = refutations?.find ? refutations.find((r) => r.claim_id === ruling.claim_id) : null;
          const defense = defenses?.find ? defenses.find((d) => d.claim_id === ruling.claim_id) : null;
          const winnerColor = ruling.winner === 'BULL' ? '#3fb950' : ruling.winner === 'BEAR' ? '#f85149' : '#d29922';

          return (
            <div key={ruling.claim_id} className="adv-claim-card">
              <div className="adv-claim-header">
                <span className="adv-claim-id">{ruling.claim_id}</span>
                <span className="adv-claim-winner" style={{ color: winnerColor }}>
                  {ruling.winner === 'BULL' ? '🐂 Bull wins' : ruling.winner === 'BEAR' ? '🐻 Bear wins' : '🤝 Draw'}
                </span>
                <span className="adv-claim-impact">{ruling.evidence_quality}</span>
              </div>
              {claim && (proseMode ? (
                <p className="adv-claim-text">"{claim.claim}"</p>
              ) : (
                <pre className="adv-claim-json">{JSON.stringify(claim, null, 2)}</pre>
              ))}
              <p className="adv-ruling-text">{ruling.ruling}</p>
              {refutation && (
                <div className="adv-refutation">
                  <span className="adv-refutation-label">🐻 Bear:</span> {refutation.refutation}
                  {refutation.kill_shot && <span className="adv-kill-shot">💀 Kill shot</span>}
                </div>
              )}
              {defense && (
                <div className={`adv-defense adv-defense--${defense.outcome.toLowerCase()}`}>
                  <span className="adv-defense-label">
                    {defense.outcome === 'CONCEDE' ? '🏳️ Conceded' : defense.outcome === 'DEFEND' ? '🛡️ Defended' : '⚖️ Partial'}:
                  </span> {defense.response}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {judgment.watch_triggers?.length > 0 && (
        <div className="adv-triggers">
          <h3 className="adv-section-title">Verdict change triggers</h3>
          {judgment.watch_triggers.map((trigger, i) => (
            <div key={i} className="adv-trigger-item">⚡ {trigger}</div>
          ))}
        </div>
      )}
      </>
      )}
    </div>
  );
}

function SortablePipelineNode({
  node,
  displayName,
  isActive,
  isSelectorOpen,
  models,
  modelLabel,
  onToggleSelector,
  onEdit,
  onDelete,
  onSelectModel,
  loading,
  canDelete,
  nodeStatus,
  isCustom,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: node.id,
    disabled: false,
  });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div ref={setNodeRef} style={style} className={`pipeline-node-wrap ${isDragging ? 'pipeline-node-dragging' : ''}`}>
      <div className="pipeline-node-container">
        <div className="pipeline-node-inner">
          <button
            type="button"
            className="pipeline-node-drag-handle"
            aria-label="Drag to reorder"
            {...attributes}
            {...listeners}
            disabled={loading}
          >
            ⋮⋮
          </button>
          <button
            type="button"
            className={`pipeline-node ${isActive ? 'pipeline-node-active' : ''} ${nodeStatus?.status === 'completed' ? 'pipeline-node-completed' : ''} ${nodeStatus?.status === 'error' ? 'pipeline-node-error' : ''} ${isCustom ? 'pipeline-node--custom' : ''}`}
            onClick={() => onToggleSelector(node.id)}
            disabled={loading}
            aria-expanded={isSelectorOpen}
            aria-haspopup="listbox"
            aria-label={`${displayName || node.name} model: ${modelLabel}. Click to change.`}
          >
            <div className="pipeline-node-label-row">
              <div
                className="pipeline-node-status-dot"
                data-status={
                  nodeStatus?.status === 'error' ? 'error' :
                  nodeStatus?.status === 'completed' ? 'completed' :
                  nodeStatus?.status === 'active' ? 'active' :
                  nodeStatus?.status === 'retrying' ? 'retrying' :
                  'idle'
                }
                aria-hidden="true"
              />
              {isCustom && <span className="pipeline-node-custom-badge" aria-label="Custom agent">◆</span>}
              {node.provider === 'search' && (
                <span className="pipeline-node-custom-badge" aria-label="Web search provider" title="Deep web research">🔍</span>
              )}
              {node.provider === 'local' && (
                <span className="pipeline-node-custom-badge" aria-label="Local LLM provider" title="Ollama (local)">🏠</span>
              )}
              {Array.isArray(node.tools) && node.tools.includes('web_search') && node.provider !== 'search' && (
                <span className="pipeline-node-custom-badge" aria-label="Web search enabled">🌐</span>
              )}
              {node.code && node.code.trim() && (
                <span className="pipeline-node-custom-badge" aria-label="Code agent" title="Programmable code agent">⚡</span>
              )}
              <span className="pipeline-node-label">{displayName || node.name}</span>
            </div>
            {node.description && <span className="pipeline-node-description">{node.description}</span>}
            <span className="pipeline-node-model">{modelLabel}</span>
            {nodeStatus?.status === 'completed' && <span className="pipeline-node-check-icon" aria-label="Completed">✓</span>}
            {nodeStatus?.status === 'error' && <span className="pipeline-node-error-icon" aria-label="Error">✕</span>}
          </button>
          <button
            type="button"
            className="pipeline-node-edit"
            onClick={(e) => { e.stopPropagation(); onEdit(node); }}
            disabled={loading}
            aria-label={`Edit ${displayName || node.name}`}
          >
            <PencilIcon />
          </button>
          <button
            type="button"
            className="pipeline-node-delete"
            onClick={(e) => { e.stopPropagation(); onDelete(node); }}
            disabled={loading || !canDelete}
            aria-label={`Delete ${displayName || node.name}`}
          >
            <XIcon />
          </button>
        </div>
        {isSelectorOpen && (
          <div className="pipeline-selector-dropdown" role="listbox">
            {models.map((o) => (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={node.model === o.value}
                className="pipeline-option"
                onClick={() => onSelectModel(node.id, o.value)}
              >
                {o.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const PROVIDER_OPTIONS = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'gemini', label: 'Gemini' },
  { value: 'local', label: 'Local (Ollama)' },
  { value: 'search', label: 'Deep Web Research' },
];

const GUARDRAIL_TEMPLATES = [
  {
    id: 'guardrail-fact-checker',
    name: 'Fact Checker',
    systemPrompt: 'You are a ruthless fact checker. Critique every claim, cite sources if possible, flag hallucinations.',
    provider: 'anthropic',
    model: 'claude-3-haiku',
  },
  {
    id: 'guardrail-security',
    name: 'Security Guardrail',
    systemPrompt: 'You are a brutal security expert. Find injection, auth, privacy, and supply-chain risks in every plan.',
    provider: 'anthropic',
    model: 'claude-3-haiku',
  },
  {
    id: 'guardrail-consistency',
    name: 'Consistency Checker',
    systemPrompt: 'You are a consistency enforcer. Ensure the plan follows previous steps, existing patterns, and does not contradict itself.',
    provider: 'anthropic',
    model: 'claude-3-haiku',
  },
];

const CODE_AGENT_PLACEHOLDER = `async function run(context) {
  // Example: fetch live data
  const res = await context.fetch('https://api.example.com/data');
  const data = await res.json();
  return {
    output: JSON.stringify(data),
    skipLLM: false  // false = inject into LLM context
                    // true = skip LLM, use this as agent output
  };
}`;

function AgentModal({ mode, initialNode, afterIndex, onSave, onClose, hasO, hasA, hasG, hasLocal }) {
  const [name, setName] = useState(initialNode?.name ?? '');
  const [systemPrompt, setSystemPrompt] = useState(initialNode?.systemPrompt ?? '');
  const [provider, setProvider] = useState(initialNode?.provider || getProviderFromModel(initialNode?.model) || 'openai');
  const [model, setModel] = useState(initialNode?.model ?? 'gpt-4o');
  const [webSearchEnabled, setWebSearchEnabled] = useState(Array.isArray(initialNode?.tools) && initialNode.tools.includes('web_search'));
  const [code, setCode] = useState(initialNode?.code ?? '');
  const [fredApiKey, setFredApiKey] = useState(initialNode?.fredApiKey ?? '');
  const [searchApiKey, setSearchApiKey] = useState(initialNode?.searchApiKey ?? '');
  const [activeTab, setActiveTab] = useState('prompt');
  const modelsForProvider = getModelsForProvider(provider);
  const canUseWebSearch = model === 'gpt-4o' || model === 'gpt-4o-mini' || provider === 'anthropic';

  useEffect(() => {
    if (initialNode) {
      setName(initialNode.name);
      setSystemPrompt(initialNode.systemPrompt);
      const prov = initialNode.provider || getProviderFromModel(initialNode.model) || 'openai';
      setProvider(prov);
      const opts = getModelsForProvider(prov);
      const m = initialNode.model ?? opts[0]?.value ?? 'gpt-4o';
      setModel(opts.some((o) => o.value === m) ? m : (opts[0]?.value ?? 'gpt-4o'));
      setWebSearchEnabled(Array.isArray(initialNode?.tools) && initialNode.tools.includes('web_search'));
      setCode(initialNode?.code ?? '');
      setFredApiKey(initialNode?.fredApiKey ?? '');
      setSearchApiKey(initialNode?.searchApiKey ?? '');
      setActiveTab(initialNode?.code?.trim() ? 'code' : 'prompt');
    } else {
      setName('');
      setSystemPrompt('');
      setProvider('openai');
      setModel(getModelsForProvider('openai')[0]?.value ?? 'gpt-4o');
      setWebSearchEnabled(false);
      setCode('');
      setFredApiKey('');
      setSearchApiKey('');
      setActiveTab('prompt');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialNode, mode]);

  useEffect(() => {
    const opts = getModelsForProvider(provider);
    if (!opts.some((o) => o.value === model)) {
      setModel(opts[0]?.value ?? 'gpt-4o');
    }
  }, [provider, model]);

  useEffect(() => {
    if (!canUseWebSearch && webSearchEnabled) {
      setWebSearchEnabled(false);
    }
  }, [canUseWebSearch, webSearchEnabled]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({
      name: name.trim(),
      systemPrompt: systemPrompt.trim() || 'You are a helpful assistant.',
      provider,
      model,
      tools: canUseWebSearch && webSearchEnabled ? ['web_search'] : [],
      code: code.trim() || null,
      fredApiKey: fredApiKey.trim() || null,
      searchApiKey: searchApiKey.trim() || null,
    }, afterIndex, initialNode?.id);
    onClose();
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="agent-modal-overlay" style={MODAL_PORTAL_STYLE} onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="agent-modal-title">
      <div className="agent-modal" onClick={(e) => e.stopPropagation()}>
        <h3 id="agent-modal-title">{mode === 'edit' ? 'Edit agent' : 'Add custom agent'}</h3>
        <form onSubmit={handleSubmit}>
          <label htmlFor="agent-name">Name / Role</label>
          <input
            id="agent-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Security Reviewer"
            required
          />

          {/* Tab switcher for Prompt / Code */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }} role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'prompt'}
              style={{
                padding: '6px 16px',
                border: '1px solid #444',
                borderRadius: '4px',
                background: activeTab === 'prompt' ? '#2d7a4f' : '#1a1a2e',
                color: '#fff',
                cursor: 'pointer',
                fontWeight: activeTab === 'prompt' ? '600' : '400',
              }}
              onClick={() => setActiveTab('prompt')}
            >
              Prompt
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'code'}
              style={{
                padding: '6px 16px',
                border: '1px solid #444',
                borderRadius: '4px',
                background: activeTab === 'code' ? '#2d7a4f' : '#1a1a2e',
                color: '#fff',
                cursor: 'pointer',
                fontWeight: activeTab === 'code' ? '600' : '400',
              }}
              onClick={() => setActiveTab('code')}
            >
              Code {code.trim() ? '⚡' : ''}
            </button>
          </div>

          {activeTab === 'prompt' && (
            <>
              <label htmlFor="agent-prompt">System prompt</label>
              <textarea
                id="agent-prompt"
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                placeholder="Define the agent's role and behavior..."
                rows={6}
              />
            </>
          )}

          {activeTab === 'code' && (
            <>
              <p className="helper" style={{ marginBottom: '8px' }}>
                Write an async function named <code>run(context)</code>.<br />
                Available: <code>context.idea</code>, <code>context.previousOutputs</code>, <code>context.fetch</code><br />
                Return: <code>{'{ output: string, skipLLM: boolean }'}</code>
              </p>
              <textarea
                id="agent-code"
                className="agent-code-textarea"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={CODE_AGENT_PLACEHOLDER}
                rows={10}
                style={{ fontFamily: 'monospace', fontSize: '13px' }}
              />
            </>
          )}

          <label htmlFor="agent-provider">Provider</label>
          <select id="agent-provider" value={provider} onChange={(e) => setProvider(e.target.value)}>
            {PROVIDER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <label htmlFor="agent-model">Model</label>
          <select id="agent-model" value={model} onChange={(e) => setModel(e.target.value)}>
            {modelsForProvider.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {canUseWebSearch && (
            <div>
              <label htmlFor="agent-web-search">
                <input
                  id="agent-web-search"
                  type="checkbox"
                  checked={webSearchEnabled}
                  onChange={(e) => setWebSearchEnabled(e.target.checked)}
                />
                {' '}🌐 Web search
              </label>
              <p className="helper">Agent can search the internet for current information</p>
            </div>
          )}
          <details style={{ marginTop: '12px' }}>
            <summary style={{ cursor: 'pointer', color: '#8b949e', fontSize: '0.85rem', fontWeight: 500, userSelect: 'none', padding: '6px 0' }}>
              🔌 Tool &amp; API Configurations
            </summary>
            <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div>
                <label htmlFor="agent-fred-key" style={{ fontSize: '0.82rem', color: '#8b949e', marginBottom: '4px', display: 'block' }}>
                  FRED API Key <span style={{ fontWeight: 400 }}>(Macro Data — DFF, CPI, GDP, etc.)</span>
                </label>
                <input
                  id="agent-fred-key"
                  type="password"
                  value={fredApiKey}
                  onChange={(e) => setFredApiKey(e.target.value)}
                  placeholder="Paste your FRED API key…"
                  style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.82rem' }}
                  autoComplete="off"
                />
              </div>
              <div>
                <label htmlFor="agent-search-key" style={{ fontSize: '0.82rem', color: '#8b949e', marginBottom: '4px', display: 'block' }}>
                  Search API Key <span style={{ fontWeight: 400 }}>(Tavily — enables web_search tool)</span>
                </label>
                <input
                  id="agent-search-key"
                  type="password"
                  value={searchApiKey}
                  onChange={(e) => setSearchApiKey(e.target.value)}
                  placeholder="Paste your Tavily API key…"
                  style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.82rem' }}
                  autoComplete="off"
                />
              </div>
              <p style={{ fontSize: '0.75rem', color: '#484f58', margin: 0 }}>
                Keys are sent only when this agent runs. They are never stored in the pipeline config or shared with other agents.
              </p>
            </div>
          </details>
          <div className="agent-modal-actions">
            <button type="button" className="copy-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="run-button">
              {mode === 'edit' ? 'Save' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

function SaveTemplateModal({ onSave, onClose }) {
  const [name, setName] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    onSave(trimmed);
  };

  return createPortal(
    <div className="agent-modal-overlay" style={MODAL_PORTAL_STYLE} onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="save-template-title">
      <div className="agent-modal pipeline-template-modal" onClick={(e) => e.stopPropagation()}>
        <h3 id="save-template-title">Save as template</h3>
        <form onSubmit={handleSubmit}>
          <label htmlFor="save-template-name">Template name</label>
          <input
            id="save-template-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. My Review Pipeline"
            required
          />
          <div className="agent-modal-actions">
            <button type="button" className="copy-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="run-button">
              Save
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

function LoadTemplatesModal({ templates, pendingTemplate, onLoad, onConfirmLoad, onCancelLoad, onRequestDelete, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (pendingTemplate) {
    return createPortal(
      <div className="agent-modal-overlay" style={MODAL_PORTAL_STYLE} onClick={onCancelLoad} role="dialog" aria-modal="true" aria-labelledby="load-template-confirm-title">
        <div className="agent-modal pipeline-template-modal" onClick={(e) => e.stopPropagation()}>
          <h3 id="load-template-confirm-title">Replace pipeline?</h3>
          <p className="pipeline-template-confirm-message">
            Your current pipeline has been modified. Replace it with &quot;{pendingTemplate.name}&quot;?
          </p>
          <div className="agent-modal-actions">
            <button type="button" className="copy-btn" onClick={onCancelLoad}>
              Cancel
            </button>
            <button type="button" className="run-button" onClick={onConfirmLoad}>
              Replace
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className="agent-modal-overlay" style={MODAL_PORTAL_STYLE} onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="load-template-title">
      <div className="agent-modal pipeline-template-modal pipeline-template-modal-load" onClick={(e) => e.stopPropagation()}>
        <h3 id="load-template-title">Load template</h3>
        <h4 className="template-section-heading">Recommended</h4>
        {VISIBLE_TEMPLATES.map((t) => (
          <div key={t.id} className="builtin-template-card">
            <div className="builtin-template-info">
              <div className="builtin-template-name">{t.name}</div>
              <div className="builtin-template-description">{t.description}</div>
            </div>
            <button type="button" className="copy-btn" onClick={() => onLoad(t)}>
              Load
            </button>
          </div>
        ))}
        {templates.length > 0 && (
          <>
            <h4 className="template-section-heading">Saved</h4>
            <ul className="pipeline-template-list">
              {templates.map((t) => (
                <li key={t.id} className="pipeline-template-item">
                  <div className="pipeline-template-item-info">
                    <span className="pipeline-template-item-name">{t.name}</span>
                    <span className="pipeline-template-item-date">{new Date(t.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div className="pipeline-template-item-actions">
                    <button type="button" className="copy-btn" onClick={() => onLoad(t)}>
                      Load
                    </button>
                    <button type="button" className="pipeline-template-delete-btn" onClick={() => onRequestDelete(t.id)}>
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
        <div className="agent-modal-actions">
          <button type="button" className="copy-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ConfirmOverwriteModal({ templateName, existingId, onOverwrite, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="agent-modal-overlay" style={MODAL_PORTAL_STYLE} onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="overwrite-template-title">
      <div className="agent-modal pipeline-template-modal" onClick={(e) => e.stopPropagation()}>
        <h3 id="overwrite-template-title">Overwrite template?</h3>
        <p className="pipeline-template-confirm-message">
          A template named &quot;{templateName}&quot; already exists. Overwrite it?
        </p>
        <div className="agent-modal-actions">
          <button type="button" className="copy-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="run-button" onClick={() => onOverwrite(templateName, existingId)}>
            Overwrite
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ConfirmDeleteModal({ templateName, templateId, onDelete, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="agent-modal-overlay" style={MODAL_PORTAL_STYLE} onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="delete-template-title">
      <div className="agent-modal pipeline-template-modal" onClick={(e) => e.stopPropagation()}>
        <h3 id="delete-template-title">Delete template?</h3>
        <p className="pipeline-template-confirm-message">
          Delete template &quot;{templateName}&quot;? This cannot be undone.
        </p>
        <div className="agent-modal-actions">
          <button type="button" className="copy-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="pipeline-template-delete-confirm-btn" onClick={() => onDelete(templateId)}>
            Delete
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ConfirmLoadSharedModal({ sharedPipeline, onConfirm, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const count = Array.isArray(sharedPipeline) ? sharedPipeline.length : 0;

  return createPortal(
    <div className="agent-modal-overlay" style={MODAL_PORTAL_STYLE} onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="confirm-load-shared-title">
      <div className="agent-modal pipeline-template-modal" onClick={(e) => e.stopPropagation()}>
        <h3 id="confirm-load-shared-title">Load shared pipeline?</h3>
        <p className="pipeline-template-confirm-message">
          This link contains a pipeline with {count} agent{count !== 1 ? 's' : ''}. Replace your current pipeline?
        </p>
        <div className="agent-modal-actions">
          <button type="button" className="copy-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="run-button" onClick={onConfirm}>
            Replace
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function SharePipelineModal({ pipeline, onClose }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = pipelineToShareUrl(pipeline);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleCopy = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExportJson = () => {
    const payload = pipeline.map((p) => ({ id: p.id, name: p.name, systemPrompt: p.systemPrompt, model: p.model }));
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pipeline-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return createPortal(
    <div className="agent-modal-overlay" style={MODAL_PORTAL_STYLE} onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="share-pipeline-title">
      <div className="agent-modal share-modal" onClick={(e) => e.stopPropagation()}>
        <h3 id="share-pipeline-title">Share pipeline</h3>
        <label htmlFor="share-url-input">Link</label>
        <input
          id="share-url-input"
          type="text"
          className="share-url-input"
          value={shareUrl}
          readOnly
          aria-label="Share URL"
        />
        <div className="share-actions">
          <button type="button" className="copy-btn" onClick={handleCopy}>
            {copied ? 'Copied!' : 'Copy link'}
          </button>
          <button type="button" className="copy-btn" onClick={handleExportJson}>
            Export JSON
          </button>
        </div>
        <div className="agent-modal-actions">
          <button type="button" className="copy-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function TemplatePicker({ onSelect, onClose, afterIndex, importedSkills, setImportedSkills, onAddSkill, onAddGuardrail, getModelLabel }) {
  const [activeTab, setActiveTab] = useState('skills');
  const [importPaste, setImportPaste] = useState('');
  const [importError, setImportError] = useState(null);
  const [importSuccess, setImportSuccess] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    if (!importSuccess) return;
    const t = setTimeout(() => setImportSuccess(false), 2500);
    return () => clearTimeout(t);
  }, [importSuccess]);

  const importValidation = (() => {
    if (!importPaste.trim()) return { valid: false };
    const result = parseSkillManifest(importPaste);
    return { valid: result.ok, error: result.ok ? null : result.error };
  })();

  const allSkills = [...SKILLS_DEFAULTS, ...importedSkills];

  const handleSelectSkill = (skill) => {
    if (onAddSkill) {
      onAddSkill(skill, afterIndex);
      onClose();
    } else {
      onSelect({
        mode: 'add',
        afterIndex,
        initialNode: {
          name: skill.name,
          systemPrompt: skill.systemPrompt,
          model: skill.recommendedModel || 'gpt-4o',
        },
      });
    }
  };

  const handleSelectCustom = () => {
    onSelect({ mode: 'add', afterIndex, initialNode: null });
  };

  const handleImportSkill = (content) => {
    const toParse = content !== undefined ? content : importPaste;
    setImportError(null);
    const result = parseSkillManifest(toParse);
    if (!result.ok) {
      setImportError(result.error);
      return;
    }
    const newSkills = result.skills.map((s, i) => ({
      id: `imported-${Date.now()}-${i}`,
      ...s,
    }));
    setImportedSkills((prev) => [...newSkills, ...prev]);
    if (content === undefined) setImportPaste('');
    setImportSuccess(true);
  };

  const handleImportFromFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportError(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      handleImportSkill(ev.target?.result ?? '');
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleBackdropClick = (e) => {
    const targetIsCurrent = e.target === e.currentTarget;
    if (typeof console !== 'undefined' && console.log) {
      console.log('backdrop clicked', { targetIsCurrent, target: e.target?.className, currentTarget: e.currentTarget?.className });
    }
    if (targetIsCurrent) onClose();
  };

  return createPortal(
    <div
      className="tp-overlay"
      style={{ position: 'fixed', inset: 0, zIndex: 2147483647, isolation: 'isolate', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.55)', pointerEvents: 'auto' }}
      aria-labelledby="template-picker-title"
      onClick={handleBackdropClick}
    >
      <div
        className="tp-backdrop"
        style={{ position: 'absolute', inset: 0, zIndex: 0 }}
        onClick={() => onClose()}
        aria-hidden="true"
      />
      <div className="tp-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} style={{ position: 'relative', inset: 'unset', zIndex: 1, background: 'var(--bg-primary)', border: '1px solid var(--border-medium)', borderRadius: '12px', boxShadow: 'var(--shadow-lg)', padding: 'var(--space-xl)', width: '90%', maxWidth: '640px', maxHeight: '85vh', overflowY: 'auto', pointerEvents: 'auto', transform: 'none' }}>
        <div className="template-picker-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-lg)' }}>
          <h3 id="template-picker-title" style={{ margin: 0 }}>Choose agent template</h3>
          <button
            type="button"
            className="copy-btn"
            onClick={onClose}
            aria-label="Close"
            style={{ padding: '0.25rem 0.5rem' }}
          >
            ✕
          </button>
        </div>
        <div className="template-picker-tabs" role="tablist" aria-label="Template picker tabs">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'skills'}
            aria-controls="template-picker-skills-panel"
            id="tab-skills"
            className={`template-picker-tab ${activeTab === 'skills' ? 'template-picker-tab-active' : ''}`}
            onClick={() => setActiveTab('skills')}
          >
            Skills
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'custom'}
            aria-controls="template-picker-custom-panel"
            id="tab-custom"
            className={`template-picker-tab ${activeTab === 'custom' ? 'template-picker-tab-active' : ''}`}
            onClick={() => setActiveTab('custom')}
          >
            Custom
          </button>
        </div>

        {activeTab === 'skills' && (
          <div id="template-picker-skills-panel" role="tabpanel" aria-labelledby="tab-skills" className="template-picker-panel">
            <p className="skills-info-text">Skills are prompt templates only—safe to import. No code is executed.</p>
            <div className="template-picker-grid skill-cards-grid">
              {allSkills.map((skill, i) => (
                <button
                  key={skill.id}
                  type="button"
                  className="skill-card template-picker-card"
                  data-tooltip={
                    skill.description
                      ? skill.description.slice(0, 120) + (skill.description.length > 120 ? '...' : '')
                      : skill.systemPrompt
                        ? skill.systemPrompt.slice(0, 120) + (skill.systemPrompt.length > 120 ? '...' : '')
                        : ''
                  }
                  onClick={() => handleSelectSkill(skill)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelectSkill(skill);
                    }
                  }}
                >
                  <span className="template-picker-card-name">{skill.name}</span>
                  <span className="template-picker-card-desc">{skill.description || 'No description'}</span>
                  {(skill.tags || []).length > 0 && (
                    <div className="skill-tags-row">
                      {(skill.tags || []).map((tag) => (
                        <span key={tag} className="skill-tag">{tag}</span>
                      ))}
                    </div>
                  )}
                  {(skill.modelLabel || skill.recommendedModel || getModelLabel?.(skill.recommendedModel, 'architect')) && (
                    <span className="template-picker-card-model">
                      {skill.modelLabel || getModelLabel?.(skill.recommendedModel, 'architect') || skill.recommendedModel}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <div className="skills-import-section">
              <h4 className="skills-import-title">Import skill manifest</h4>
              <p className="skills-import-hint">Paste JSON or upload a file. Format: [&#123; name, description, systemPrompt, recommendedModel &#125;] or a single object.</p>
              <textarea
                className="skills-import-textarea"
                placeholder='[{"name":"My Skill","description":"...","systemPrompt":"You are..."}]'
                value={importPaste}
                onChange={(e) => { setImportPaste(e.target.value); setImportError(null); }}
                rows={3}
                aria-label="Paste skill manifest JSON"
              />
              <div className="skills-import-actions">
                <button
                  type="button"
                  className="run-button skills-add-skill-btn"
                  onClick={() => handleImportSkill()}
                  disabled={!importPaste.trim() || !importValidation.valid}
                  aria-label="Add skill from paste"
                >
                  Add Skill
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json,application/json"
                  onChange={handleImportFromFile}
                  className="skills-import-file-input"
                  aria-label="Upload skill manifest file"
                />
                <button type="button" className="copy-btn" onClick={() => fileInputRef.current?.click()}>
                  Import from file
                </button>
              </div>
              {importSuccess && <p className="skills-import-success">Skill Added!</p>}
              {(importError || importValidation.error) && (
                <p className="skills-import-error">{importError || importValidation.error}</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'custom' && (
          <div id="template-picker-custom-panel" role="tabpanel" aria-labelledby="tab-custom" className="template-picker-panel">
            {onAddGuardrail && (
              <div className="guardrails-palette">
                <h4 className="guardrails-palette-title">Guardrails</h4>
                <div className="guardrails-palette-grid">
                  {GUARDRAIL_TEMPLATES.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className="guardrail-btn"
                      onClick={() => { onAddGuardrail(t, afterIndex); onClose(); }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onAddGuardrail(t, afterIndex);
                          onClose();
                        }
                      }}
                    >
                      <span className="guardrail-btn-name">{t.name}</span>
                      <span className="guardrail-btn-desc">{t.systemPrompt.length > 60 ? `${t.systemPrompt.slice(0, 60)}…` : t.systemPrompt}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="template-picker-grid">
              <button
                type="button"
                className="template-picker-card template-picker-card-custom"
                onClick={handleSelectCustom}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelectCustom();
                  }
                }}
              >
                <span className="template-picker-card-name">Custom</span>
                <span className="template-picker-card-desc">Define your own agent from scratch</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

function getModelsForRole(role, _hasO, _hasA, _hasG, hasLocal) {
  const models = [];
  const roleId = typeof role === 'string' ? role.toLowerCase() : '';
  if (roleId === 'architect') {
    models.push(...MODEL_OPTIONS.openai, ...MODEL_OPTIONS.anthropic, ...MODEL_OPTIONS.gemini);
  } else if (roleId === 'skeptic') {
    models.push(...MODEL_OPTIONS.anthropic, ...MODEL_OPTIONS.openai, ...MODEL_OPTIONS.gemini);
  } else if (roleId === 'synthesizer') {
    models.push(...MODEL_OPTIONS.gemini, ...MODEL_OPTIONS.openai, ...MODEL_OPTIONS.anthropic);
  } else {
    models.push(...MODEL_OPTIONS.openai, ...MODEL_OPTIONS.anthropic, ...MODEL_OPTIONS.gemini);
  }
  if (hasLocal) models.push(...MODEL_OPTIONS.local);
  return models;
}

function ImagePreviewGrid({ images, onRemove, disabled, showIds }) {
  if (!images || images.length === 0) return null;
  return (
    <div className="image-preview-grid">
      {images.map((file, index) => (
        <ImageThumbnail key={`${file.name}-${file.size}-${index}`} file={file} onRemove={() => onRemove(index)} disabled={disabled} imageId={showIds ? `IMG${index + 1}` : null} />
      ))}
    </div>
  );
}

function ImageThumbnail({ file, onRemove, disabled, imageId }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    const objUrl = URL.createObjectURL(file);
    setUrl(objUrl);
    return () => URL.revokeObjectURL(objUrl);
  }, [file]);
  return (
    <div className={`image-thumbnail ${imageId ? 'image-thumbnail--with-id' : ''}`}>
      {imageId && <span className="uploaded-file-id">{imageId}</span>}
      {url && <img src={url} alt={file.name} className="image-thumbnail-img" />}
      {imageId && <span className="uploaded-file-name image-thumbnail-filename">{file.name}</span>}
      <button
        type="button"
        className="image-thumbnail-remove"
        onClick={onRemove}
        disabled={disabled}
        aria-label={`Remove ${file.name}`}
      >
        ×
      </button>
    </div>
  );
}

function App() {
  const [idea, setIdea] = useState('');
  const [codeFiles, setCodeFiles] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [uploadExpanded, setUploadExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [openaiKey, setOpenaiKey] = useState('');
  const [anthropicKey, setAnthropicKey] = useState('');
  const [geminiKey, setGeminiKey] = useState('');
  const [tavilyKey, setTavilyKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [, setDeliberationStatus] = useState(null);
  const [pipelineStatus, setPipelineStatus] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copiedPlan, setCopiedPlan] = useState(false);
  const [copiedPrepareForCursor, setCopiedPrepareForCursor] = useState(false);
  const [copiedShareResult, setCopiedShareResult] = useState(false);
  const [copiedTests, setCopiedTests] = useState(false);
  const [, setHistory] = useState([]);
  const [runHistory, setRunHistory] = useState([]);
  const [pipeline, setPipeline] = useState(() => {
    const loaded = loadPipeline();
    if (loaded && loaded.length > 0) return loaded;
    return DEFAULT_PIPELINE.map((p) => ({
      ...p,
      tools: Array.isArray(p.tools) ? p.tools : [],
      code: p.code ?? null,
    }));
  });
  const [templates, setTemplates] = useState(() => loadPipelineTemplates());
  const [importedSkills, setImportedSkills] = useState(() => loadImportedSkills());
  const [localLlmUrl, setLocalLlmUrl] = useState('');
  const [localLlmModel, setLocalLlmModel] = useState('llama3');
  const [localLlmConnectionStatus, setLocalLlmConnectionStatus] = useState('idle'); // idle | testing | connected | unreachable
  const [runMode, setRunMode] = useState('cloud'); // cloud | local
  const [rerunSkepticLoading, setRerunSkepticLoading] = useState(false);
  const [skepticIntensity, setSkepticIntensity] = useState(3);
  const [shared, setShared] = useState(false);
  const [pipelineSelectorOpen, setPipelineSelectorOpen] = useState(null);
  const [agentModal, setAgentModal] = useState(null);
  const [showInsertHint, setShowInsertHint] = useState(() => {
    try {
      return !localStorage.getItem(STORAGE_KEYS.seenInsertHint);
    } catch {
      return false;
    }
  });
  const [expandedAgentIds, setExpandedAgentIds] = useState(() => new Set());

  const [narrativeOpen, setNarrativeOpen] = useState(true);
  const [resultTab, setResultTab] = useState('implementation');
  const [isHistoryOpen, setIsHistoryOpen] = useState(true);
  const [apiKeysOpen, setApiKeysOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [workflowEditorOpen, setWorkflowEditorOpen] = useState(false);
  const [emailCapture, setEmailCapture] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [abCompareEnabled, setAbCompareEnabled] = useState(false);
  const [selectedJudge, setSelectedJudge] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEYS.selectedJudge) || 'default';
    } catch {
      return 'default';
    }
  });
  const [abResults, setAbResults] = useState(null);
  const [abCostPerToken, setAbCostPerToken] = useState(DEFAULT_COST_PER_TOKEN);
  const [workflow, setWorkflow] = useState(() => defaultWorkflow.map((w) => ({ ...w })));
  const [selectedTemplate, setSelectedTemplate] = useState('Custom');
  const [ideaType, setIdeaType] = useState('coding');
  const [streamingContent, setStreamingContent] = useState({}); // { [runId]: { [agentId]: string } }
  const [currentRunId, setCurrentRunId] = useState(null);
  const [promptsLibrary, setPromptsLibrary] = useState(null); // { architect, skeptic, synthesizer, grader }
  const streamQueueRef = useRef([]);
  const streamAccumulatorRef = useRef({}); // { [runId]: { [agentId]: string } } - ref-based to prevent double-append
  const skepticRef = useRef(''); // Skeptic: single source of truth, latest-wins
  const streamMountedRef = useRef(true);
  const streamListenerActiveRef = useRef(false); // Strict mode guard: only one listener
  const projectFileInputRef = useRef(null);
  const ideaRef = useRef(null);
  const [abExpandedRaw, setAbExpandedRaw] = useState(false);
  const [abExpandedCouncil, setAbExpandedCouncil] = useState(false);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (streamListenerActiveRef.current) return;
    streamListenerActiveRef.current = true;
    streamMountedRef.current = true;
    const timer = setInterval(() => {
      if (!streamMountedRef.current) return;
      const q = streamQueueRef.current;
      if (q.length === 0) return;
      const batch = q.splice(0, q.length);
      const acc = streamAccumulatorRef.current;
      for (const next of batch) {
        const runId = next.runId || 'default';
        const agentId = next.id;
        const chunk = next.text;
        if (!chunk || typeof chunk !== 'string') continue;
        if (!acc[runId]) acc[runId] = {};
        if (agentId === 'skeptic') {
          const current = skepticRef.current;
          if (chunk.startsWith(current) && chunk.length > current.length) {
            skepticRef.current = chunk;
          } else if (!current.endsWith(chunk)) {
            skepticRef.current += chunk;
          } else {
            continue;
          }
          const sanitized = sanitizeSkepticOutput(skepticRef.current);
          acc[runId][agentId] = sanitized;
        } else {
          const current = acc[runId][agentId] || '';
          if (chunk.startsWith(current) && chunk.length > current.length) {
            acc[runId][agentId] = chunk;
          } else if (current.endsWith(chunk)) {
            continue;
          } else {
            let overlap = 0;
            for (let n = Math.min(chunk.length, current.length); n > 0; n--) {
              if (current.endsWith(chunk.slice(0, n))) {
                overlap = n;
                break;
              }
            }
            acc[runId][agentId] = overlap > 0 ? current + chunk.slice(overlap) : current + chunk;
          }
        }
      }
      if (!streamMountedRef.current) return;
      console.log('[stream drain] batch size:', batch.length, 'chunks:', batch.map((b) => ({ id: b.id, text: b.text?.slice(0, 80) })));
      setStreamingContent(() => {
        const snapshot = {};
        for (const r of Object.keys(acc)) {
          snapshot[r] = { ...acc[r] };
        }
        return snapshot;
      });
    }, 16);
    return () => {
      streamListenerActiveRef.current = false;
      streamMountedRef.current = false;
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/api/prompts`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => data && setPromptsLibrary(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const state = parseShareStateFromUrl();
    if (state) {
      setIdea(state.idea);
      setResult((prev) => (prev ? { ...prev, cursor_prompt: state.cursor_prompt, plan_md: state.plan_md } : { cursor_prompt: state.cursor_prompt, plan_md: state.plan_md }));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const sharedResult = loadResultFromUrl();
    if (sharedResult && (sharedResult.cursor_prompt || sharedResult.plan_md)) {
      setResult(sharedResult);
      if (sharedResult.idea) setIdea(sharedResult.idea);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const sharedPipeline = loadPipelineFromUrl();
    if (sharedPipeline) {
      setAgentModal({ mode: 'confirm-load-shared', sharedPipeline });
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.removeItem(STORAGE_KEYS.apiKeysOpen);
    } catch {
      /* ignore */
    }
  }, []);

  /* Force sidebar closed on mount; clear any persisted sidebar state */
  useEffect(() => {
    setSidebarOpen(false);
    if (typeof console !== 'undefined' && console.log) console.log('Forced sidebar closed on mount');
    try {
      localStorage.removeItem('council_sidebar_open');
    } catch {
      /* ignore */
    }
  }, []);

  /* Interrupted run recovery + sync workflow from pipeline (fixes order jumble on back/remount) */
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.activeRun);
      if (raw) {
        const parsed = JSON.parse(raw);
        const startedAt = parsed?.startedAt || 0;
        const age = Date.now() - startedAt;
        if (age < 10 * 60 * 1000) {
          setError('Run was interrupted. Start a new run.');
        }
        localStorage.removeItem(STORAGE_KEYS.activeRun);
      }
    } catch {
      try {
        localStorage.removeItem(STORAGE_KEYS.activeRun);
      } catch {
        /* ignore */
      }
    }
    const pl = loadPipeline();
    if (pl && pl.length > 0) {
      const items = pl.map((p) => ({
        id: p.id,
        name: p.name,
        description: p.description,
        provider: p.provider || getProviderFromModel(p.model),
        systemPrompt: p.systemPrompt,
        parentIds: [],
      }));
      setWorkflow(recalculateParentIdsFromOrder(items));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* beforeunload: warn user before leaving mid-run */
  useEffect(() => {
    const handler = (e) => {
      if (loading) {
        // console.log('[Council] beforeunload: run in progress, prompting user');
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [loading]);

  /* Cleanup on unmount: abort fetch stream to prevent zombie connections */
  useEffect(() => {
    return () => {
      // console.log('[Council] unmount: aborting fetch, clearing activeRun');
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      try {
        localStorage.removeItem(STORAGE_KEYS.activeRun);
      } catch {
        /* ignore */
      }
    };
  }, []);

  useEffect(() => {
    if (typeof console !== 'undefined' && console.log) console.log('sidebarOpen changed:', sidebarOpen);
  }, [sidebarOpen]);

  useEffect(() => {
    setOpenaiKey(localStorage.getItem(STORAGE_KEYS.openai) || '');
    setAnthropicKey(localStorage.getItem(STORAGE_KEYS.anthropic) || '');
    const raw = localStorage.getItem(STORAGE_KEYS.gemini);
    const clean = sanitizeKey(raw);
    if (raw && !clean) {
      try {
        localStorage.removeItem(STORAGE_KEYS.gemini);
      } catch {
        /* ignore */
      }
    }
    setGeminiKey(clean || '');
    setTavilyKey(localStorage.getItem(STORAGE_KEYS.tavily) || '');
    setLocalLlmUrl(localStorage.getItem(STORAGE_KEYS.localLlmUrl) || '');
    setLocalLlmModel(localStorage.getItem(STORAGE_KEYS.localLlmModel) || 'llama3');
    try {
      const h = JSON.parse(localStorage.getItem(STORAGE_KEYS.history) || '[]');
      setHistory(Array.isArray(h) ? h.slice(0, 10) : []);
    } catch {
      setHistory([]);
    }
    try {
      const rh = JSON.parse(localStorage.getItem(STORAGE_KEYS.runHistory) || '[]');
      setRunHistory(Array.isArray(rh) ? rh : []);
    } catch {
      setRunHistory([]);
    }
  }, []);

  useEffect(() => {
    if (openaiKey) localStorage.setItem(STORAGE_KEYS.openai, openaiKey);
  }, [openaiKey]);
  useEffect(() => {
    if (anthropicKey) localStorage.setItem(STORAGE_KEYS.anthropic, anthropicKey);
  }, [anthropicKey]);
  useEffect(() => {
    if (tavilyKey) localStorage.setItem(STORAGE_KEYS.tavily, tavilyKey);
    else try { localStorage.removeItem(STORAGE_KEYS.tavily); } catch { /* ignore */ }
  }, [tavilyKey]);
  useEffect(() => {
    if (localLlmUrl) localStorage.setItem(STORAGE_KEYS.localLlmUrl, localLlmUrl);
  }, [localLlmUrl]);
  useEffect(() => {
    if (localLlmModel) localStorage.setItem(STORAGE_KEYS.localLlmModel, localLlmModel);
  }, [localLlmModel]);

  /* apiKeysOpen no longer persisted to localStorage; always starts closed on load */

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.pipeline, JSON.stringify(pipeline));
    } catch (e) {
      console.warn('Pipeline save failed:', e);
    }
  }, [pipeline]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.pipelineTemplates, JSON.stringify(templates));
    } catch (e) {
      console.warn('Pipeline templates save failed:', e);
    }
  }, [templates]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.importedSkills, JSON.stringify(importedSkills));
    } catch (e) {
      console.warn('Imported skills save failed:', e);
    }
  }, [importedSkills]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.selectedJudge, selectedJudge);
    } catch (e) {
      console.warn('Selected judge save failed:', e);
    }
  }, [selectedJudge]);

  useEffect(() => {
    if (selectedJudge !== 'default' && !importedSkills.find((s) => s.id === selectedJudge)) {
      setSelectedJudge('default');
    }
  }, [selectedJudge, importedSkills]);

  useEffect(() => {
    if (result?.results?.length) {
      const synth = result.results.find((r) => r.id === 'synthesizer');
      const toExpand = synth ? synth.id : result.results[result.results.length - 1]?.id;
      setExpandedAgentIds(toExpand ? new Set([toExpand]) : new Set());
    }
  }, [result?.results]);

  const toggleAgentOutput = (id) => {
    setExpandedAgentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const saveToHistory = (entry) => {
    if (!entry || !entry.idea || !String(entry.idea).trim()) return; // don't save runs with no prompt
    setHistory((prev) => {
      const next = [entry, ...prev].slice(0, 10);
      try {
        localStorage.setItem(STORAGE_KEYS.history, JSON.stringify(next));
      } catch (e) {
        console.warn('History save failed:', e);
      }
      return next;
    });
  };

  const loadFromRunHistory = (entry) => {
    const hasResults = result != null || (currentRunId && Object.keys(streamingContent[currentRunId] || {}).length > 0);
    if (hasResults && !window.confirm('Loading a past run will clear your current results. Continue?')) return;
    setSelectedTemplate(entry?.templateName || 'Custom');
    setIdea(entry.idea || '');
    const wf = Array.isArray(entry.workflow) ? entry.workflow.map((w) => ({ ...w })) : defaultWorkflow.map((w) => ({ ...w }));
    setWorkflow(recalculateParentIdsFromOrder(wf));
    setPipeline(wf.map((w) => ({
      id: w.id,
      name: w.name,
      description: w.description,
      systemPrompt: w.systemPrompt,
      model: w.model || 'gpt-4o',
      tools: Array.isArray(w.tools) ? w.tools : [],
      code: w.code ?? null,
    })));
    setResult({
      plan_md: entry.plan_md || '',
      cursor_prompt: entry.cursor_prompt || '',
      results: Array.isArray(entry.results) ? entry.results : undefined,
    });
    streamAccumulatorRef.current = {};
    skepticRef.current = '';
    setStreamingContent({});
    setCurrentRunId(null);
    setError(null);
    streamQueueRef.current = [];
    setSidebarOpen(false);
  };

  const deleteRunHistoryItem = (entryId, e) => {
    e.stopPropagation();
    setRunHistory((prev) => {
      const next = prev.filter((r) => r.id !== entryId);
      try {
        localStorage.setItem(STORAGE_KEYS.runHistory, JSON.stringify(next));
      } catch (err) {
        console.warn('Run history delete failed:', err);
      }
      return next;
    });
  };

  const copyForAiCopilot = () => {
    const prompt = result?.cursor_prompt ?? '';
    const plan = result?.plan_md ?? '';
    const combined = [prompt, plan].filter(Boolean).join('\n\n## PLAN.md\n\n');
    const text = combined
      ? `I have an architectural plan to implement. Use the instructions below to update the codebase, maintaining existing patterns and handling edge cases:\n\n${combined}`
      : '';
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedPrepareForCursor(true);
    setTimeout(() => setCopiedPrepareForCursor(false), 2000);
  };

  const exportPlanMd = () => {
    const content = result?.plan_md;
    if (content == null || content === '') return;
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `PLAN-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportAbResults = () => {
    if (!abResults) return;
    const ts = new Date().toISOString();
    const fmt = (n) => (typeof n === 'number' ? n.toFixed(4) : String(n ?? ''));
    const section = (branch) => {
      const r = branch;
      if (!r) return '';
      let md = '';
      if (r.error) {
        md += `**Error:** ${r.error}\n\n`;
      }
      md += `### Prompt\n\n\`\`\`\n${r.prompt ?? '(none)'}\n\`\`\`\n\n`;
      md += `### Response\n\n\`\`\`\n${r.response ?? '(empty)'}\n\`\`\`\n\n`;
      md += `### Metrics\n`;
      md += `- Tokens: ${r.tokens ?? '—'}\n`;
      md += `- Cost: $${fmt(r.cost)}\n`;
      if (r.grade) {
        if (r.grade.needs_review) {
          md += `- Score (LLM Judge): Needs review\n\n`;
          md += `### LLM Judge Grade\n`;
          md += `- Status: Needs review (invalid JSON)\n`;
        } else {
          md += `- Score (LLM Judge): ${r.grade.score}/100\n\n`;
          md += `### LLM Judge Grade\n`;
          md += `- Score: ${r.grade.score}/100\n`;
          md += `- Adherence: ${r.grade.breakdown?.adherence ?? '—'}/40\n`;
          md += `- Technical Sophistication: ${r.grade.breakdown?.technical_sophistication ?? '—'}/30\n`;
          md += `- DX/Cleanliness: ${r.grade.breakdown?.dx ?? '—'}/20\n`;
          md += `- Contextual Safety: ${r.grade.breakdown?.contextual_safety ?? '—'}/10\n`;
        }
        if (r.grade.metrics && !r.grade.needs_review) {
          md += `- console.log: ${r.grade.metrics.hasConsoleLog ? 'yes' : 'no'}\n`;
          md += `- aria-: ${r.grade.metrics.hasAria ? 'yes' : 'no'}\n`;
          md += `- TODOs: ${r.grade.metrics.todoCount ?? '—'}\n`;
        }
        if (r.grade.hallucinations?.length > 0 && !r.grade.needs_review) {
          md += `\n**Hallucinations:**\n`;
          r.grade.hallucinations.forEach((h) => { md += `- ${h}\n`; });
        }
        if (r.grade.reasoning && !r.grade.needs_review) {
          md += `\n**Reasoning:**\n\`\`\`\n${r.grade.reasoning}\n\`\`\`\n`;
        }
        if (r.grade.needs_review && r.grade.raw_output) {
          md += `\n**Raw output:**\n\`\`\`\n${r.grade.raw_output}\n\`\`\`\n`;
        }
      } else if (r.heuristics) {
        md += `- Score (Heuristic): ${r.heuristics.score ?? '—'}\n`;
        md += `- TODOs: ${r.heuristics.todoCount ?? '—'}\n`;
        md += `- console.log: ${r.heuristics.consoleLogCount ?? '—'}\n`;
        md += `- aria-: ${r.heuristics.hasAria ? 'yes' : 'no'}\n`;
      }
      return md;
    };
    const rawSection = section(abResults.raw);
    const councilSection = section(abResults.council);
    const content = `# A/B Test Results
Generated: ${ts}
Cost per Token: $${fmt(abCostPerToken)}

## Raw Results

${rawSection}

## Pipeline Results

${councilSection}
`;
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ab-results-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const runRerunSkeptic = async () => {
    const plan = result?.architect_plan;
    if (!plan || !idea.trim()) return;
    setRerunSkepticLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('idea', idea);
      formData.append('architect_plan', plan);
      formData.append('skeptic_intensity', String(skepticIntensity));
      formData.append('openai_key', openaiKey);
      formData.append('anthropic_key', anthropicKey);
      formData.append('gemini_key', geminiKey);
      const res = await fetch('https://council-production-d5c7.up.railway.app/api/skeptic', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const errMsg = Array.isArray(data.detail) ? data.detail.map((d) => d.msg || d.message).join('; ') : (typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail || res.statusText));
        throw new Error(errMsg || `Request failed (${res.status})`);
      }
      setResult((prev) => ({
        ...prev,
        cursor_prompt: data.cursor_prompt ?? prev?.cursor_prompt,
        plan_md: data.plan_md ?? prev?.plan_md,
        skeptic_text: data.skeptic_text ?? prev?.skeptic_text,
      }));
    } catch (err) {
      setError(err.message || 'Re-run skeptic failed');
    } finally {
      setRerunSkepticLoading(false);
    }
  };

  useEffect(() => {
    const hasO = !!openaiKey?.trim();
    const hasA = !!anthropicKey?.trim();
    const hasG = !!geminiKey?.trim();
    const hasLocal = !!localLlmUrl?.trim();
    setPipeline((prev) => {
      let changed = false;
      const next = prev.map((node) => {
        const models = getModelsForRole(node.id, hasO, hasA, hasG, hasLocal);
        if (models.length && !models.some((m) => m.value === node.model)) {
          changed = true;
          return { ...node, model: models[0].value };
        }
        return node;
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openaiKey, anthropicKey, geminiKey, localLlmUrl]);

  const displayPipeline = pipeline.length > 0 ? pipeline : DEFAULT_PIPELINE;
  const allLocalPipeline = displayPipeline.length > 0 && displayPipeline.every((a) => isLocalAgent(a, localLlmUrl));
  const agentStack = displayPipeline;
  const displayWorkflow = getWorkflowForDisplay(workflow);
  const hasIntensitySupport = agentStack.some((p) => p.id === 'skeptic' || p.intensity != null);

  const getAgentDisplayName = (agentId, nodeOrResult) =>
    pipelineStatus[agentId]?.name ||
    (nodeOrResult?.name && nodeOrResult.name !== 'Custom' && nodeOrResult.name !== 'custom' ? nodeOrResult.name : null) ||
    pipeline.find((p) => p.id === agentId)?.name ||
    workflow.find((w) => w.id === agentId)?.name ||
    agentId ||
    'Agent';

  useEffect(() => {
    if (loading && Object.keys(streamingContent).length > 0) {
      console.log('Live Feed streamingContent keys:', Object.keys(streamingContent), 'displayWorkflow ids:', displayWorkflow.map((n) => n.id), 'content:', streamingContent);
    }
  }, [loading, streamingContent, displayWorkflow]);

  const getAgentOutputFromResult = (agentId) => {
    const r = result?.results?.find((x) => x.id === agentId);
    if (r?.output) return r.output;
    if (agentId === 'architect') return result?.architect_plan;
    if (agentId === 'skeptic') return result?.skeptic_text;
    if (agentId === 'synthesizer') return result?.cursor_prompt || result?.plan_md;
    return result?.summary ?? '';
  };

  const runData = currentRunId ? (streamingContent[currentRunId] || {}) : {};
  const liveFeed = displayWorkflow.filter((n) => (runData[n.id] || '').trim().length > 0);
  const isProcessing = loading || liveFeed.length > 0 || result != null || !!error;
  useEffect(() => {
    const graderContent = currentRunId ? (streamingContent[currentRunId]?.['grader']) : null;
    if (!loading && graderContent) {
      const scavenged = parseGraderOutput(graderContent);
      console.log('FINAL SCAVENGE:', scavenged);
      setAbResults((prev) => {
        if (!prev) return prev;
        if (prev.council?.grade?.score === 0) {
          return { ...prev, council: { ...prev.council, grade: scavenged } };
        }
        if (prev.raw?.grade?.score === 0) {
          return { ...prev, raw: { ...prev.raw, grade: scavenged } };
        }
        return prev;
      });
    }
  }, [loading, currentRunId, streamingContent]);

  const activePipelineNode = loading
    ? (displayWorkflow.find((n) => pipelineStatus[n.id]?.status === 'active')?.id ?? null)
    : pipelineSelectorOpen;

  useEffect(() => {
    if (!loading || !activePipelineNode) return;
    document.getElementById(`feed-card-${activePipelineNode}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activePipelineNode, loading]);

  useEffect(() => {
    setResultTab('implementation');
  }, [currentRunId]);

  const handleFileChange = (e) => {
    const newSelectedFiles = Array.from(e.target.files || []);
    const newCode = [];
    const newImages = [];
    for (const f of newSelectedFiles) {
      if (f.type?.startsWith('image/')) {
        newImages.push(f);
      } else {
        newCode.push(f);
      }
    }
    const isDuplicate = (file, list) =>
      list.some((existing) => existing.name === file.name && existing.size === file.size);
    setCodeFiles((prev) => [...prev, ...newCode.filter((f) => !isDuplicate(f, prev))]);
    setImageFiles((prev) => [...prev, ...newImages.filter((f) => !isDuplicate(f, prev))]);
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (!files?.length) return;
    handleFileChange({ target: { files } });
  };

  const handleRemoveCodeFile = (indexToRemove) => {
    setCodeFiles((prev) => prev.filter((_, i) => i !== indexToRemove));
  };

  const handleRemoveImageFile = (indexToRemove) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== indexToRemove));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const copyToClipboard = (text, setCopied) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = useCallback(() => {
    const url = createShareUrl(idea, result?.cursor_prompt, result?.plan_md);
    if (url) {
      navigator.clipboard.writeText(url);
      setShared(true);
      setTimeout(() => setShared(false), 2000);
    }
  }, [idea, result?.cursor_prompt, result?.plan_md]);

  const buildCouncilFormData = useCallback((pipelineOverride, ideaOverride, filesOverride, runIdOverride, workflowOverride, skipWorkflow) => {
    const formData = new FormData();
    const pl = pipelineOverride ?? pipeline;
    const codeToUse = filesOverride !== undefined
      ? filesOverride.filter((f) => !f.type?.startsWith('image/'))
      : codeFiles;
    const imagesToUse = filesOverride !== undefined
      ? filesOverride.filter((f) => f.type?.startsWith('image/'))
      : imageFiles;
    const fileLabels = [
      ...codeToUse.map((f, i) => `[FILE${i + 1}: ${f.name || f.file?.name || 'unknown'}]`),
      ...imagesToUse.map((f, i) => `[IMG${i + 1}: ${f.name || f.file?.name || 'unknown'}]`),
    ];
    const ideaToSend = ideaOverride !== undefined ? ideaOverride : idea;
    const ideaWithLabels = fileLabels.length > 0 ? fileLabels.join('\n') + '\n\n' + ideaToSend : ideaToSend;
    formData.append('idea', ideaWithLabels);
    if (runIdOverride) formData.append('run_id', runIdOverride);
    formData.append('openai_key', openaiKey);
    formData.append('anthropic_key', anthropicKey);
    formData.append('gemini_key', geminiKey);
    formData.append('pipeline', JSON.stringify(pl));
    const pipelineJson = pl.map((agent) => {
      const base = (agent.id || '').split('-')[0];
      const prov = agent.provider || getProviderFromModel(agent.model);
      const displayName = agent.name || agent.id || '';
      const agentTools = Array.isArray(agent.tools) ? agent.tools : [];
      const agentCode = typeof agent.code === 'string' ? agent.code : null;
      if (base === 'architect') return { role: 'Architect', provider: prov, model: agent.model, tools: agentTools, code: agentCode };
      if (base === 'skeptic') return { role: 'Skeptic', provider: prov, model: agent.model, tools: agentTools, code: agentCode };
      if (base === 'synthesizer') return { role: 'Synthesizer', provider: prov, model: agent.model, tools: agentTools, code: agentCode };
      if (base === 'verifier') return { role: 'Verifier', provider: prov, model: agent.model, tools: agentTools, code: agentCode };
      return { role: 'Custom', name: displayName, system_prompt: agent.systemPrompt || '', provider: prov, model: agent.model, tools: agentTools, code: agentCode };
    });
    formData.append('pipeline_json', JSON.stringify(pipelineJson));
    if (!skipWorkflow) {
      const wf = workflowOverride ?? workflow;
      const workflowPayload = wf.map((w) => ({
      id: w.id,
      name: w.name || w.id,
      system_prompt: w.systemPrompt || promptsLibrary?.[w.id] || '',
      provider: w.provider || 'openai',
      model: w.model || undefined,
      parent_ids: w.parentIds || [],
      pass_full_history_parents: w.passFullHistoryParents || [],
      tools: w.tools || [],
      json_schema: w.jsonSchema || undefined,
      fred_api_key: w.fredApiKey || undefined,
      search_api_key: w.searchApiKey || undefined,
    }));
      formData.append('workflow', JSON.stringify(workflowPayload));
    }
    formData.append('skeptic_intensity', String(skepticIntensity));
    if (tavilyKey?.trim()) {
      formData.append('tavily_key', tavilyKey.trim());
    }
    const hasLocalAgent = pl.some((a) => a.provider === 'local');
    const effectiveLocalUrl = localLlmUrl?.trim() || (hasLocalAgent ? 'http://localhost:11434' : '');
    if (effectiveLocalUrl) {
      formData.append('local_llm_url', effectiveLocalUrl);
      formData.append('local_llm_model', localLlmModel || 'llama3');
    }
    codeToUse.forEach((file) => formData.append('files', file));
    imagesToUse.forEach((file) => formData.append('images', file));
    return formData;
  }, [idea, openaiKey, anthropicKey, geminiKey, tavilyKey, pipeline, workflow, promptsLibrary, skepticIntensity, localLlmUrl, localLlmModel, codeFiles, imageFiles]);

  const callCouncilApi = useCallback(async (formData, onProgress, onContent, onMetadata, setStreamingContentFn, runIdParam, pipelineForMapping) => {
    const res = await fetch(COUNCIL_API_URL, {
      method: 'POST',
      body: formData,
      signal: abortControllerRef.current?.signal,
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      let errMsg = data.detail;
      if (Array.isArray(errMsg)) {
        errMsg = errMsg.map((d) => d.msg || d.message).join('; ');
      } else if (typeof errMsg !== 'string') {
        errMsg = errMsg ? JSON.stringify(errMsg) : res.statusText;
      }
      throw new Error(errMsg || `Request failed (${res.status})`);
    }

    const extractAgentIdFromRawLine = (raw) => {
      const m = raw.match(/"(?:agentId|agent)"\s*:\s*["']?(architect|skeptic|synthesizer|[a-z0-9_-]+)/);
      if (m) return m[1];
      if (raw.includes('synthesizer')) return 'synthesizer';
      if (raw.includes('skeptic')) return 'skeptic';
      if (raw.includes('architect')) return 'architect';
      return null;
    };

    const contentType = res.headers.get('content-type') || '';
    // console.log('[Stream] Response content-type:', contentType, 'status:', res.status);
    if (res.ok && res.body && (contentType.includes('ndjson') || contentType.includes('stream') || contentType.includes('text/plain') || contentType.includes('octet-stream'))) {
      if (!res.body) {
        console.error('[Stream] Body is null despite stream content-type');
        throw new Error('Stream body is null');
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullStream = '';
      let finalResult = null;
      let chunkCount = 0;
      let lineCount = 0;

      const mapBackendAgentIdToFrontend = (backendId) => {
        if (!backendId || !Array.isArray(pipelineForMapping) || pipelineForMapping.length === 0) return backendId;
        const builtin = ['architect', 'skeptic', 'synthesizer'];
        if (builtin.includes(backendId)) {
          // Pipeline agents may have timestamped IDs (e.g. "architect-1234-0") after template loading.
          // Find the pipeline agent whose base name matches the builtin role the backend reported.
          const match = pipelineForMapping.find((a) => (a.id || '').split('-')[0] === backendId);
          return match?.id || backendId;
        }
        const m = String(backendId).match(/^custom_(\d+)$/);
        if (m) {
          const idx = parseInt(m[1], 10);
          const frontendId = pipelineForMapping[idx]?.id;
          return frontendId || backendId;
        }
        return backendId;
      };

      const processLine = (cleanLine) => {
        if (cleanLine === '[DONE]' || cleanLine.startsWith('data: [DONE]')) {
          return;
        }
        if (cleanLine.startsWith('data: ')) {
          try {
            const obj = JSON.parse(cleanLine.slice(6).trim());
            if (obj.final === true || obj.results) {
              finalResult = {
                ...obj,
                cursor_prompt: obj.cursor_prompt || obj.synthesizer_output || '',
                plan_md: obj.plan_md || '',
              };
              return;
            }
          } catch (_) {}
        }
        if (!cleanLine) return;
        lineCount += 1;
        // console.log('[Stream] Raw line', lineCount, ':', cleanLine.slice(0, 100));
        try {
          const obj = JSON.parse(cleanLine);
          const rawId = obj.agentId || obj.agent || extractAgentIdFromRawLine(cleanLine);
          const id = mapBackendAgentIdToFrontend(rawId);
          const runId = obj.runId ?? runIdParam ?? 'default';
          if (obj.type === 'progress' && onProgress && id) {
            // console.log('[Stream] progress', id, obj.status);
            onProgress(id, obj.status, obj.message, obj.name);
          } else if (obj.type === 'architect' || obj.type === 'skeptic' || obj.type === 'synthesizer') {
            const chunk = (obj.content ?? '').replace(/\[MODE:\s*(?:NO_REPO|REPO)\]/gi, '').replace(/\[\[?MODE:.*?\]\]?/gi, '');
            const agentId = mapBackendAgentIdToFrontend(obj.type);
            if (setStreamingContentFn && chunk) {
              setStreamingContentFn((prev) => ({
                ...prev,
                [runId]: { ...(prev[runId] || {}), [agentId]: chunk },
              }));
            }
            if (onContent && chunk) onContent(agentId, chunk, runId);
          } else if (obj.type === 'content' && id) {
            const chunk = (obj.content ?? '').replace(/\[MODE:\s*(?:NO_REPO|REPO)\]/gi, '').replace(/\[\[?MODE:.*?\]\]?/gi, '');
            // console.log('[Stream] content', id, 'chunk len:', chunk?.length);
            if (setStreamingContentFn && chunk) {
              setStreamingContentFn((prev) => ({
                ...prev,
                [runId]: { ...(prev[runId] || {}), [id]: (prev[runId]?.[id] || '') + chunk },
              }));
            }
            if (onContent && chunk) onContent(id, chunk, runId);
          } else if (obj.type === 'metadata' && onMetadata) {
            onMetadata(obj.usage, obj.cost);
          } else if (obj.type === 'result' || obj.final === true || obj.results) {
            // console.log('[Stream] Result received:', Object.keys(obj));
            finalResult = {
              ...obj,
              cursor_prompt: obj.cursor_prompt || obj.synthesizer_output || '',
              plan_md: obj.plan_md || '',
              verifier_verdict: obj.verifier_verdict || '',
              verifier_items: obj.verifier_items || [],
              verifier_text: obj.verifier_text || obj.verifier || '',
            };
          } else if (obj.type === 'error') {
            throw new Error(obj.detail || 'Stream error');
          }
        } catch (e) {
          if (e.message === 'Stream error') throw e;
          const fallbackId = extractAgentIdFromRawLine(cleanLine);
          if (fallbackId && cleanLine.length > 5) {
            const runId = runIdParam ?? 'default';
            if (onContent) onContent(fallbackId, cleanLine + '\n', runId);
          }
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          // console.log('[Stream] Stream ended. Chunks:', chunkCount, 'lines:', lineCount, 'finalResult:', !!finalResult);
          if (!finalResult && buffer.trim()) {
            const lines = buffer.split('\n').map((l) => l.replace(/^data:\s*/, '').trim()).filter(Boolean);
            for (const toParse of lines) {
              try {
                const obj = JSON.parse(toParse);
                if (obj.results || obj.final === true || obj.type === 'result') {
                  // console.log('[Stream] Final buffer parse: found result with results/final/type');
                  finalResult = {
                    ...obj,
                    cursor_prompt: obj.cursor_prompt || obj.synthesizer_output || '',
                    plan_md: obj.plan_md || '',
                    verifier_verdict: obj.verifier_verdict || '',
                    verifier_items: obj.verifier_items || [],
                    verifier_text: obj.verifier_text || obj.verifier || '',
                  };
                  break;
                }
              } catch (_) {}
            }
          }
          break;
        }
        chunkCount += 1;
        const decoded = decoder.decode(value, { stream: true });
        // if (chunkCount <= 3 || decoded.includes('"type":"result"')) {
        //   console.log('[Stream] Chunk', chunkCount, 'received, length:', decoded.length, 'preview:', decoded.slice(0, 80));
        // }
        fullStream += decoded;
        buffer += decoded;
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const cleanLine = line.replace(/^data:\s*/, '').trim();
          processLine(cleanLine);
        }
      }

      if (!finalResult && (fullStream || buffer).includes('---COUNCIL_FINAL_RESULT---')) {
        const text = (fullStream || buffer);
        const parts = text.split('---COUNCIL_FINAL_RESULT---');
        const finalJsonString = parts[parts.length - 1].trim();
        try {
          const parsed = JSON.parse(finalJsonString);
          if (parsed.results) {
            // console.log('[Stream] Delimiter parse: found result');
            finalResult = {
              ...parsed,
              cursor_prompt: parsed.cursor_prompt || parsed.synthesizer_output || '',
              plan_md: parsed.plan_md || '',
              verifier_verdict: parsed.verifier_verdict || '',
              verifier_items: parsed.verifier_items || [],
              verifier_text: parsed.verifier_text || parsed.verifier || '',
            };
          } else if (parsed.status !== undefined) {
            // results_map format: agent IDs as direct string keys, no top-level 'results' array
            const METADATA_KEYS = new Set([
              'status', 'errors',
              'synthesizer_cursor_prompt', 'synthesizer_plan_md',
              'verifier', 'verifier_verdict', 'verifier_items',
              'judge', 'tests',
              '_dd_confidence_score',
            ]);
            const results = Object.entries(parsed)
              .filter(([k, v]) => !METADATA_KEYS.has(k) && typeof v === 'string')
              .map(([id, output]) => ({ id, name: id, status: 'success', output }));
            if (results.length > 0) {
              finalResult = {
                results,
                cursor_prompt: parsed.synthesizer_cursor_prompt || '',
                plan_md: parsed.synthesizer_plan_md || '',
                verifier_verdict: parsed.verifier_verdict || '',
                verifier_items: parsed.verifier_items || [],
                verifier_text: parsed.verifier || '',
              };
            }
          }
        } catch (e) {
          console.error('[Stream] Failed to parse final result JSON after delimiter', e);
        }
      }
      if (!finalResult && fullStream) {
        // console.log('[Stream] Last-ditch parse: scanning full stream for result object');
        const allLines = fullStream.split('\n').map((l) => l.replace(/^data:\s*/, '').trim()).filter((l) => l && l !== '[DONE]' && !l.startsWith('data: [DONE]'));
        for (let i = allLines.length - 1; i >= 0; i--) {
          let candidate = allLines[i];
          try {
            const obj = JSON.parse(candidate);
            if (obj.type === 'result' || obj.results || obj.final === true) {
              // console.log('[Stream] Last-ditch: found result at line', i);
              finalResult = {
                ...obj,
                cursor_prompt: obj.cursor_prompt || obj.synthesizer_output || '',
                plan_md: obj.plan_md || '',
                verifier_verdict: obj.verifier_verdict || '',
                verifier_items: obj.verifier_items || [],
                verifier_text: obj.verifier_text || obj.verifier || '',
              };
              break;
            }
          } catch (_) {
            if (candidate.includes('"type":"result"') || candidate.includes('"type": "result"') || candidate.includes('"final":true') || candidate.includes('"results"')) {
              for (let j = i + 1; j < allLines.length; j++) {
                candidate += '\n' + allLines[j];
                try {
                  const obj = JSON.parse(candidate);
                  if (obj.type === 'result' || obj.results || obj.final === true) {
                    // console.log('[Stream] Last-ditch: found split result at lines', i, '-', j);
                    finalResult = {
                      ...obj,
                      cursor_prompt: obj.cursor_prompt || obj.synthesizer_output || '',
                      plan_md: obj.plan_md || '',
                      verifier_verdict: obj.verifier_verdict || '',
                      verifier_items: obj.verifier_items || [],
                      verifier_text: obj.verifier_text || obj.verifier || '',
                    };
                    break;
                  }
                } catch (_) {}
              }
              if (finalResult) break;
            }
          }
        }
      }
      if (!finalResult) {
        const errDetail = `No result in stream (chunks=${chunkCount}, lines=${lineCount}). Buffer tail: ${buffer.slice(-200)}`;
        console.error('[Stream]', errDetail);
        throw new Error(errDetail);
      }
      const { type, ...rest } = finalResult;
      return rest;
    }
    return res.json().catch(() => ({}));
  }, []);

  const testLocalLlmConnection = useCallback(async () => {
    const baseUrl = normalizeLocalBaseUrl(localLlmUrl);
    setLocalLlmConnectionStatus('testing');
    try {
      const res = await fetch(`${baseUrl}/api/tags`, { method: 'GET' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setLocalLlmConnectionStatus('connected');
    } catch (_err) {
      setLocalLlmConnectionStatus('unreachable');
    }
  }, [localLlmUrl]);

  const runCouncil = async () => {
    const runId = `run_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    setError(null);
    setResult(null);
    setAbResults(null);
    setAbExpandedRaw(false);
    setAbExpandedCouncil(false);
    setCurrentRunId(runId);
    streamAccumulatorRef.current = { [runId]: {} };
    skepticRef.current = '';
    streamQueueRef.current = [];
    setStreamingContent({ [runId]: {} });
    setPipelineStatus({});
    setLoading(true);
    abortControllerRef.current = new AbortController();
    try {
      localStorage.setItem(STORAGE_KEYS.activeRun, JSON.stringify({ runId, startedAt: Date.now() }));
    } catch {
      /* ignore */
    }

    const hasO = !!openaiKey?.trim();
    const hasA = !!anthropicKey?.trim();
    const hasG = !!geminiKey?.trim();
    const hasT = !!tavilyKey?.trim();
    const allLocal = pipeline.every((a) => isLocalAgent(a, localLlmUrl));
    setRunMode(allLocal ? 'local' : 'cloud');

    const needsOpenAI = pipeline.some((node) => {
      if (isLocalAgent(node, localLlmUrl)) return false;
      const prov = node.provider || getProviderFromModel(node.model);
      return prov === 'openai';
    });
    if (needsOpenAI && !hasO) {
      const msg = 'OpenAI API Key is required to run this pipeline';
      window.alert(msg);
      setError(msg);
      setSidebarOpen(true);
      setApiKeysOpen(true);
      setLoading(false);
      setRunMode('cloud');
      return;
    }

    const needsSearch = pipeline.some((node) => {
      if (isLocalAgent(node, localLlmUrl)) return false;
      const prov = node.provider || getProviderFromModel(node.model);
      return prov === 'search';
    });
    if (needsSearch && !hasT) {
      const msg = 'Tavily API Key is required for Deep Web Research';
      window.alert(msg);
      setError(msg);
      setSidebarOpen(true);
      setApiKeysOpen(true);
      setLoading(false);
      setRunMode('cloud');
      return;
    }

    const missingKeys = [];
    for (const node of pipeline) {
      if (isLocalAgent(node, localLlmUrl)) continue;
      const prov = node.provider || getProviderFromModel(node.model);
      if (prov === 'anthropic' && !hasA) missingKeys.push('Anthropic');
      if (prov === 'gemini' && !hasG) missingKeys.push('Gemini');
      if (prov === 'search' && !hasT) missingKeys.push('Tavily');
    }
    const uniqueMissing = [...new Set(missingKeys)];
    if (uniqueMissing.length > 0) {
      setError(`Missing API key${uniqueMissing.length > 1 ? 's' : ''}: ${uniqueMissing.join(', ')}. Add ${uniqueMissing.length > 1 ? 'them' : 'it'} in Configure API Keys.`);
      setSidebarOpen(true);
      setApiKeysOpen(true);
      setLoading(false);
      setRunMode('cloud');
      return;
    }

    const initPipelineStatus = (agents) => {
      const status = {};
      agents.forEach((a) => { status[a.id] = { status: 'pending' }; });
      setPipelineStatus(status);
    };

    const handleProgress = (agentId, status, message, name) => {
      setPipelineStatus((prev) => ({
        ...prev,
        [agentId]: {
          ...prev[agentId],
          status: status === 'error' ? 'error' : status === 'retrying' ? 'retrying' : status,
          message: status === 'error' ? (message || 'Unknown error') : status === 'retrying' ? (message || '') : prev[agentId]?.message || '',
          name: (name && String(name).trim()) || prev[agentId]?.name || '',
        },
      }));
    };

    const handleContent = (agentId, content, contentRunId) => {
      if (!content) return;
      streamQueueRef.current.push({ runId: contentRunId ?? runId, id: agentId, text: content });
    };

    const handleMetadata = () => {}; /* Cost estimator removed - inaccurate for multi-model workflows */

    try {
      if (false && abCompareEnabled) { /* A/B disabled */
        const rawModel = getRawCodingModel(hasO, hasA, hasG);
        const rawPipeline = [{ id: 'raw', name: 'Raw', systemPrompt: RAW_CODING_AGENT_SYSTEM, model: rawModel }];

        const rawPrompt = (() => {
          const base = `Feature request:\n${idea}\n\n`;
          return (codeFiles.length + imageFiles.length) > 0 ? base + 'Project files: [attached]\n\n' : base;
        })();

        let rawData = null;
        let rawError = null;
        try {
          initPipelineStatus(rawPipeline);
          console.debug({ event: 'ab_sent', branch: 'raw', promptPreview: councilSafePreview(rawPrompt), tokensEstimate: estimateTokens(rawPrompt) });
          rawData = await callCouncilApi(buildCouncilFormData(rawPipeline, undefined, undefined, runId), handleProgress, handleContent, handleMetadata, undefined, runId);
        } catch (e) {
          rawError = e.message || 'Raw flow failed';
        }

        let councilData = null;
        let councilError = null;
        try {
          initPipelineStatus(DEFAULT_PIPELINE);
          councilData = await callCouncilApi(buildCouncilFormData([], undefined, undefined, runId), handleProgress, handleContent, handleMetadata, undefined, runId);
        } catch (e) {
          councilError = e.message || 'Pipeline flow failed';
        }

        let councilFinalPrompt = '';
        if (councilData) {
          if (councilData.cursor_prompt && String(councilData.cursor_prompt).trim()) {
            councilFinalPrompt = councilData.cursor_prompt.trim();
          } else {
            const planMd = councilData.plan_md ?? '';
            councilFinalPrompt = planMd ? `Synthesizer fallback:\n\n${planMd}` : '';
            if (!councilFinalPrompt) {
              console.warn('A/B Council: cursor_prompt absent/empty, fallback to plan_md also empty');
            } else {
              console.warn('A/B Council: cursor_prompt absent/empty, using plan_md fallback');
            }
          }
        }

        let councilCodingData = null;
        if (councilFinalPrompt && !councilError) {
          try {
            initPipelineStatus(rawPipeline);
            console.debug({ event: 'ab_sent', branch: 'council', promptPreview: councilSafePreview(councilFinalPrompt), tokensEstimate: estimateTokens(councilFinalPrompt) });
            councilCodingData = await callCouncilApi(buildCouncilFormData(rawPipeline, councilFinalPrompt, undefined, runId), handleProgress, handleContent, handleMetadata, undefined, runId);
          } catch (e) {
            councilError = councilError || (e.message || 'Pipeline coding step failed');
          }
        } else if (!councilFinalPrompt || councilError) {
          console.warn('A/B Council: pipeline failed or cursor_prompt empty, skipping Council coding step');
        }

        const rawOutput = rawData?.results?.[0]?.status === 'success' ? rawData.results[0].output : '';
        const councilOutput = councilCodingData?.results?.[0]?.status === 'success' ? councilCodingData.results[0].output : '';
        const councilPromptForDisplay = councilFinalPrompt || '(Pipeline failed or produced empty prompt)';

        const fileNames = [...codeFiles, ...imageFiles].map((f) => f.name || f.file?.name || 'unknown').join(', ') || '(none)';
        const buildGraderPrompt = (originalPrompt, modelResponse) =>
          `## Original Prompt\n${originalPrompt}\n\n## Uploaded File Names\n${fileNames}\n\n## Model Response\n${modelResponse}`;

        const graderModel = getRawCodingModel(hasO, hasA, hasG);
        const judgeSkill = selectedJudge !== 'default' ? importedSkills.find((s) => s.id === selectedJudge) : null;
        const graderSystemPrompt = judgeSkill?.systemPrompt || promptsLibrary?.grader || GRADER_AGENT_SYSTEM_FALLBACK;
        const graderPipeline = [{ id: 'grader', name: 'Grader', systemPrompt: graderSystemPrompt, model: graderModel }];

        let rawGrade = null;
        let councilGrade = null;
        if (rawOutput && !rawError) {
          try {
            const graderData = await callCouncilApi(buildCouncilFormData(graderPipeline, buildGraderPrompt(rawPrompt, rawOutput), [], runId, undefined, true), handleProgress, handleContent, handleMetadata, undefined, runId);
            const graderResult = graderData?.results?.[0];
            const graderOutput = graderResult?.output ?? '';
            if (graderResult?.status === 'needs_review') {
              rawGrade = { needs_review: true, raw_output: graderOutput, grade: null };
            } else {
              rawGrade = parseGraderOutput(graderOutput);
            }
          } catch (e) {
            rawGrade = { score: 0, breakdown: { adherence: 0, technical_sophistication: 0, dx: 0, contextual_safety: 0 }, metrics: { hasConsoleLog: false, hasAria: false, todoCount: 0 }, hallucinations: [], reasoning: `Grader failed: ${e.message}` };
          }
        }
        if (councilOutput && !councilError) {
          try {
            const graderData = await callCouncilApi(buildCouncilFormData(graderPipeline, buildGraderPrompt(councilPromptForDisplay, councilOutput), [], runId, undefined, true), handleProgress, handleContent, handleMetadata, undefined, runId);
            const graderResult = graderData?.results?.[0];
            const graderOutput = graderResult?.output ?? '';
            if (graderResult?.status === 'needs_review') {
              councilGrade = { needs_review: true, raw_output: graderOutput, grade: null };
            } else {
              councilGrade = parseGraderOutput(graderOutput);
            }
          } catch (e) {
            councilGrade = { score: 0, breakdown: { adherence: 0, technical_sophistication: 0, dx: 0, contextual_safety: 0 }, metrics: { hasConsoleLog: false, hasAria: false, todoCount: 0 }, hallucinations: [], reasoning: `Grader failed: ${e.message}` };
          }
        }

        const rawTokens = estimateTokens(rawPrompt) + estimateTokens(rawOutput);
        const councilTokens = estimateTokens(councilFinalPrompt) + estimateTokens(councilOutput);

        const rawHeuristics = computeQualityHeuristics(rawOutput);

        setAbResults({
          raw: {
            prompt: rawPrompt,
            response: rawOutput,
            error: rawError,
            tokens: rawTokens,
            heuristics: rawHeuristics,
            grade: rawGrade,
            cost: rawTokens * abCostPerToken,
          },
          council: {
            prompt: councilPromptForDisplay,
            response: councilOutput,
            error: councilError,
            tokens: councilTokens,
            heuristics: computeQualityHeuristics(councilOutput),
            grade: councilGrade,
            cost: councilTokens * abCostPerToken,
          },
        });

        if (councilData) {
          const councilIdeaToSave = (typeof idea === 'string' && idea.trim())
            ? idea.trim()
            : (ideaRef?.current?.value?.trim() || '');
          const normalized = {
            ...councilData,
            cursor_prompt: councilData.cursor_prompt ?? '',
            plan_md: councilData.plan_md ?? '',
            architect_plan: finalizeText(councilData.architect_plan ?? ''),
            skeptic_text: finalizeText(councilData.skeptic_text ?? ''),
          };
          if (Array.isArray(normalized.results)) {
            normalized.results = normalized.results.map((r) => ({
              ...r,
              output: finalizeText(r.output),
            }));
          }
          setResult(normalized);
          const councilGraderResult = normalized.results?.find((r) =>
            r.name?.toLowerCase().includes('grader') || r.name?.toLowerCase().includes('judge')
          );
          const councilParsedGrader = councilGraderResult?.output ? parseGraderOutput(councilGraderResult.output) : null;
          const councilGraderScore = isAdversarialPipeline(pipeline)
            ? extractAdversarialScore(normalized.results)
            : (councilParsedGrader?.score ?? null);
          const councilAllTemplates = [...BUILTIN_TEMPLATES, ...templates];
          const councilMatchedTemplate = councilAllTemplates.find(
            (t) =>
              t.pipeline?.length === pipeline.length &&
              t.pipeline.every((p, i) => p.name === pipeline[i]?.name)
          );
          let councilPipelineName = councilMatchedTemplate?.name ?? (pipeline.length === 3 ? 'Coder\'s Council' : 'Custom Pipeline');
          if (isAdversarialPipeline(pipeline) && !councilMatchedTemplate) councilPipelineName = 'Adversarial Reasoning';
          saveToHistory({
            idea: councilIdeaToSave,
            timestamp: Date.now(),
            cursor_prompt: normalized.cursor_prompt,
            plan_md: normalized.plan_md,
            architect_plan: normalized.architect_plan,
            skeptic_text: normalized.skeptic_text,
            results: normalized.results,
            agentCount: pipeline.length,
            models: [...new Set(pipeline.map((p) => p.model).filter(Boolean))],
            graderScore: councilGraderScore,
            pipelineName: councilPipelineName,
          });
        }
        if (rawError && councilError) {
          setError('Both Raw and Pipeline flows failed. See comparison panel for details.');
        } else if (councilError && !rawError) {
          setError('Pipeline failed to synthesize a final prompt — showing Raw results.');
        }
      } else {
        initPipelineStatus(displayWorkflow);
        // Set first agent to active immediately so UI feels instant
        if (displayWorkflow.length > 0) {
          setPipelineStatus((prev) => ({
            ...prev,
            [displayWorkflow[0].id]: { ...prev[displayWorkflow[0].id], status: 'active' },
          }));
        }
        let data;
        if (allLocal) {
          data = await runLocalPipeline(
            pipeline,
            idea,
            localLlmUrl,
            localLlmModel,
            runId,
            handleProgress,
            handleContent
          );
        } else {
          // Run client-side code agents before cloud execution
          // Collect code outputs to inject into the idea context
          const codeAgentOutputs = [];
          const codeOnlyResults = [];
          for (const agent of pipeline) {
            if (agent.code && agent.code.trim()) {
              const codeResult = await runCodeAgent(agent.code, {
                idea,
                previousOutputs: Object.fromEntries(
                  codeAgentOutputs.map(r => [r.name, r.output])
                ),
              });
              if (codeResult?.skipLLM) {
                // This agent is code-only, record its result
                codeOnlyResults.push({
                  id: agent.id,
                  name: agent.name,
                  status: 'success',
                  output: codeResult.output,
                  codeOnly: true,
                });
                codeAgentOutputs.push({ name: agent.name, output: codeResult.output });
                // Stream the code output for this agent
                handleProgress(agent.id, 'active', '', agent.name);
                handleContent(agent.id, codeResult.output, runId);
                handleProgress(agent.id, 'completed', '', agent.name);
              } else if (codeResult?.output) {
                // Code ran, output will be injected into context for cloud agent
                codeAgentOutputs.push({ name: agent.name, output: codeResult.output });
              }
            }
          }

          // Build augmented idea with code agent outputs
          let augmentedIdea = idea;
          const codeInjections = codeAgentOutputs
            .filter(co => !codeOnlyResults.some(cr => cr.name === co.name))
            .map(co => `[Live Data from ${co.name} Code Agent]:\n${co.output}`)
            .join('\n\n');
          if (codeInjections) {
            augmentedIdea = `${idea}\n\n${codeInjections}`;
          }

          data = await callCouncilApi(
            buildCouncilFormData(undefined, augmentedIdea, undefined, runId, undefined),
            handleProgress,
            handleContent,
            handleMetadata,
            setStreamingContent,
            runId,
            pipeline
          );

          // Merge code-only results with cloud results
          if (codeOnlyResults.length > 0 && data.results) {
            // Insert code-only results at their proper positions
            const mergedResults = [];
            for (const agent of pipeline) {
              const codeOnly = codeOnlyResults.find(r => r.id === agent.id);
              if (codeOnly) {
                mergedResults.push(codeOnly);
              } else {
                const cloudResult = data.results.find(r => r.id === agent.id);
                if (cloudResult) {
                  mergedResults.push(cloudResult);
                }
              }
            }
            // Add any remaining cloud results not in pipeline order
            for (const r of data.results) {
              if (!mergedResults.some(m => m.id === r.id)) {
                mergedResults.push(r);
              }
            }
            data.results = mergedResults;
          }
        }
        let normalized = data;
        if (Array.isArray(data.results) && data.results.length > 0) {
          const synthResult =
            data.results.find((r) => (r.id || '').split('-')[0] === 'synthesizer' && r.status === 'success') ||
            data.results.slice().reverse().find((r) => (r.id || '').split('-')[0] !== 'verifier' && r.status === 'success');
          const parsed = synthResult?.output
            ? parseSynthesizerOutput(synthResult.output)
            : { cursor_prompt: '', plan_md: '' };
          normalized = {
            ...data,
            cursor_prompt: data.cursor_prompt || data.synthesizer_output || data.code || data.content || parsed.cursor_prompt || '',
            plan_md: data.plan_md || parsed.plan_md || '',
            architect_plan: data.architect_plan || (data.results[0]?.status === 'success' ? data.results[0].output : ''),
          };
        } else {
          normalized = {
            ...data,
            cursor_prompt: data.cursor_prompt || data.synthesizer_output || data.code || data.content || '',
            plan_md: data.plan_md || '',
          };
        }

        if (!normalized.cursor_prompt || !String(normalized.cursor_prompt).trim()) {
          if (!isAdversarialPipeline(pipeline)) {
            const raw = data.summary || data.content || '';
            normalized.cursor_prompt = raw ? String(raw).slice(0, 5000) : 'No prompt data found';
          }
        }
        normalized.cursor_prompt = stripDuplicateModeTags(normalized.cursor_prompt || '') || normalized.cursor_prompt;
        normalized.plan_md = stripDuplicateModeTags(normalized.plan_md || '') || normalized.plan_md;
        const streamBuf = streamAccumulatorRef.current[runId] || {};
        normalized.architect_plan = finalizeText(streamBuf.architect || normalized.architect_plan);
        normalized.skeptic_text = finalizeText(skepticRef.current || normalized.skeptic_text);
        // Use only the final results payload, not the stream buffer (avoids duplication)
        const finalAgentResults = data.results || [];
        const deduped = finalAgentResults.filter((r, i, arr) =>
          arr.findIndex(x => x.id === r.id && x.name === r.name) === i
        );
        if (deduped.length > 0) {
          normalized.results = deduped.map((r) => ({
            ...r,
            name:
              r.name && r.name !== 'Custom' && r.name !== 'custom' && r.name !== r.id
                ? r.name
                : workflow.find((w) => w.id === r.id)?.name || pipeline.find((p) => p.id === r.id)?.name || r.id || 'Agent',
            output: finalizeText(r.output),
          }));
        }
        const lastAgent = displayWorkflow[displayWorkflow.length - 1];
        const synthId = lastAgent?.id;
        setDeliberationStatus('The Synthesizer is reconciling the debate...');
        if (synthId) {
          setPipelineStatus((prev) => ({ ...prev, [synthId]: { status: 'active' } }));
          const acc = streamAccumulatorRef.current;
          const current = (acc[runId] || {})[synthId] || '';
          const thought = '\n\nFinalizing the Agent-Ready Prompt...';
          streamAccumulatorRef.current = { ...acc, [runId]: { ...(acc[runId] || {}), [synthId]: current + thought } };
          setStreamingContent(() => {
            const snapshot = {};
            for (const r of Object.keys(streamAccumulatorRef.current)) {
              snapshot[r] = { ...streamAccumulatorRef.current[r] };
            }
            return snapshot;
          });
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
        setDeliberationStatus(null);
        if (synthId) {
          setPipelineStatus((prev) => ({ ...prev, [synthId]: { status: 'completed' } }));
        }
        setResult((prev) => {
          const hasValidPlan = normalized.plan_md && String(normalized.plan_md).trim().length > 0;
          const hasValidPrompt = normalized.cursor_prompt && String(normalized.cursor_prompt).trim().length > 0;
          if (prev?.plan_md && !hasValidPlan) {
            console.warn('Prevented empty plan_md from overwriting existing state.');
            return { ...prev, ...normalized, plan_md: prev.plan_md };
          }
          if (prev?.cursor_prompt && !hasValidPrompt) {
            console.warn('Prevented empty cursor_prompt from overwriting existing state.');
            return { ...prev, ...normalized, cursor_prompt: prev.cursor_prompt };
          }
          return normalized;
        });
        const ideaToSave = (typeof idea === 'string' && idea.trim())
          ? idea.trim()
          : (ideaRef?.current?.value?.trim() || '');
        const allTemplates = [...BUILTIN_TEMPLATES, ...templates];

        // For adversarial pipelines, extract confidence from Judge output and skip normal grader
        if (isAdversarialPipeline(pipeline)) {
          console.log('[SAVE DEBUG] idea value:', JSON.stringify(idea), 'ideaRef:', ideaRef?.current?.value?.slice(0, 50));
          const promptText = (typeof idea === 'string' && idea.trim())
            ? idea.trim()
            : (ideaRef?.current?.value?.trim() || 'No prompt recorded');
          const historyScore = extractAdversarialScore(normalized.results);
          const allTpl = [...BUILTIN_TEMPLATES, ...templates];
          const matchedTpl = allTpl.find(
            (t) => t.pipeline?.length === pipeline.length && t.pipeline.every((p, i) => p.name === pipeline[i]?.name)
          );
          const pipelineName = matchedTpl?.name ?? 'Adversarial Reasoning';
          saveToHistory({
            idea: promptText,
            timestamp: Date.now(),
            cursor_prompt: normalized.cursor_prompt ?? '',
            plan_md: normalized.plan_md ?? '',
            architect_plan: finalizeText(normalized.architect_plan),
            skeptic_text: finalizeText(normalized.skeptic_text),
            results: Array.isArray(normalized.results)
              ? normalized.results.map((r) => ({ ...r, output: finalizeText(r.output) }))
              : normalized.results,
            agentCount: pipeline.length,
            models: [...new Set(pipeline.map((p) => p.model).filter(Boolean))],
            graderScore: historyScore,
            pipelineName,
          });
          const runEntry = {
            id: runId,
            timestamp: Date.now(),
            idea: promptText,
            workflow: workflow.map((w) => ({ ...w })),
            plan_md: normalized.plan_md ?? '',
            cursor_prompt: normalized.cursor_prompt ?? '',
            templateName: selectedTemplate,
            results: Array.isArray(normalized.results) ? normalized.results : undefined,
            agentCount: pipeline.length,
            models: [...new Set(pipeline.map((p) => p.model).filter(Boolean))],
            graderScore: historyScore,
            pipelineName,
          };
          if (!promptText) return;
          setRunHistory((prev) => {
            const next = [runEntry, ...prev].slice(0, 50);
            try {
              localStorage.setItem(STORAGE_KEYS.runHistory, JSON.stringify(next));
            } catch (e) {
              console.warn('Run history save failed:', e);
            }
            return next;
          });
          return;
        }

        const graderResult = normalized.results?.find((r) =>
          r.name?.toLowerCase().includes('grader') || r.name?.toLowerCase().includes('judge')
        );
        const parsedGrader = graderResult?.output ? parseGraderOutput(graderResult.output) : null;
        const graderScore = parsedGrader?.score ?? null;
        const matchedTemplate = allTemplates.find(
          (t) =>
            t.pipeline?.length === pipeline.length &&
            t.pipeline.every((p, i) => p.name === pipeline[i]?.name)
        );
        const pipelineName = matchedTemplate?.name ?? (pipeline.length === 3 ? 'Coder\'s Council' : 'Custom Pipeline');

        saveToHistory({
          idea: ideaToSave,
          timestamp: Date.now(),
          cursor_prompt: normalized.cursor_prompt ?? '',
          plan_md: normalized.plan_md ?? '',
          architect_plan: finalizeText(normalized.architect_plan),
          skeptic_text: finalizeText(normalized.skeptic_text),
          results: Array.isArray(normalized.results)
            ? normalized.results.map((r) => ({ ...r, output: finalizeText(r.output) }))
            : normalized.results,
          agentCount: pipeline.length,
          models: [...new Set(pipeline.map((p) => p.model).filter(Boolean))],
          graderScore,
          pipelineName,
        });
        const runEntry = {
          id: runId,
          timestamp: Date.now(),
          idea: ideaToSave,
          workflow: workflow.map((w) => ({ ...w })),
          plan_md: normalized.plan_md ?? '',
          cursor_prompt: normalized.cursor_prompt ?? '',
          templateName: selectedTemplate,
          results: Array.isArray(normalized.results) ? normalized.results : undefined,
          agentCount: pipeline.length,
          models: [...new Set(pipeline.map((p) => p.model).filter(Boolean))],
          graderScore,
          pipelineName,
        };
        if (!ideaToSave) return; // don't save runs with no prompt
        setRunHistory((prev) => {
          const next = [runEntry, ...prev].slice(0, 50);
          try {
            localStorage.setItem(STORAGE_KEYS.runHistory, JSON.stringify(next));
          } catch (e) {
            console.warn('Run history save failed:', e);
          }
          return next;
        });
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Something went wrong');
      }
    } finally {
      setDeliberationStatus(null);
      setLoading(false);
      setRunMode('cloud');
      abortControllerRef.current = null;
      try {
        localStorage.removeItem(STORAGE_KEYS.activeRun);
      } catch {
        /* ignore */
      }
    }
  };

  const cancelCouncil = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        if (!loading && idea.trim()) runCouncil();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, idea]);

  const pipelineRef = useRef(null);
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleAgentModalSave = (data, afterIndex, editingId) => {
    if (editingId) {
      setPipeline((prev) => prev.map((p) => (p.id === editingId ? { ...p, ...data } : p)));
    } else {
      const newId = `custom-${Date.now()}`;
      const newNode = { id: newId, ...data };
      setPipeline((prev) => {
        const idx = afterIndex + 1;
        return [...prev.slice(0, idx), newNode, ...prev.slice(idx)];
      });
      setWorkflow((prev) => {
        const newWorkflowNode = {
          id: newId,
          name: newNode.name,
          provider: newNode.provider || getProviderFromModel(newNode.model),
          systemPrompt: newNode.systemPrompt,
        tools: Array.isArray(newNode.tools) ? newNode.tools : [],
        code: newNode.code ?? null,
          parentIds: [],
        };
        const idx = afterIndex + 1;
        const inserted = [...prev.slice(0, idx), newWorkflowNode, ...prev.slice(idx)];
        return recalculateParentIdsFromOrder(inserted);
      });
    }
    setAgentModal(null);
  };

  const insertAgentAt = (index, agent) => {
    const newId = `${agent.id || 'custom'}-${Date.now()}`;
    const newNode = {
      id: newId,
      name: agent.name,
      systemPrompt: agent.systemPrompt,
      model: agent.model || 'claude-3-haiku',
      provider: agent.provider || getProviderFromModel(agent.model),
      tools: Array.isArray(agent.tools) ? agent.tools : [],
      code: agent.code ?? null,
      ...(agent.intensity != null && { intensity: agent.intensity }),
    };
    const newPipeline = [...pipeline.slice(0, index), newNode, ...pipeline.slice(index)];
    const newWorkflowNode = {
      id: newId,
      name: newNode.name,
      provider: newNode.provider,
      systemPrompt: newNode.systemPrompt,
      tools: Array.isArray(newNode.tools) ? newNode.tools : [],
      code: newNode.code ?? null,
      parentIds: [],
    };
    const orderedWorkflow = newPipeline
      .map((p) => (p.id === newId ? newWorkflowNode : workflow.find((w) => w.id === p.id)))
      .filter(Boolean);
    const newWorkflow = recalculateParentIdsFromOrder(orderedWorkflow);
    setPipeline(newPipeline);
    setWorkflow(newWorkflow);
    setAgentModal(null);
  };

  const handleAddGuardrail = (template, afterIndex) => {
    insertAgentAt(afterIndex + 1, template);
  };

  const handleAddSkill = (skill, afterIndex) => {
    const model = skill.recommendedModel || 'gpt-4o';
    const newNode = {
      id: skill.id,
      name: skill.name,
      systemPrompt: skill.systemPrompt,
      model,
      provider: getProviderFromModel(model),
      tools: Array.isArray(skill.tools) ? skill.tools : [],
      code: null,
      ...(skill.intensity != null && { intensity: skill.intensity }),
    };
    insertAgentAt(afterIndex + 1, newNode);
  };

  const savePipelineTemplate = (name, overwriteId) => {
    const trimmed = typeof name === 'string' ? name.trim() : '';
    if (!trimmed) return;
    const existing = templates.find((t) => t.name.toLowerCase() === trimmed.toLowerCase());
    if (existing && !overwriteId) {
      setAgentModal({ mode: 'confirm-template-overwrite', templateName: trimmed, existingId: existing.id });
      return;
    }
    const id = overwriteId || `template-${Date.now()}`;
    const createdAt = existing && overwriteId ? existing.createdAt : Date.now();
    const template = { id, name: trimmed, createdAt, pipeline: pipeline.map((p) => ({ ...p })) };
    setTemplates((prev) => {
      if (overwriteId) {
        return prev.map((t) => (t.id === overwriteId ? template : t));
      }
      return [...prev, template];
    });
    setAgentModal(null);
  };

  const applyTemplateToPipeline = (template) => {
    const raw = migratePipeline(template.pipeline) || template.pipeline;
    if (!raw || raw.length === 0) return;
    const ts = Date.now();
    // Build ID remap so pre-defined parentIds can be updated to new timestamped IDs
    const idRemap = {};
    raw.forEach((p, i) => { idRemap[p.id] = `${p.id}-${ts}-${i}`; });
    const hasPreDefinedParentIds = raw.some((p) => Array.isArray(p.parentIds));
    const pipelineWithUniqueIds = raw.map((p) => ({
      ...p,
      id: idRemap[p.id],
    }));
    const workflowItems = pipelineWithUniqueIds.map((p, i) => ({
      ...p,
      provider: p.provider || getProviderFromModel(p.model),
      // Remap original parentIds to new timestamped IDs when template defines DAG structure
      ...(hasPreDefinedParentIds && {
        parentIds: Array.isArray(raw[i].parentIds)
          ? raw[i].parentIds.map((pid) => idRemap[pid]).filter(Boolean)
          : [],
      }),
    }));
    const wired = hasPreDefinedParentIds ? workflowItems : recalculateParentIdsFromOrder(workflowItems);
    setWorkflow(wired);
    setPipeline(wired.map((w) => ({
      id: w.id,
      name: w.name,
      description: w.description,
      systemPrompt: w.systemPrompt,
      model: w.model || 'gpt-4o',
      provider: w.provider || getProviderFromModel(w.model),
      tools: Array.isArray(w.tools) ? w.tools : [],
      code: w.code ?? null,
    })));
    setSelectedTemplate(template?.name || 'Custom');
    setPipelineSelectorOpen(null);
    setAgentModal(null);
  };

  const loadPipelineTemplate = (template) => {
    if (!template || !Array.isArray(template.pipeline) || template.pipeline.length === 0) return;
    if (isPipelineModifiedFromDefault(pipeline)) {
      setAgentModal({ mode: 'load-templates', pendingTemplate: template });
      return;
    }
    applyTemplateToPipeline(template);
    setAgentModal(null);
  };

  const confirmLoadPipelineTemplate = () => {
    const pending = agentModal?.pendingTemplate;
    if (!pending?.pipeline) return;
    applyTemplateToPipeline(pending);
    setAgentModal(null);
  };

  const deletePipelineTemplate = (id) => {
    setTemplates((prev) => prev.filter((t) => t.id !== id));
    setAgentModal(null);
  };

  const confirmLoadSharedPipeline = () => {
    const shared = agentModal?.sharedPipeline;
    if (!shared || !Array.isArray(shared)) return;
    setPipeline(migratePipeline(shared) || (shared || []).map((p) => ({
      ...p,
      tools: Array.isArray(p.tools) ? p.tools : [],
      code: p.code ?? null,
    })));
    setAgentModal(null);
    window.history.replaceState({}, '', window.location.pathname + window.location.search);
  };

  const openAddAgentModalAtIndex = (insertIndex) => {
    try {
      localStorage.setItem(STORAGE_KEYS.seenInsertHint, '1');
    } catch {
      /* ignore */
    }
    setShowInsertHint(false);
    setAgentModal({ mode: 'template-picker', afterIndex: insertIndex - 1 });
  };

  const dismissSharedPipeline = () => {
    setAgentModal(null);
    window.history.replaceState({}, '', window.location.pathname + window.location.search);
  };

  const handleWorkflowEditorSave = (newWorkflow) => {
    setWorkflow(newWorkflow);
    const newPipeline = newWorkflow.map((w) => {
      const existing = pipeline.find((p) => p.id === w.id);
      return existing || {
        id: w.id,
        name: w.name,
        systemPrompt: w.systemPrompt || '',
        model: w.model || 'gpt-4o',
        tools: Array.isArray(w.tools) ? w.tools : [],
        code: w.code ?? null,
      };
    });
    setPipeline(newPipeline);
    setWorkflowEditorOpen(false);
  };

  useEffect(() => {
    if (!pipelineSelectorOpen) return;
    const close = (e) => {
      if (pipelineRef.current && !pipelineRef.current.contains(e.target)) {
        setPipelineSelectorOpen(null);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setPipelineSelectorOpen(null);
    };
    document.addEventListener('click', close);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [pipelineSelectorOpen]);

  return (
    <div className="app-wrapper">
      <div className={`App ${isProcessing ? 'is-split' : ''}`}>
      <header className="header">
        <div className="header-top">
          <button
            type="button"
            className="sidebar-toggle"
            onClick={() => setSidebarOpen((o) => !o)}
            aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
            aria-expanded={sidebarOpen}
          >
            <HamburgerIcon />
          </button>
          <div className="header-title-block">
            <h1>Viqo</h1>
            <p className="subtitle">Turn feature requests into agent-ready implementation plans</p>
            <p className="subtitle-secondary">Architect plans it. Skeptic tears it apart. Synthesizer writes the spec.</p>
          </div>
          <div className="header-actions">
            {loading && (
              <button
                type="button"
                className="cancel-button header-cancel-btn"
                onClick={cancelCouncil}
                aria-label="Cancel deliberation"
              >
                Cancel
              </button>
            )}
            <div className="header-run-wrapper">
              <button
                type="button"
                className={`header-run-btn run-button ${!loading && idea.trim() ? 'run-button-pulse' : ''}`}
                onClick={runCouncil}
                disabled={loading || !idea.trim()}
                aria-busy={loading}
              >
                {loading ? 'Running...' : 'Run Pipeline'}
              </button>
              {loading && runMode === 'local' && (
                <span className="cost-estimate">Running locally</span>
              )}
              {(() => {
                const costRange = estimateRunCostRange(pipeline, idea);
                const fmt = (v) => (v < 0.01 ? '<$0.01' : `$${v.toFixed(2)}`);
                return !allLocalPipeline && costRange !== null && idea.trim() && (
                  <span className="cost-estimate">
                    ~{fmt(costRange.min)}–{fmt(costRange.max)} est.
                  </span>
                );
              })()}
            </div>
            <button
              type="button"
              className="header-history-btn"
              onClick={() => setSidebarOpen(true)}
              aria-label="View run history"
            >
              History
            </button>
          </div>
        </div>
      </header>

      {pipeline.some((a) => Array.isArray(a.tools) && a.tools.includes('web_search')) && !tavilyKey && (
        <div className="web-search-warning">
          ⚠️ Web search is enabled but no Tavily API key is configured. Agents may fall back to outdated training data instead of real-time results.
          <button type="button" onClick={() => setSidebarOpen(true)}>Add Tavily key →</button>
        </div>
      )}

      <div
        className={`sidebar-overlay ${sidebarOpen ? 'sidebar-overlay--open' : ''}`}
        style={sidebarOpen ? { pointerEvents: 'auto', zIndex: 10001 } : undefined}
        onClick={(e) => {
          const targetIsCurrent = e.target === e.currentTarget;
          if (typeof console !== 'undefined' && console.log) console.log('backdrop click - targetIsCurrent:', targetIsCurrent);
          if (targetIsCurrent) setSidebarOpen(false);
        }}
        aria-hidden={!sidebarOpen}
      />
      <aside className={`sidebar ${sidebarOpen ? 'sidebar--open' : ''}`} aria-label="Settings and history" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="sidebar-close-btn"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close sidebar"
        >
          ✕
        </button>
        <div className="sidebar-section sidebar-section--collapsible">
          <button
            type="button"
            className="sidebar-section-toggle"
            onClick={() => setApiKeysOpen((o) => !o)}
            aria-expanded={apiKeysOpen}
            aria-controls="sidebar-api-keys"
          >
            <h3>Configure API Keys</h3>
            <span className="sidebar-section-chevron" aria-hidden="true">{apiKeysOpen ? '▼' : '▶'}</span>
          </button>
          <div id="sidebar-api-keys" className={`sidebar-collapsible-content ${apiKeysOpen ? 'sidebar-collapsible-content--open' : ''}`} aria-hidden={!apiKeysOpen}>
          <p className="helper-trust">🔒 Your API keys are stored locally in your browser and are never saved to our servers.</p>
          <div className="key-inputs">
            <div className="api-key-field">
              <span className="api-key-label">OpenAI</span>
              <input
                type="password"
                placeholder="sk-..."
                value={openaiKey}
                onChange={(e) => setOpenaiKey(e.target.value)}
                disabled={loading}
                aria-label="OpenAI API Key"
              />
            </div>
            <div className="api-key-field">
              <span className="api-key-label">Anthropic</span>
              <input
                type="password"
                placeholder="sk-ant-..."
                value={anthropicKey}
                onChange={(e) => setAnthropicKey(e.target.value)}
                disabled={loading}
                aria-label="Anthropic API Key"
              />
            </div>
            <div className="api-key-field">
              <span className="api-key-label">Gemini</span>
              <input
                type="password"
                placeholder="Gemini API Key"
                value={geminiKey}
                onChange={(e) => {
                  const clean = sanitizeKey(e.target.value);
                  setGeminiKey(clean);
                  if (clean) localStorage.setItem(STORAGE_KEYS.gemini, clean);
                  else try { localStorage.removeItem(STORAGE_KEYS.gemini); } catch { /* ignore */ }
                }}
                disabled={loading}
                aria-label="Gemini API Key"
              />
            </div>
            <div className="api-key-field">
              <span className="api-key-label">Tavily</span>
              <input
                type="password"
                placeholder="tvly-... (for Deep Web Research)"
                value={tavilyKey}
                onChange={(e) => setTavilyKey(e.target.value)}
                disabled={loading}
                aria-label="Tavily API Key"
              />
            </div>
          </div>
          <div className="local-llm">
            <label>Local LLM URL (optional)</label>
            <input
              type="url"
              placeholder="http://localhost:11434"
              value={localLlmUrl}
              onChange={(e) => {
                setLocalLlmUrl(e.target.value);
                if (localLlmConnectionStatus !== 'idle') setLocalLlmConnectionStatus('idle');
              }}
              disabled={loading}
              className="local-llm-url"
              aria-label="Local LLM URL"
            />
            <div className="local-llm-test-row">
              <button
                type="button"
                className="history-clear-btn"
                onClick={testLocalLlmConnection}
                disabled={loading || localLlmConnectionStatus === 'testing'}
              >
                {localLlmConnectionStatus === 'testing' ? 'Testing...' : 'Test connection'}
              </button>
              {localLlmConnectionStatus === 'connected' && (
                <span className="history-meta-chip score-high">Connected</span>
              )}
              {localLlmConnectionStatus === 'unreachable' && (
                <span className="history-meta-chip score-low">Unreachable</span>
              )}
            </div>
            {localLlmUrl?.trim() && (
              <input
                type="text"
                placeholder="Model (e.g. llama3)"
                value={localLlmModel}
                onChange={(e) => setLocalLlmModel(e.target.value)}
                disabled={loading}
                className="local-llm-model"
                aria-label="Local LLM model"
              />
            )}
          </div>
          </div>
        </div>
        <div className="sidebar-section sidebar-section--collapsible">
          <button
            type="button"
            className="sidebar-section-toggle"
            onClick={() => setIsHistoryOpen((o) => !o)}
            aria-expanded={isHistoryOpen}
            aria-controls="sidebar-run-history"
          >
            <h3>Run History</h3>
            <span className="sidebar-section-chevron" aria-hidden="true">{isHistoryOpen ? '▼' : '▶'}</span>
          </button>
          <div id="sidebar-run-history" className={`sidebar-collapsible-content ${isHistoryOpen ? 'sidebar-collapsible-content--open' : ''}`} aria-hidden={!isHistoryOpen}>
          <div className="history-header-row">
            <span className="history-title">Run History</span>
            {runHistory.length > 0 && (
              <button
                className="history-clear-btn"
                onClick={() => {
                  if (window.confirm('Clear all run history?')) {
                    setRunHistory([]);
                    localStorage.removeItem(STORAGE_KEYS.runHistory);
                  }
                }}
              >
                Clear all
              </button>
            )}
          </div>
          {runHistory.filter((entry) => {
            const val = entry.idea ?? entry.cursor_prompt ?? '';
            return (
              val &&
              String(val).trim() &&
              !String(val).startsWith('No prompt') &&
              String(val) !== 'No prompt data found'
            );
          }).length > 0 ? (
            <ul className="history-list run-history-list">
              {runHistory.filter((entry) => {
                const val = entry.idea ?? entry.cursor_prompt ?? '';
                return (
                  val &&
                  String(val).trim() &&
                  !String(val).startsWith('No prompt') &&
                  String(val) !== 'No prompt data found'
                );
              }).map((entry) => (
                <li key={entry.id} className="history-item run-history-item">
                  <button
                    type="button"
                    className="history-entry-btn"
                    onClick={() => loadFromRunHistory(entry)}
                  >
                    <span className="run-history-idea">{entry.idea ? (entry.idea.length > 100 ? `${entry.idea.slice(0, 100)}…` : entry.idea) : (entry.cursor_prompt ? (entry.cursor_prompt.length > 100 ? `${entry.cursor_prompt.slice(0, 100).replace(/\n/g, ' ')}…` : entry.cursor_prompt.replace(/\n/g, ' ')) : 'Untitled')}</span>
                    <span className="run-history-meta">
                      {new Date(entry.timestamp).toLocaleString()} · {entry.pipelineName || (entry.templateName && entry.templateName !== 'Custom' ? (entry.templateName === 'default' ? "Coder's Council" : entry.templateName.replace(/_/g, ' ')) : 'Custom')}
                    </span>
                    <div className="history-entry-meta">
                      {entry.agentCount && (
                        <span className="history-meta-chip">{entry.agentCount} agents</span>
                      )}
                      {entry.graderScore !== null && entry.graderScore !== undefined && (
                        <span className={`history-meta-chip history-score-chip ${
                          entry.graderScore >= 80 ? 'score-high' :
                          entry.graderScore >= 60 ? 'score-mid' : 'score-low'
                        }`}>
                          {entry.graderScore}/100
                        </span>
                      )}
                      {entry.pipelineName && (
                        <span className="history-meta-chip">{entry.pipelineName}</span>
                      )}
                    </div>
                  </button>
                  <button
                    type="button"
                    className="run-history-delete"
                    onClick={(e) => deleteRunHistoryItem(entry.id, e)}
                    aria-label={`Delete run ${(entry.idea || entry.cursor_prompt)?.slice(0, 20) || 'untitled'}`}
                  >
                    🗑
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="helper">No runs yet. Run pipeline to save.</p>
          )}
          </div>
        </div>
        <div className="sidebar-section">
          <h3>Share</h3>
          <button
            type="button"
            className="button-secondary sidebar-share-btn"
            onClick={() => { handleShare(); setSidebarOpen(false); }}
            disabled={!result?.cursor_prompt && !result?.plan_md}
          >
            {shared ? 'Copied!' : 'Share result URL'}
          </button>
          <button
            type="button"
            className="button-secondary sidebar-share-btn"
            onClick={() => { setAgentModal({ mode: 'share-pipeline' }); setSidebarOpen(false); }}
            disabled={loading}
          >
            Share pipeline
          </button>
        </div>
        <div className="sidebar-section email-capture-section">
          <h3>Stay in the loop</h3>
          <p className="email-capture-description">Get notified when premium features launch.</p>
          {emailSubmitted ? (
            <p className="email-capture-success">You're on the list.</p>
          ) : (
            <div className="email-capture-form">
              <input
                type="email"
                className="email-capture-input"
                placeholder="your@email.com"
                value={emailCapture}
                onChange={(e) => setEmailCapture(e.target.value)}
                aria-label="Email address"
              />
              <button
                type="button"
                className="email-capture-btn"
                onClick={async () => {
                  if (!emailCapture.trim() || !emailCapture.includes('@')) return;
                  try {
                    await fetch('https://formspree.io/f/mvzwvlek', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ email: emailCapture }),
                    });
                  } catch (_) {}
                  setEmailSubmitted(true);
                }}
              >
                Notify me
              </button>
            </div>
          )}
        </div>
      </aside>

      <div className={`App-content-wrapper ${isProcessing ? 'is-split' : ''}`}>
      <main className={`main main-workspace ${isProcessing ? 'is-split' : 'is-centered'}`}>
        <div className="left-pane">
        <div className="hero-section">
          <div className="hero-steps">
            <div className="hero-step">
              <span className="hero-step-icon">⚙️</span>
              <span className="hero-step-label">Define</span>
              <span className="hero-step-desc">Describe your feature</span>
            </div>
            <div className="hero-arrow">→</div>
            <div className="hero-step">
              <span className="hero-step-icon">⚡</span>
              <span className="hero-step-label">Run</span>
              <span className="hero-step-desc">Agents plan and critique</span>
            </div>
            <div className="hero-arrow">→</div>
            <div className="hero-step">
              <span className="hero-step-icon">✦</span>
              <span className="hero-step-label">Get results</span>
              <span className="hero-step-desc">Paste into your AI editor</span>
            </div>
          </div>
        </div>
        <section className="section">
          <label htmlFor="idea">What do you want to work on?</label>
          <div className="template-chips" role="group" aria-label="Prompt type">
            <button type="button" className={`template-chip ${ideaType === 'coding' ? 'active' : ''}`} onClick={() => setIdeaType('coding')}>
              Coding
            </button>
          </div>
          <div className="example-prompt-chips">
            {EXAMPLE_PROMPTS.map((p, i) => (
              <button
                key={i}
                type="button"
                className="example-prompt-chip"
                onClick={() => setIdea(p)}
                disabled={loading}
              >
                {p.slice(0, 42)}…
              </button>
            ))}
          </div>
          <textarea
            ref={ideaRef}
            id="idea"
            value={idea}
            onChange={(e) => setIdea(e.target.value)}
            placeholder='e.g. "Add pagination to the user list endpoint" or "Refactor the auth middleware to use JWT" or "Build a rate limiter in Redis"'
            rows={4}
            disabled={loading}
          />
        </section>

        <section className="section">
          <div className="upload-section">
            <button
              type="button"
              className="upload-toggle-btn"
              onClick={() => setUploadExpanded((prev) => !prev)}
            >
              <span className="upload-toggle-icon">📎</span>
              <span className="upload-toggle-label">
                {codeFiles.length + imageFiles.length > 0
                  ? `${codeFiles.length + imageFiles.length} file${codeFiles.length + imageFiles.length > 1 ? 's' : ''} attached`
                  : 'Attach project files (optional)'}
              </span>
              <span className="upload-toggle-arrow">{uploadExpanded ? '▲' : '▼'}</span>
            </button>
            {uploadExpanded && (
              <div className="upload-expanded">
                <p className="upload-hint">⚠️ Large files use more tokens and increase API costs.</p>
                <div
                  className={`file-upload-zone ${isDragging ? 'drag-active' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <label>Upload project files (optional)</label>
                  <p className="helper">Add relevant files so the council can reference your existing patterns</p>
                  <input
                    ref={projectFileInputRef}
                    type="file"
                    multiple
                    accept="image/*,.txt,.md,.json,.js,.ts,.tsx,.jsx,.py,.go,.java,.c,.cpp,.h,.rs,.html,.css,.scss,.yaml,.yml,.xml,.sh"
                    onChange={handleFileChange}
                    disabled={loading}
                    className="file-input-hidden"
                  />
                  <div
                    role="button"
                    tabIndex={loading ? -1 : 0}
                    aria-disabled={loading}
                    className={`file-upload-drop-target ${loading ? 'disabled' : ''}`}
                    onClick={() => !loading && projectFileInputRef.current?.click()}
                    onKeyDown={(e) => {
                      if (!loading && (e.key === 'Enter' || e.key === ' ') && projectFileInputRef.current) {
                        e.preventDefault();
                        projectFileInputRef.current.click();
                      }
                    }}
                  >
                    Drag & drop project files here, or click to browse
                  </div>
                  {(codeFiles.length > 0 || imageFiles.length > 0) && (
                  <>
                    {codeFiles.length > 0 && (
                      <div className="selected-files-container">
                        <p className="file-count">{codeFiles.length} code file(s) selected</p>
                        <ul className="file-list">
                          {codeFiles.map((file, index) => (
                            <li key={`${file.name}-${file.size}-${index}`} className="uploaded-file-item file-item">
                              <span className="uploaded-file-id">FILE{index + 1}</span>
                              <span className="uploaded-file-name">{file.name} ({formatFileSize(file.size)})</span>
                              <button
                                type="button"
                                className="remove-file-btn"
                                onClick={() => handleRemoveCodeFile(index)}
                                disabled={loading}
                              >
                                Remove
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <ImagePreviewGrid images={imageFiles} onRemove={handleRemoveImageFile} disabled={loading} showIds />
                    <button
                      type="button"
                      className="clear-files-btn"
                      onClick={() => { setCodeFiles([]); setImageFiles([]); }}
                      disabled={loading}
                    >
                      Clear All Files
                    </button>
                  </>
                )}
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="section pipeline-section">
          <div className="pipeline-header">
            <div className="pipeline-header-text">
              <span className="pipeline-header-label">⚙️ Your pipeline</span>
              <span className="pipeline-header-hint">Customize your agents and connections</span>
            </div>
            <button
              type="button"
              className="wf-open-btn"
              onClick={() => setWorkflowEditorOpen(true)}
            >
              ✦ Visual editor
            </button>
          </div>
          <div className="pipeline-flow" role="group" aria-label="Model pipeline" ref={pipelineRef}>
              <DndContext
                sensors={dndSensors}
                collisionDetection={closestCenter}
                onDragEnd={(event) => {
                  const { active, over } = event;
                  if (!active?.id || !over?.id || active.id === over.id) return;
                  const oldIndex = agentStack.findIndex((p) => p.id === active.id);
                  const newIndex = agentStack.findIndex((p) => p.id === over.id);
                  if (oldIndex === -1 || newIndex === -1) return;
                  const newPipeline = arrayMove(agentStack, oldIndex, newIndex);
                  setPipeline(newPipeline);
                  const orderedWorkflow = newPipeline.map((p) => workflow.find((w) => w.id === p.id)).filter(Boolean);
                  const newWorkflow = recalculateParentIdsFromOrder(orderedWorkflow);
                  setWorkflow(newWorkflow);
                }}
              >
                <SortableContext items={agentStack.map((p) => p.id)} strategy={horizontalListSortingStrategy}>
                  <div className="pipeline-nodes">
                    {agentStack.map((node, idx) => (
                      <Fragment key={node.id}>
                        <SortablePipelineNode
                          node={node}
                          displayName={getAgentDisplayName(node.id, node)}
                          isActive={activePipelineNode === node.id}
                          isSelectorOpen={pipelineSelectorOpen === node.id}
                          models={getModelsForRole(node.id, !!openaiKey?.trim(), !!anthropicKey?.trim(), !!geminiKey?.trim(), !!localLlmUrl?.trim())}
                          modelLabel={getModelLabel(node.model, node.id, !!openaiKey?.trim(), !!anthropicKey?.trim(), !!geminiKey?.trim(), !!localLlmUrl?.trim())}
                          onToggleSelector={(id) => setPipelineSelectorOpen((o) => (o === id ? null : id))}
                          onEdit={(n) => setAgentModal({ mode: 'edit', editingId: n.id })}
                          onDelete={(n) => setPipeline((prev) => prev.filter((p) => p.id !== n.id))}
                          onSelectModel={(id, value) => {
                            setPipeline((prev) => prev.map((p) => (p.id === id ? { ...p, model: value, provider: getProviderFromModel(value) } : p)));
                            setWorkflow((prev) => prev.map((w) => (w.id === id ? { ...w, model: value, provider: getProviderFromModel(value) } : w)));
                            setPipelineSelectorOpen(null);
                          }}
                          loading={loading}
                          canDelete={agentStack.length > 1}
                          nodeStatus={pipelineStatus[node.id]}
                          isCustom={isCustomAgent(node)}
                        />
                        {idx < agentStack.length - 1 && (
                          <div className="pipeline-edge" role="group">
                            {/* TODO: Make insert-socket a droppable target for drag-and-drop agent chips */}
                            <div className={`pipeline-flow-arrow ${loading ? 'pipeline-flow-arrow--active' : ''}`} aria-hidden="true">
                              <span className="pipeline-flow-line" />
                              <span className="pipeline-flow-head">›</span>
                            </div>
                            <button
                              type="button"
                              className="insert-socket"
                              aria-label={`Add agent between ${getAgentDisplayName(node.id, node)} and ${getAgentDisplayName(agentStack[idx + 1].id, agentStack[idx + 1])}`}
                              aria-haspopup="dialog"
                              title={showInsertHint && idx === 0 ? 'Click to add an agent here' : undefined}
                              disabled={loading}
                              onClick={() => openAddAgentModalAtIndex(idx + 1)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  openAddAgentModalAtIndex(idx + 1);
                                }
                              }}
                            >
                              +
                            </button>
                          </div>
                        )}
                      </Fragment>
                    ))}
                    <div className="pipeline-edge pipeline-edge-end" role="group">
                      <button
                        type="button"
                        className="insert-socket"
                        aria-label={agentStack.length > 0 ? `Add agent after ${getAgentDisplayName(agentStack[agentStack.length - 1].id, agentStack[agentStack.length - 1])}` : 'Add agent at end'}
                        aria-haspopup="dialog"
                        title={showInsertHint && agentStack.length <= 1 ? 'Click to add an agent here' : undefined}
                        disabled={loading}
                        onClick={() => openAddAgentModalAtIndex(agentStack.length)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openAddAgentModalAtIndex(agentStack.length);
                          }
                        }}
                      >
                        +
                      </button>
                    </div>
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          {(openaiKey?.trim() || anthropicKey?.trim() || geminiKey?.trim()) && (
            <div className="pipeline-template-buttons">
              <button
                type="button"
                className="copy-btn pipeline-template-btn"
                onClick={() => setAgentModal({ mode: 'load-templates' })}
                disabled={loading}
              >
                Load template
              </button>
            </div>
          )}
          {agentModal?.mode === 'template-picker' && (
            <TemplatePicker
              afterIndex={agentModal.afterIndex}
              onSelect={(next) => setAgentModal(next)}
              onClose={() => setAgentModal(null)}
              importedSkills={importedSkills}
              setImportedSkills={setImportedSkills}
              onAddSkill={handleAddSkill}
              onAddGuardrail={handleAddGuardrail}
              getModelLabel={(value) => getModelLabel(value, 'architect', !!openaiKey?.trim(), !!anthropicKey?.trim(), !!geminiKey?.trim(), !!localLlmUrl?.trim())}
            />
          )}
          {agentModal?.mode === 'save-template' && (
            <SaveTemplateModal
              onSave={(name) => savePipelineTemplate(name)}
              onClose={() => setAgentModal(null)}
            />
          )}
          {agentModal?.mode === 'load-templates' && (
            <LoadTemplatesModal
              templates={templates}
              pendingTemplate={agentModal.pendingTemplate}
              onLoad={(t) => loadPipelineTemplate(t)}
              onConfirmLoad={confirmLoadPipelineTemplate}
              onCancelLoad={() => setAgentModal({ mode: 'load-templates' })}
              onRequestDelete={(id) => setAgentModal({ mode: 'confirm-template-delete', templateId: id })}
              onClose={() => setAgentModal(null)}
            />
          )}
          {agentModal?.mode === 'confirm-template-overwrite' && (
            <ConfirmOverwriteModal
              templateName={agentModal.templateName}
              existingId={agentModal.existingId}
              onOverwrite={(name, id) => savePipelineTemplate(name, id)}
              onClose={() => setAgentModal(null)}
            />
          )}
          {agentModal?.mode === 'confirm-template-delete' && (
            <ConfirmDeleteModal
              templateName={templates.find((t) => t.id === agentModal.templateId)?.name ?? 'Unknown'}
              templateId={agentModal.templateId}
              onDelete={deletePipelineTemplate}
              onClose={() => setAgentModal(null)}
            />
          )}
          {agentModal?.mode === 'confirm-load-shared' && (
            <ConfirmLoadSharedModal
              sharedPipeline={agentModal.sharedPipeline}
              onConfirm={confirmLoadSharedPipeline}
              onClose={dismissSharedPipeline}
            />
          )}
          {agentModal?.mode === 'share-pipeline' && (
            <SharePipelineModal pipeline={pipeline} onClose={() => setAgentModal(null)} />
          )}
          {agentModal && (agentModal.mode === 'add' || agentModal.mode === 'edit') && (
            <AgentModal
              mode={agentModal.mode}
              initialNode={agentModal.editingId ? pipeline.find((p) => p.id === agentModal.editingId) : agentModal.initialNode ?? null}
              afterIndex={agentModal.afterIndex}
              onSave={handleAgentModalSave}
              onClose={() => setAgentModal(null)}
              hasO={!!openaiKey?.trim()}
              hasA={!!anthropicKey?.trim()}
              hasG={!!geminiKey?.trim()}
              hasLocal={!!localLlmUrl?.trim()}
            />
          )}
          {hasIntensitySupport && (
            <div className="skeptic-intensity">
              <label htmlFor="skeptic-intensity">Skeptic intensity</label>
              <div className="intensity-slider-wrap">
                <input
                  id="skeptic-intensity"
                  type="range"
                  min={1}
                  max={5}
                  value={skepticIntensity}
                  onChange={(e) => setSkepticIntensity(Number(e.target.value))}
                  disabled={loading}
                  aria-valuemin={1}
                  aria-valuemax={5}
                  aria-valuenow={skepticIntensity}
                  aria-valuetext={INTENSITY_LABELS[skepticIntensity]}
                />
                <span className="intensity-value">{INTENSITY_LABELS[skepticIntensity]}</span>
              </div>
            </div>
          )}
        </section>

        {!openaiKey?.trim() && !anthropicKey?.trim() && !geminiKey?.trim() && (
          <div className="no-keys-banner">
            <span>⚙️ Add your API keys to run</span>
            <button
              type="button"
              className="no-keys-banner-btn"
              onClick={() => setSidebarOpen(true)}
            >
              Open settings
            </button>
          </div>
        )}

        {/* A/B compare disabled - evaluation harness removed. Backup: backups/frontend.20260314015501/ */}
        {false && (
        <section className="ab-compare-toggle-section">
          <label className="ab-compare-toggle-label">
            <input
              type="checkbox"
              checked={abCompareEnabled}
              onChange={(e) => setAbCompareEnabled(e.target.checked)}
              disabled={loading}
              aria-describedby="ab-compare-hint"
            />
            <span>Enable A/B compare</span>
          </label>
          <p id="ab-compare-hint" className="ab-compare-hint">
            Compare &quot;Raw&quot; (idea → coding model) vs &quot;Pipeline&quot; (Architect → Skeptic → Synthesizer).
          </p>
          {abCompareEnabled && (
            <div className="ab-judge-select-wrap">
              <label htmlFor="ab-judge-persona">A/B Judge Persona</label>
              <select
                id="ab-judge-persona"
                className="ab-judge-select"
                value={importedSkills.find((s) => s.id === selectedJudge) ? selectedJudge : 'default'}
                onChange={(e) => setSelectedJudge(e.target.value)}
                disabled={loading}
                aria-label="A/B Judge Persona"
              >
                <option value="default">Default Master Grader</option>
                {importedSkills.map((skill) => (
                  <option key={skill.id} value={skill.id}>
                    {skill.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {abCompareEnabled && idea.trim() && (
            <p className="ab-compare-cost-hint">
              Est. cost: ~${(estimateTokens(idea) * 7 * abCostPerToken).toFixed(4)} (both flows).
              <label className="ab-cost-input-wrap">
                $/token:
                <input
                  type="number"
                  step="0.0000001"
                  min="0"
                  value={abCostPerToken}
                  onChange={(e) => setAbCostPerToken(Number(e.target.value) || 0)}
                  className="ab-cost-input"
                  aria-label="Cost per token in dollars"
                />
              </label>
            </p>
          )}
        </section>
        )}

        </div>
        <div className="right-pane">
        {loading && (
        <div className="run-button-group">
              <section className="live-council-feed" aria-label="Live Feed">
                <div className="mini-pipeline" role="status" aria-live="polite" aria-label="Pipeline progress">
                  {displayWorkflow.map((node, idx) => {
                    const status = pipelineStatus[node.id]?.status ?? 'pending';
                    const displayName = getAgentDisplayName(node.id, node);
                    const modelLabel = getModelLabel(node.model, node.id, !!openaiKey?.trim(), !!anthropicKey?.trim(), !!geminiKey?.trim(), !!localLlmUrl?.trim());
                    const dotStatus = status === 'error' ? 'error' : status === 'completed' ? 'completed' : status === 'active' ? 'active' : status === 'retrying' ? 'retrying' : 'idle';
                    return (
                      <Fragment key={node.id}>
                        <button
                          type="button"
                          className={`pipeline-node pipeline-node--mini${status === 'active' ? ' pipeline-node-active' : ''}${status === 'completed' ? ' pipeline-node-completed' : ''}${status === 'error' ? ' pipeline-node-error' : ''}`}
                          onClick={() => document.getElementById(`feed-card-${node.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })}
                          aria-label={`${displayName}: ${status}`}
                        >
                          <div className="pipeline-node-label-row">
                            <div className="pipeline-node-status-dot" data-status={dotStatus} aria-hidden="true" />
                            <span className="pipeline-node-label">{displayName}</span>
                            {status === 'completed' && <span className="pipeline-node-check-icon" aria-label="Completed">✓</span>}
                          </div>
                          <span className="pipeline-node-model">{modelLabel}</span>
                        </button>
                        {idx < displayWorkflow.length - 1 && (
                          <span className="mini-pipeline-arrow" aria-hidden="true">→</span>
                        )}
                      </Fragment>
                    );
                  })}
                </div>
                {displayWorkflow.map((node) => {
                  const agentId = node.id;
                  const hasVisibleContent = (runData[agentId] || '').trim().length > 0;
                  const summaryFallback = getAgentOutputFromResult(agentId);
                  const rawContent = (runData[agentId] ?? summaryFallback ?? '')
                    .replace(/\[MODE:\s*(?:NO_REPO|REPO)\]\s*/gi, '')
                    .replace(/\[NO_REPO\]\s*/gi, '')
                    .replace(/NO_REPO\]\s*/g, '');
                  const status = pipelineStatus[agentId]?.status ?? 'pending';
                  const isActive = status === 'active';
                  const highlight = hasVisibleContent && activePipelineNode === agentId;
                  const isTyping = isActive && loading;
                  const cardContent = rawContent.length > 0
                    ? rawContent
                    : (loading ? (isActive ? 'Agent is typing...' : 'Waiting...') : 'No data received');
                  const isRunning = status === 'active' || status === 'retrying';
                  return (
                    <div id={`feed-card-${agentId}`} key={agentId} className={`live-feed-card ${highlight ? 'live-feed-card--active' : ''} ${isRunning ? 'agent-feed-card--active' : ''}`}>
                      <div
                        className="live-feed-content"
                        style={{
                          fontFamily: 'monospace',
                          fontSize: '13px',
                          lineHeight: '1.5',
                          whiteSpace: 'pre-wrap',
                          backgroundColor: '#1e1e1e',
                          padding: '10px',
                          borderRadius: '4px',
                          overflowY: 'auto',
                          maxHeight: '300px',
                        }}
                      >
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{cardContent}</ReactMarkdown>
                        {isTyping && <span className="typing-cursor" aria-hidden="true">▋</span>}
                      </div>
                      {pipelineStatus[agentId]?.status === 'retrying' && (
                        <div className="agent-retrying-banner">
                          {pipelineStatus[agentId]?.message || 'Retrying...'}
                        </div>
                      )}
                      {pipelineStatus[agentId]?.status === 'error' && (
                        <div className="agent-error-banner">
                          ⚠️ Agent failed: {pipelineStatus[agentId]?.message || 'Unknown error'}
                        </div>
                      )}
                    </div>
                  );
                })}
              </section>
        </div>
        )}

        {error && (
          <div className="error">
            {error}
          </div>
        )}

        {false && abResults && ( /* A/B panel disabled */
          <section className="ab-compare-panel" aria-label="A/B comparison results">
            <h3 className="ab-compare-title">A/B Comparison</h3>
            <div className="ab-compare-columns">
              <div className={`ab-result-column ${abResults.raw.error ? 'ab-result-error' : ''}`}>
                <h4 className="ab-result-heading">Raw</h4>
                {abResults.raw.error && <p className="ab-result-error-msg">{abResults.raw.error}</p>}
                <div className="ab-result-meta">
                  <span>Tokens: ~{abResults.raw.tokens}</span>
                  {abCostPerToken > 0 && <span>Cost: ~${abResults.raw.cost.toFixed(4)}</span>}
                  {abResults.raw.grade?.needs_review ? (
                    <span className="ab-grade-score ab-grade-needs-review">Needs review</span>
                  ) : abResults.raw.grade ? (
                    <>
                      <span className="ab-grade-score">Score: {abResults.raw.grade.score}/100</span>
                      <span>Adherence: {abResults.raw.grade.breakdown.adherence}/40</span>
                      <span>Tech Sophistication: {abResults.raw.grade.breakdown.technical_sophistication}/30</span>
                      <span>DX: {abResults.raw.grade.breakdown.dx}/20</span>
                      <span>Contextual Safety: {abResults.raw.grade.breakdown.contextual_safety}/10</span>
                    </>
                  ) : (
                    <>
                      <span>Score: {abResults.raw.heuristics.score}</span>
                      <span>TODOs: {abResults.raw.heuristics.todoCount}</span>
                      <span>console.log: {abResults.raw.heuristics.consoleLogCount}</span>
                      <span>aria-: {abResults.raw.heuristics.hasAria ? 'yes' : 'no'}</span>
                    </>
                  )}
                </div>
                {abResults.raw.grade?.needs_review && (
                  <div className="ab-grade-breakdown">
                    <p className="ab-grade-needs-review-msg">Grader returned invalid JSON. Raw output:</p>
                    <pre className="ab-grade-raw-output">{abResults.raw.grade.raw_output || '(empty)'}</pre>
                  </div>
                )}
                {abResults.raw.grade && !abResults.raw.grade.needs_review && (
                  <div className="ab-grade-breakdown">
                    <h5 className="ab-grade-breakdown-title">LLM Judge Breakdown</h5>
                    <dl className="ab-grade-breakdown-list">
                      <dt>Adherence (40pts)</dt>
                      <dd>{abResults.raw.grade.breakdown.adherence}</dd>
                      <dt>Technical Sophistication (30pts)</dt>
                      <dd>{abResults.raw.grade.breakdown.technical_sophistication}</dd>
                      <dt>DX/Cleanliness (20pts)</dt>
                      <dd>{abResults.raw.grade.breakdown.dx}</dd>
                      <dt>Contextual Safety (10pts)</dt>
                      <dd>{abResults.raw.grade.breakdown.contextual_safety}</dd>
                    </dl>
                    <div className="ab-grade-metrics">
                      <span>console.log: {abResults.raw.grade.metrics.hasConsoleLog ? 'yes' : 'no'}</span>
                      <span>aria-: {abResults.raw.grade.metrics.hasAria ? 'yes' : 'no'}</span>
                      <span>TODOs: {abResults.raw.grade.metrics.todoCount}</span>
                    </div>
                    {abResults.raw.grade.hallucinations?.length > 0 && (
                      <div className="ab-grade-hallucinations">
                        <strong>Hallucinations:</strong>
                        <ul>
                          {abResults.raw.grade.hallucinations.map((h, i) => (
                            <li key={i}>{h}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {abResults.raw.grade.reasoning && (
                      <details className="ab-grade-reasoning">
                        <summary>Grader reasoning</summary>
                        <pre>{abResults.raw.grade.reasoning}</pre>
                      </details>
                    )}
                  </div>
                )}
                <div className="ab-result-block">
                  <strong>Prompt</strong>
                  <pre className="ab-result-prompt">{abResults.raw.prompt}</pre>
                </div>
                <div className="ab-result-block">
                  <button
                    type="button"
                    className="ab-result-toggle"
                    onClick={() => setAbExpandedRaw((x) => !x)}
                    aria-expanded={abExpandedRaw}
                  >
                    {abExpandedRaw ? '▼' : '▶'} Response
                  </button>
                  {abExpandedRaw && (
                    <pre className="ab-result-response markdown-container">{abResults.raw.response || '(empty)'}</pre>
                  )}
                </div>
              </div>
              <div className={`ab-result-column ${abResults.council.error ? 'ab-result-error' : ''}`}>
                <h4 className="ab-result-heading">Pipeline</h4>
                {abResults.council.error && (
                  <p className="ab-result-error-msg">
                    Pipeline failed to synthesize a final prompt — showing Raw results only. {abResults.council.error}
                  </p>
                )}
                <div className="ab-result-meta">
                  <span>Tokens: ~{abResults.council.tokens}</span>
                  {abCostPerToken > 0 && <span>Cost: ~${abResults.council.cost.toFixed(4)}</span>}
                  {abResults.council.grade?.needs_review ? (
                    <span className="ab-grade-score ab-grade-needs-review">Needs review</span>
                  ) : abResults.council.grade ? (
                    <>
                      <span className="ab-grade-score">Score: {abResults.council.grade.score}/100</span>
                      <span>Adherence: {abResults.council.grade.breakdown.adherence}/40</span>
                      <span>Tech Sophistication: {abResults.council.grade.breakdown.technical_sophistication}/30</span>
                      <span>DX: {abResults.council.grade.breakdown.dx}/20</span>
                      <span>Contextual Safety: {abResults.council.grade.breakdown.contextual_safety}/10</span>
                    </>
                  ) : (
                    <>
                      <span>Score: {abResults.council.heuristics.score}</span>
                      <span>TODOs: {abResults.council.heuristics.todoCount}</span>
                      <span>console.log: {abResults.council.heuristics.consoleLogCount}</span>
                      <span>aria-: {abResults.council.heuristics.hasAria ? 'yes' : 'no'}</span>
                    </>
                  )}
                </div>
                {abResults.council.grade?.needs_review && (
                  <div className="ab-grade-breakdown">
                    <p className="ab-grade-needs-review-msg">Grader returned invalid JSON. Raw output:</p>
                    <pre className="ab-grade-raw-output">{abResults.council.grade.raw_output || '(empty)'}</pre>
                  </div>
                )}
                {abResults.council.grade && !abResults.council.grade.needs_review && (
                  <div className="ab-grade-breakdown">
                    <h5 className="ab-grade-breakdown-title">LLM Judge Breakdown</h5>
                    <dl className="ab-grade-breakdown-list">
                      <dt>Adherence (40pts)</dt>
                      <dd>{abResults.council.grade.breakdown.adherence}</dd>
                      <dt>Technical Sophistication (30pts)</dt>
                      <dd>{abResults.council.grade.breakdown.technical_sophistication}</dd>
                      <dt>DX/Cleanliness (20pts)</dt>
                      <dd>{abResults.council.grade.breakdown.dx}</dd>
                      <dt>Contextual Safety (10pts)</dt>
                      <dd>{abResults.council.grade.breakdown.contextual_safety}</dd>
                    </dl>
                    <div className="ab-grade-metrics">
                      <span>console.log: {abResults.council.grade.metrics.hasConsoleLog ? 'yes' : 'no'}</span>
                      <span>aria-: {abResults.council.grade.metrics.hasAria ? 'yes' : 'no'}</span>
                      <span>TODOs: {abResults.council.grade.metrics.todoCount}</span>
                    </div>
                    {abResults.council.grade.hallucinations?.length > 0 && (
                      <div className="ab-grade-hallucinations">
                        <strong>Hallucinations:</strong>
                        <ul>
                          {abResults.council.grade.hallucinations.map((h, i) => (
                            <li key={i}>{h}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {abResults.council.grade.reasoning && (
                      <details className="ab-grade-reasoning">
                        <summary>Grader reasoning</summary>
                        <pre>{abResults.council.grade.reasoning}</pre>
                      </details>
                    )}
                  </div>
                )}
                <div className="ab-result-block">
                  <strong>Prompt</strong>
                  <pre className="ab-result-prompt">{abResults.council.prompt}</pre>
                </div>
                <div className="ab-result-block">
                  <button
                    type="button"
                    className="ab-result-toggle"
                    onClick={() => setAbExpandedCouncil((x) => !x)}
                    aria-expanded={abExpandedCouncil}
                  >
                    {abExpandedCouncil ? '▼' : '▶'} Response
                  </button>
                  {abExpandedCouncil && (
                    <pre className="ab-result-response markdown-container">{abResults.council.response || '(empty)'}</pre>
                  )}
                </div>
              </div>
            </div>
            <div className="ab-results-actions">
              <button
                type="button"
                className="button-secondary"
                onClick={exportAbResults}
                disabled={!abResults}
              >
                Export Results
              </button>
            </div>
          </section>
        )}

        {result && !abResults && (
          <>
          <section className="results">
            {/* Investment verdict card — always shown above tabs */}
            {(() => {
              const resultJson = result?.cursor_prompt ? extractResultJson(result.cursor_prompt) : null;
              return resultJson && resultJson.verdict && isInvestmentPipeline(pipeline) ? (
                <div className="result-json-card">
                  <div className="result-json-verdict" data-verdict={resultJson.verdict}>
                    {resultJson.verdict === 'Invest' ? '✅' : resultJson.verdict === 'Pass' ? '❌' : '👁️'} {resultJson.verdict}
                    <span className="result-json-confidence">Confidence: {resultJson.confidence != null ? resultJson.confidence : '—'}/100</span>
                  </div>
                  {resultJson.thesis && <p className="result-json-thesis">{resultJson.thesis}</p>}
                  {((resultJson.bull_probability != null) || (resultJson.bear_probability != null)) && (
                    <div className="result-json-probs">
                      {resultJson.bull_probability != null && <span className="result-json-bull">🐂 Bull Scenario Likelihood: {resultJson.bull_probability}%</span>}
                      {resultJson.bear_probability != null && <span className="result-json-bear">🐻 Bear Downside Risk: {resultJson.bear_probability}%</span>}
                    </div>
                  )}
                  {resultJson.action && (
                    <div className="result-json-action">
                      <span className="result-json-action-label">Action:</span> {resultJson.action}
                    </div>
                  )}
                  {resultJson.kpi_triggers?.length > 0 && (
                    <div className="result-json-kpis">
                      <span className="result-json-kpis-label">KPI Triggers:</span>
                      <ul>
                        {resultJson.kpi_triggers.map((kpi, i) => <li key={i}>{kpi}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              ) : null;
            })()}
            {/* Adversarial scorecard — always shown above tabs */}
            {isAdversarialPipeline(pipeline) && result?.results && (() => {
              const adversarialData = parseAdversarialOutput(result.results);
              return adversarialData ? <AdversarialScorecard data={adversarialData} result={result} idea={idea} pipeline={pipeline} /> : null;
            })()}

            {/* Tab bar */}
            {!(isAdversarialPipeline(pipeline) && parseAdversarialOutput(result?.results)) && (
              <div className="result-tabs" role="tablist">
                {[
                  { id: 'implementation', label: 'Implementation ↗' },
                  { id: 'spec', label: 'Review' },
                  { id: 'debate', label: 'Debate' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={resultTab === tab.id}
                    className={`result-tab-btn${resultTab === tab.id ? ' result-tab-btn--active' : ''}`}
                    onClick={() => setResultTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            {/* Tab: Spec — PLAN.md */}
            {!(isAdversarialPipeline(pipeline) && parseAdversarialOutput(result?.results)) && resultTab === 'spec' && (() => {
              const planText = stripResultJson(result?.plan_md)?.trim() || '(No plan generated — output may have been truncated)';
              const decisionLogMatch = /^###\s*Decision\s*Log/m.exec(planText);
              const planMain = decisionLogMatch ? planText.slice(0, decisionLogMatch.index).trim() : planText;
              const decisionLogContent = decisionLogMatch ? planText.slice(decisionLogMatch.index).trim() : null;
              return (
                <>
                  <p className="tab-panel-subtitle">Your checklist, risks, and decisions from this run.</p>
                  {(result?.verifier_verdict === 'PASS' || result?.verifier_verdict === 'FAIL') && (
                    <div className={`verifier-verdict verifier-verdict--${result.verifier_verdict === 'PASS' ? 'pass' : 'fail'}`}>
                      <span className="verifier-verdict-badge">
                        {result.verifier_verdict === 'PASS' ? '✓ Completeness check passed' : '⚠ Gaps found:'}
                      </span>
                      {result.verifier_verdict === 'FAIL' && Array.isArray(result.verifier_items) && result.verifier_items.length > 0 && (
                        <ul className="verifier-verdict-items">
                          {result.verifier_items.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                  <div className="result-block">
                    <div className="result-header">
                      <h3>PLAN.md</h3>
                      <div className="result-actions">
                        {result && (
                          <button
                            type="button"
                            className="copy-btn share-result-btn"
                            onClick={() => {
                              const url = resultToShareUrl({ ...result, idea });
                              if (!url) {
                                alert('Result is too large to share via URL.');
                                return;
                              }
                              navigator.clipboard.writeText(url);
                              setCopiedShareResult(true);
                              setTimeout(() => setCopiedShareResult(false), 2000);
                            }}
                          >
                            {copiedShareResult ? '✓ Copied!' : '🔗 Share result'}
                          </button>
                        )}
                        <button
                          className="copy-btn"
                          onClick={() => copyToClipboard(result.plan_md ?? '', setCopiedPlan)}
                        >
                          {copiedPlan ? 'Copied!' : 'Copy'}
                        </button>
                        <button
                          className="copy-btn export-btn"
                          onClick={exportPlanMd}
                          disabled={!result?.plan_md}
                        >
                          Export
                        </button>
                      </div>
                    </div>
                    <div className="markdown-container plan-markdown-container">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{planMain}</ReactMarkdown>
                    </div>
                    {decisionLogContent && (
                      <details style={{ marginTop: '0.75rem' }}>
                        <summary className="agent-logs-toggle">📋 Decision Log</summary>
                        <div className="markdown-container" style={{ marginTop: '0.5rem' }}>
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{decisionLogContent}</ReactMarkdown>
                        </div>
                      </details>
                    )}
                  </div>
                  {result?.judge && (
                    <details className="result-block" style={{ marginTop: '1rem' }}>
                      <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Quality Gate (Judge)</summary>
                      <div className="markdown-container">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{result.judge}</ReactMarkdown>
                      </div>
                    </details>
                  )}
                  {result?.tests && (
                    <details className="result-block" style={{ marginTop: '1rem' }}>
                      <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Generated Tests</summary>
                      <div className="result-actions" style={{ marginTop: '0.5rem' }}>
                        <button
                          type="button"
                          className="copy-btn"
                          onClick={() => copyToClipboard(result.tests ?? '', setCopiedTests)}
                        >
                          {copiedTests ? 'Copied!' : 'Copy Tests'}
                        </button>
                      </div>
                      <pre className="language-python" style={{ marginTop: '0.5rem', padding: '1rem', overflow: 'auto', maxHeight: '400px' }}>
                        <code>{result.tests}</code>
                      </pre>
                    </details>
                  )}
                  {result?.architect_plan && pipeline.some((p) => p.name?.toLowerCase().includes('skeptic')) && (
                    <button
                      type="button"
                      className="run-button rerun-skeptic-btn"
                      onClick={runRerunSkeptic}
                      disabled={rerunSkepticLoading || loading}
                    >
                      {rerunSkepticLoading ? 'Running...' : 'Run Skeptic Again ↺'}
                    </button>
                  )}
                </>
              );
            })()}

            {/* Tab: Implementation — cursor_prompt / CORE IMPLEMENTATION */}
            {!(isAdversarialPipeline(pipeline) && parseAdversarialOutput(result?.results)) && resultTab === 'implementation' && (() => {
              const rawCursorText = stripResultJson(result?.cursor_prompt) ?? '';
              const output2Match = /^##\s*OUTPUT\s*2(?:\s*\(CONDENSED\))?/m.exec(rawCursorText);
              const output2Content = output2Match ? rawCursorText.slice(output2Match.index + output2Match[0].length).trim() : null;
              return (
                <>
                  <p className="tab-panel-subtitle">→ Copy and paste into Claude Code or Cursor to start building.</p>
                  <div className="result-block agent-ready-prompt-block">
                    <div className="agent-ready-prompt-header">
                      <span className="agent-ready-prompt-label">PIPELINE OUTPUT</span>
                      <button
                        type="button"
                        className={`agent-ready-copy-btn agent-ready-copy-btn--primary${copiedPrepareForCursor ? ' agent-ready-copy-btn--copied' : ''}`}
                        onClick={copyForAiCopilot}
                        disabled={!result?.cursor_prompt && !result?.plan_md}
                        title="Copy and paste into Claude Code"
                      >
                        <span className="agent-ready-copy-icon" aria-hidden="true">
                          {copiedPrepareForCursor ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M20 6L9 17l-5-5"/>
                            </svg>
                          ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                            </svg>
                          )}
                        </span>
                        {copiedPrepareForCursor ? 'Copied!' : 'Copy for Claude Code'}
                      </button>
                      <button
                        type="button"
                        className="copy-btn export-pdf-btn"
                        onClick={() => exportToPDF(result, idea, pipeline)}
                      >
                        📄 Export PDF
                      </button>
                    </div>
                    <div className="agent-ready-prompt-code">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{cleanOutputDisplay(rawCursorText) ?? '(empty)'}</ReactMarkdown>
                    </div>
                  </div>
                  {output2Content && (
                    <section className="section">
                      <details>
                        <summary className="agent-logs-toggle">📋 OUTPUT 2 (CONDENSED)</summary>
                        <div className="agent-ready-prompt-code" style={{ marginTop: '0.5rem' }}>
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{output2Content}</ReactMarkdown>
                        </div>
                      </details>
                    </section>
                  )}
                </>
              );
            })()}

            {/* Tab: Debate — agent logs + reasoning narrative */}
            {!(isAdversarialPipeline(pipeline) && parseAdversarialOutput(result?.results)) && resultTab === 'debate' && (
              <>
                <p className="tab-panel-subtitle">How Architect, Skeptic, Synthesizer, and Verifier reasoned through this.</p>
                {!loading && Array.isArray(result.results) && result.results.length > 1 && (() => {
                  const dedupedResults = (result.results || []).filter((r, i, arr) =>
                    arr.findIndex(x => x.id === r.id && x.name === r.name) === i
                  );
                  return (
                    <section className="section agent-outputs">
                      <details open>
                        <summary className="agent-logs-toggle">
                          🧠 View Agent Debate Logs
                          <span className="agent-logs-count">({dedupedResults.length} agents)</span>
                        </summary>
                        {dedupedResults.map((agentResult) => {
                          const isExpanded = expandedAgentIds.has(agentResult.id);
                          const isError = agentResult.status === 'error';
                          const isCodeAgentError = typeof agentResult.output === 'string' && agentResult.output.startsWith('[Code Agent Error]:');
                          const displayName = getAgentDisplayName(agentResult.id, agentResult);
                          const adversarialAgents = ['Bull: Claims', 'Bear: Refutations', 'Bull: Defense'];
                          const displayOutput = isAdversarialPipeline(pipeline) && adversarialAgents.includes(agentResult.name)
                            ? summarizeAdversarialAgentOutput(agentResult.name, agentResult.output)
                            : agentResult.name === 'Judge' && isAdversarialPipeline(pipeline)
                              ? `Judge ruled: ${agentResult.output?.includes('"verdict"') ? 'See claim-by-claim scorecard above' : agentResult.output}`
                              : agentResult.output;
                          const agentDef = pipeline.find(p => p.id === agentResult.id);
                          const hasCodeAndPrompt = agentDef?.code?.trim() && agentDef?.systemPrompt?.trim() && !agentResult.codeOnly;
                          const showCodeIndicator = agentResult.codeRanBeforeLLM || hasCodeAndPrompt;
                          return (
                            <div
                              key={agentResult.id}
                              id={`agent-output-${agentResult.id}`}
                              className={`agent-output-block ${isError || isCodeAgentError ? 'agent-output-error' : ''}`}
                            >
                              <button
                                type="button"
                                className="agent-output-toggle"
                                onClick={() => toggleAgentOutput(agentResult.id)}
                                aria-expanded={isExpanded}
                              >
                                <span className="agent-output-chevron" aria-hidden="true">{isExpanded ? '▼' : '▶'}</span>
                                <span>{displayName}</span>
                                {isError && <span className="agent-output-status"> (failed)</span>}
                                {isCodeAgentError && !isError && <span style={{ color: '#ff6b6b', marginLeft: '8px', fontSize: '12px' }}> (code error)</span>}
                                {showCodeIndicator && !isCodeAgentError && <span style={{ marginLeft: '6px', fontSize: '12px' }} title="Code ran before LLM call">⚡</span>}
                              </button>
                              {isExpanded && (
                                <div className="agent-output-content markdown-container">
                                  {(isError || isCodeAgentError) ? (
                                    <pre className="agent-output-error-text">{displayOutput}</pre>
                                  ) : (() => {
                                    const metricsRows = renderMetricsTable(displayOutput);
                                    if (metricsRows) {
                                      return (
                                        <table className="metrics-table">
                                          <thead>
                                            <tr>
                                              <th>Metric</th>
                                              <th>Value</th>
                                              <th>Source</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {metricsRows.map((row) => (
                                              <tr key={row.key} className={row.status === 'MISSING' || row.value.startsWith('GAP') ? 'metrics-row-gap' : 'metrics-row-found'}>
                                                <td>{row.metric}</td>
                                                <td>{row.value}</td>
                                                <td>
                                                  {row.url
                                                    ? <a href={row.url} target="_blank" rel="noopener noreferrer" className="metrics-source-link">{row.url}</a>
                                                    : <span className="metrics-gap-label">GAP</span>}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      );
                                    }
                                    return <ReactMarkdown remarkPlugins={[remarkGfm]}>{stripResultJson(displayOutput) || '(empty)'}</ReactMarkdown>;
                                  })()}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </details>
                    </section>
                  );
                })()}
                {!loading && Array.isArray(result.results) && result.results.length > 1 && (() => {
                  const narrativeSteps = buildReasoningNarrative(result.results, pipeline);
                  if (narrativeSteps.length === 0) return null;
                  return (
                    <div className="reasoning-narrative">
                      <button
                        type="button"
                        className="reasoning-narrative-toggle"
                        onClick={() => setNarrativeOpen((prev) => !prev)}
                      >
                        <span>🧠 How agents collaborated</span>
                        <span>{narrativeOpen ? '▲' : '▼'}</span>
                      </button>
                      {narrativeOpen && (
                        <div className="reasoning-narrative-body">
                          {narrativeSteps.map((step, i) => (
                            <div
                              key={i}
                              className="reasoning-step reasoning-step--clickable"
                              onClick={() => {
                                const el = document.getElementById(`agent-output-${step.id}`);
                                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  const el = document.getElementById(`agent-output-${step.id}`);
                                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }
                              }}
                              role="button"
                              tabIndex={0}
                            >
                              <div className="reasoning-step-header">
                                <span className="reasoning-step-name">{step.name}</span>
                                <span className="reasoning-step-verb">{step.verb}</span>
                                <span className="reasoning-step-link">↗</span>
                              </div>
                              <p className="reasoning-step-preview">{step.preview}...</p>
                              {i < narrativeSteps.length - 1 && (
                                <div className="reasoning-step-arrow">↓</div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </>
            )}
          </section>
          </>
        )}
        </div>
      </main>
      </div>
      </div>
      {workflowEditorOpen && (
        <WorkflowEditor
          workflow={workflow}
          pipeline={pipeline}
          onSave={handleWorkflowEditorSave}
          onClose={() => setWorkflowEditorOpen(false)}
          pipelineStatus={pipelineStatus}
          loading={loading}
        />
      )}
    </div>
  );
}

// Prevent no-unused-vars for template prompt constants that are no longer referenced
// after pruning built-in decision templates.
void SECURITY_AUDITOR_PROMPT;
void PERFORMANCE_PROFILER_PROMPT;
void CAMPAIGN_ARCHITECT_PROMPT;
void SEO_EXPERT_PROMPT;
void COPYWRITER_PROMPT;
void SYNTHESIZER_MARKETING_PROMPT;
void SYNTHESIZER_SECURITY_PROMPT;
void SOFTWARE_ARCH_BULL_PROMPT;
void SOFTWARE_ARCH_BEAR_PROMPT;
void SOFTWARE_ARCH_JUDGE_PROMPT;
void RESEARCH_PROPONENT_PROMPT;
void RESEARCH_BEAR_PROMPT;
void RESEARCH_JUDGE_PROMPT;
void STARTUP_BULL_PROMPT;
void STARTUP_BEAR_PROMPT;
void STARTUP_JUDGE_PROMPT;
void MA_BULL_PROMPT;
void MA_BEAR_PROMPT;
void MA_JUDGE_PROMPT;
void POLICY_BULL_PROMPT;
void POLICY_BEAR_PROMPT;
void POLICY_JUDGE_PROMPT;

if (typeof window !== 'undefined') {
  window.__testParseGrader = (s) => console.log('PARSE:', parseGraderOutput(s));
}

export default App;